import { createFileRoute } from '@tanstack/react-router';
import { formatDistanceStrict } from 'date-fns';
import {
    CheckCircle2,
    ChevronDown,
    ChevronRight,
    ExternalLink,
    FileText,
    Headphones,
    Loader2,
    Maximize2,
    MessageSquare,
    Phone,
    PhoneCall,
    PhoneIncoming,
    Play,
    RefreshCw,
    RotateCcw,
    Save,
    Send,
    Square,
    Trash2,
    Wrench,
} from 'lucide-react';
import {
    Fragment,
    type MutableRefObject,
    type ReactNode,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

import { Badge } from '@apps/web/components/ui/badge';
import { Button } from '@apps/web/components/ui/button';
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
} from '@apps/web/components/ui/card';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@apps/web/components/ui/dialog';
import { ScrollArea } from '@apps/web/components/ui/scroll_area';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@apps/web/components/ui/select';
import { Skeleton } from '@apps/web/components/ui/skeleton';
import { Switch } from '@apps/web/components/ui/switch';
import { Textarea } from '@apps/web/components/ui/textarea';
import { VoiceCallAudioPlayer } from '@apps/web/components/voice_calls/voice_call_audio_player';
import { toast } from '@apps/web/lib/app_notifications';
import { getBrowserAudioContextConstructor } from '@apps/web/lib/browser_audio';
import { cn } from '@apps/web/lib/utils';
import {
    endCaregiverSimVoiceSession,
    getCaregiverSimVoiceTimeline,
    injectCaregiverSimVoiceUtterance,
    reflectCaregiverSimMemory,
    reseedCaregiverSimCaregivers,
    resetCaregiverSimPersonality,
    runCaregiverSimScenario,
    simulateCaregiverSimMessage,
    startCaregiverSimRealVoiceCall,
    startCaregiverSimVoiceSession,
    synthesizeCaregiverSimVoiceUtterance,
    updateCaregiverSimCaregiverDetails,
    useAdminVoiceCallRecordingUrls,
    useCaregiverSimCaregiverDetails,
    useCaregiverSimLatestVoiceCall,
    useCaregiverSimLogs,
    useCaregiverSimMemoryEvents,
    useCaregiverSimState,
} from '@apps/web/swr_data_fetching/data_fetching_hooks';
import { type components } from '@libraries/typescript/api_client';

export const Route = createFileRoute('/_app/admin/caregiver-sim')({
    component: CaregiverSimPage,
    head: () => ({
        meta: [
            {
                title: 'Caregiver Sim | Phoebe Admin',
            },
        ],
    }),
});

type CaregiverSimPersonalityRead =
    components['schemas']['CaregiverSimPersonalityRead'];
type CaregiverSimThreadRead = components['schemas']['CaregiverSimThreadRead'];
type CaregiverSimMessageRead = components['schemas']['CaregiverSimMessageRead'];
type CaregiverSimDecisionLogRead =
    components['schemas']['CaregiverSimDecisionLogRead'];
type CaregiverSimReseedResponse =
    components['schemas']['CaregiverSimReseedResponse'];
type CaregiverSimCaregiverDetailsResponse =
    components['schemas']['CaregiverSimCaregiverDetailsResponse'];
type CaregiverSimCaregiverDetailsUpdateRequest =
    components['schemas']['CaregiverSimCaregiverDetailsUpdateRequest'];
type CaregiverSimMemoryEventsResponse =
    components['schemas']['CaregiverSimMemoryEventsResponse'];
type CaregiverSimMemoryEventRead =
    components['schemas']['CaregiverSimMemoryEventRead'];
type CaregiverSimScenarioRunRead =
    components['schemas']['CaregiverSimScenarioRunRead'];
type CaregiverSimScenarioRunCheckRead =
    components['schemas']['CaregiverSimScenarioRunCheckRead'];
type CaregiverSimVoiceSessionStartResponse =
    components['schemas']['CaregiverSimVoiceSessionStartResponse'];
type VoiceCallRead = components['schemas']['VoiceCallRead'];

type ControlAction =
    | 'reset-all-personalities'
    | 'reset-personality'
    | 'reset-thread'
    | 'reseed';
type ScenarioAction = 'continue-scenario' | 'scenario';
type WorkflowType =
    | 'open_shift_callout'
    | 'multi_shift_outreach'
    | 'shift_confirmation'
    | 'clock_in'
    | 'clock_out';
type WorkflowChannel = 'sms' | 'voice';
type VoiceSimDirection = 'outbound' | 'inbound';
type CaregiverVoiceAudioMode = 'tts' | 'voice_agent';
type ScenarioCheckSeverity = 'pass' | 'warn' | 'fail';
type PendingAction = ControlAction | 'intervention' | null;
type PendingSimulation = {
    readonly counterpartyPhone: string;
    readonly body: string;
    readonly personalityName: string;
    readonly startedAt: string;
};
type PendingScenarioRun = {
    readonly action: ScenarioAction;
    readonly counterpartyPhone: string;
    readonly workflowType: WorkflowType;
    readonly workflowChannel: WorkflowChannel;
    readonly personalityIdentifier: string;
    readonly personalityName: string;
    readonly startedAt: string;
};
type VoiceSimStatus =
    | 'idle'
    | 'connecting'
    | 'listening'
    | 'phoebe-speaking'
    | 'caregiver-thinking'
    | 'caregiver-speaking'
    | 'ending'
    | 'ended'
    | 'error';
type VoiceSimCall = {
    readonly callId: string;
    readonly callSid: string;
    readonly accountSid: string;
    readonly streamSid: string;
    readonly websocketUrl: string;
    readonly personalityIdentifier: string;
    readonly counterpartyPhone: string;
    readonly workflowType: WorkflowType;
    readonly caregiverAudioMode: CaregiverVoiceAudioMode;
    readonly scenarioContext: string;
    readonly status: VoiceSimStatus;
    readonly statusText: string;
    readonly startedAt: string;
    readonly turnsCompleted: number;
    readonly errorMessage: string | null;
};
type VoiceSimCallsByKey = Record<string, VoiceSimCall>;
type VoiceTranscriptSpeaker = 'phoebe' | 'caregiver';
type VoiceTranscriptEntry = {
    readonly id: string;
    readonly speaker: VoiceTranscriptSpeaker;
    readonly text: string;
    readonly audioPayload: string | null;
    readonly audioPayloads: ReadonlyArray<string>;
    readonly createdAt: string;
};
type VoiceTranscriptsByCallId = Record<string, Array<VoiceTranscriptEntry>>;
type ToolCallStatus =
    | 'queued'
    | 'running'
    | 'success'
    | 'skipped'
    | 'error'
    | 'cancelled';
type ToolCallDisplayEntry = {
    readonly id: string;
    readonly name: string;
    readonly status: ToolCallStatus | string;
    readonly arguments: unknown;
    readonly result: unknown;
    readonly error: string | null;
    readonly createdAt: string | null;
};
type VoiceToolCallsByCallId = Record<string, Array<ToolCallDisplayEntry>>;
type VoiceReplayState = {
    readonly callId: string;
    readonly positionMs: number;
    readonly durationMs: number;
    readonly playing: boolean;
};
type VoiceHealthIssue = {
    readonly severity: Exclude<ScenarioCheckSeverity, 'pass'>;
    readonly title: string;
};
type Mutable<T> = {
    -readonly [Key in keyof T]: T[Key];
};
type VoiceSimTransport = Mutable<{
    readonly callId: string;
    readonly callKey: string;
    readonly socket: WebSocket;
    readonly silenceIntervalId: number | null;
    readonly timelineIntervalId: number | null;
    readonly timelinePolling: boolean;
    readonly seenAssistantTexts: Set<string>;
    readonly transcriptKeys: Set<string>;
    readonly pendingAssistantAudioPayloads: Array<string>;
    readonly turnsCompleted: number;
    readonly ending: boolean;
    readonly cleanupRequested: boolean;
}>;
type LiveVoicePlaybackBuffer = Mutable<{
    readonly chunks: Array<Uint8Array>;
    readonly byteLength: number;
    readonly flushTimeoutId: number | null;
}>;
type VoiceTimelineEntry = {
    readonly kind?: string;
    readonly role?: string;
    readonly text?: string;
    readonly text_preview?: string;
    readonly context_id?: string;
    readonly sequence?: number;
    readonly timestamp_iso?: string;
    readonly function_name?: string;
    readonly tool_call_id?: string;
    readonly arguments?: unknown;
    readonly result?: unknown;
    readonly function_calls?: unknown;
};
type VoiceTimelineTranscriptEntry = {
    readonly speaker: VoiceTranscriptSpeaker;
    readonly text: string;
};
type VoiceAssistantResponsePrompt = {
    readonly text: string;
};

const DEFAULT_SIM_COUNTERPARTY_PHONE = '+15551112222';
const DEFAULT_CAREGIVER_VOICE_AUDIO_MODE: CaregiverVoiceAudioMode = 'tts';
const SYNTHETIC_VOICE_HARNESS_LOCAL_STORAGE_KEY =
    'caregiver-sim-synthetic-voice';
const SIM_SCROLL_PANEL_HEIGHT_CLASS = 'h-[calc(100vh-20rem)] min-h-[720px]';
const SIM_GRID_PANEL_SCROLL_HEIGHT_CLASS = 'h-[300px]';
const SIM_MODAL_PANEL_SCROLL_HEIGHT_CLASS = 'h-[min(70vh,760px)]';
const VOICE_SIM_MAX_TURNS = 8;
const SYNTHETIC_TWILIO_MEDIA_FRAME_INTERVAL_MS = 20;
const SYNTHETIC_TWILIO_MEDIA_FRAME_BYTES = 160;
const VOICE_REPLAY_TURN_PAUSE_MS = 250;
const VOICE_TIMELINE_POLL_INTERVAL_MS = 250;
const VOICE_LIVE_AUDIO_BUFFER_MS = 100;
const VOICE_LIVE_AUDIO_BUFFER_BYTES = Math.round(
    (8000 * VOICE_LIVE_AUDIO_BUFFER_MS) / 1000,
);

function syntheticVoiceHarnessEnabled(): boolean {
    return (
        typeof window !== 'undefined' &&
        window.localStorage.getItem(
            SYNTHETIC_VOICE_HARNESS_LOCAL_STORAGE_KEY,
        ) === '1'
    );
}

const DEFAULT_CAREGIVER_MODEL_VALUE = 'anthropic:claude-sonnet-4-6';
const CAREGIVER_MODEL_OPTIONS = [
    {
        value: DEFAULT_CAREGIVER_MODEL_VALUE,
        provider: 'anthropic',
        model: 'claude-sonnet-4-6',
        label: 'Claude Sonnet 4.6',
        iconSrc: '/anthropic.svg',
        iconAlt: 'Anthropic',
    },
    {
        value: 'anthropic:claude-opus-4-6',
        provider: 'anthropic',
        model: 'claude-opus-4-6',
        label: 'Claude Opus 4.6',
        iconSrc: '/anthropic.svg',
        iconAlt: 'Anthropic',
    },
    {
        value: 'openai:gpt-5.6-luna',
        provider: 'openai',
        model: 'gpt-5.6-luna',
        label: 'GPT-5.6 Luna',
        iconSrc: '/openai.svg',
        iconAlt: 'OpenAI',
    },
    {
        value: 'openai:gpt-5.4',
        provider: 'openai',
        model: 'gpt-5.4',
        label: 'GPT-5.4',
        iconSrc: '/openai.svg',
        iconAlt: 'OpenAI',
    },
] as const;
const DEFAULT_SCENARIO_DETAILS = '';
const OPEN_SHIFT_SCENARIO_TEMPLATE = `Shift: tomorrow 9 AM to 1 PM with Irene Park on East 71st Street.
Requirements: client uses a Hoyer lift, has two small dogs, and needs help with transfers.
Goal: confirm whether the caregiver can work the shift and is comfortable with the requirements.`;

const WORKFLOW_OPTIONS: Array<{
    readonly value: WorkflowType;
    readonly label: string;
    readonly details: string;
}> = [
    {
        value: 'open_shift_callout',
        label: 'Open shift',
        details: OPEN_SHIFT_SCENARIO_TEMPLATE,
    },
    {
        value: 'multi_shift_outreach',
        label: 'Multi-shift',
        details: `Workflow: multi-shift outreach.
Shifts:
1. Tomorrow 9 AM to 1 PM with Irene Park on East 71st Street. Hoyer lift and two small dogs.
2. The next day 10 AM to 2 PM with Elaine Murphy on East 20th Street. Dog-friendly home and transfers.
3. Friday 8 AM to 12 PM with Leon Baptiste in Brooklyn. Mobility assistance and fall precautions.
Goal: find out which shift numbers the caregiver can cover.`,
    },
    {
        value: 'shift_confirmation',
        label: 'Confirmation',
        details: `Workflow: shift confirmation.
Assigned shift: tomorrow 1 PM to 5 PM with Irene Park on East 71st Street.
Requirements: medication reminders, Hoyer lift comfort, and two small dogs in the home.
Goal: confirm whether the caregiver is still good for the assigned shift.`,
    },
    {
        value: 'clock_in',
        label: 'Clock in',
        details: `Workflow: clock-in reminder.
Assigned shift: started 15 minutes ago with Irene Park on East 71st Street.
Issue: Phoebe does not see a clock-in yet.
Goal: get the caregiver to clock in now or explain why they cannot.`,
    },
    {
        value: 'clock_out',
        label: 'Clock out',
        details: `Workflow: clock-out reminder.
Assigned shift: ended 10 minutes ago with Leon Baptiste in Brooklyn.
Issue: Phoebe does not see a clock-out yet.
Goal: get the caregiver to clock out now or explain why they cannot.`,
    },
];

const WORKFLOW_CHANNEL_OPTIONS: Array<{
    readonly value: WorkflowChannel;
    readonly label: string;
}> = [
    { value: 'sms', label: 'SMS' },
    { value: 'voice', label: 'Voice' },
];

type CaregiverModelOption = (typeof CAREGIVER_MODEL_OPTIONS)[number];

function CaregiverSimPage() {
    const stateQuery = useCaregiverSimState();
    const now = useRelativeNow();
    const [selectedIdentifier, setSelectedIdentifier] = useState<string | null>(
        null,
    );
    const [selectedCounterparty, setSelectedCounterparty] = useState<
        string | null
    >(null);
    const [pendingAction, setPendingAction] = useState<PendingAction>(null);
    const [lastReseed, setLastReseed] =
        useState<CaregiverSimReseedResponse | null>(null);
    // `||` (not `??`) so an empty-string home care number from the API
    // still falls back to the default sim counterparty.
    const scenarioCounterparty =
        stateQuery.data?.phoebe_home_care_number ||
        DEFAULT_SIM_COUNTERPARTY_PHONE;
    const [workflowType, setWorkflowType] =
        useState<WorkflowType>('open_shift_callout');
    const [workflowChannel, setWorkflowChannel] =
        useState<WorkflowChannel>('sms');
    const [caregiverVoiceAudioMode, setCaregiverVoiceAudioMode] =
        useState<CaregiverVoiceAudioMode>(DEFAULT_CAREGIVER_VOICE_AUDIO_MODE);
    const [scenarioDetails, setScenarioDetails] = useState(
        DEFAULT_SCENARIO_DETAILS,
    );
    const [caregiverModelValue, setCaregiverModelValue] = useState(
        DEFAULT_CAREGIVER_MODEL_VALUE,
    );
    const [interventionBody, setInterventionBody] = useState('');
    const [pendingSimulation, setPendingSimulation] =
        useState<PendingSimulation | null>(null);
    const [pendingScenarioRuns, setPendingScenarioRuns] = useState<
        Record<string, PendingScenarioRun>
    >({});
    const pendingScenarioRunKeysRef = useRef(new Set<string>());
    const [voiceSimCalls, setVoiceSimCalls] = useState<VoiceSimCallsByKey>({});
    const voiceSimCallsRef = useRef<VoiceSimCallsByKey>({});
    const voiceTransportsRef = useRef(new Map<string, VoiceSimTransport>());
    const [voiceTranscripts, setVoiceTranscripts] =
        useState<VoiceTranscriptsByCallId>({});
    const voiceTranscriptsRef = useRef<VoiceTranscriptsByCallId>({});
    const [voiceToolCalls, setVoiceToolCalls] =
        useState<VoiceToolCallsByCallId>({});
    const [startingVoiceSimKeys, setStartingVoiceSimKeys] = useState<
        Set<string>
    >(() => new Set());
    const startingVoiceSimKeysRef = useRef(new Set<string>());
    const voiceAudioContextRef = useRef<AudioContext | null>(null);
    const nextVoicePlaybackTimeRef = useRef(0);
    const liveVoicePlaybackBuffersRef = useRef(
        new Map<string, LiveVoicePlaybackBuffer>(),
    );
    const selectedVoiceCallKeyRef = useRef<string | null>(null);
    const [replayingVoiceCallId, setReplayingVoiceCallId] = useState<
        string | null
    >(null);
    const replayingVoiceCallIdRef = useRef<string | null>(null);
    const [voiceReplayState, setVoiceReplayState] =
        useState<VoiceReplayState | null>(null);
    const voiceReplayStateRef = useRef<VoiceReplayState | null>(null);
    const voiceReplayBufferRef = useRef<AudioBuffer | null>(null);
    const voiceReplaySourceRef = useRef<AudioBufferSourceNode | null>(null);
    const voiceReplayStartedAtRef = useRef(0);
    const voiceReplayAnimationFrameRef = useRef<number | null>(null);
    const [logsRefreshToken, setLogsRefreshToken] = useState(0);
    const [detailsOpen, setDetailsOpen] = useState(false);
    const [expandedPersonalityIdentifier, setExpandedPersonalityIdentifier] =
        useState<string | null>(null);
    const previousMessageCountsRef = useRef(new Map<string, number>());
    const [newActivityIdentifiers, setNewActivityIdentifiers] = useState<
        Set<string>
    >(() => new Set());

    const personalities = useMemo(
        () => stateQuery.data?.personalities ?? [],
        [stateQuery.data?.personalities],
    );
    const selectedPersonality = useMemo(
        () =>
            personalities.find(
                (personality) => personality.identifier === selectedIdentifier,
            ) ?? personalities[0],
        [personalities, selectedIdentifier],
    );
    const expandedPersonality = useMemo(
        () =>
            personalities.find(
                (personality) =>
                    personality.identifier === expandedPersonalityIdentifier,
            ) ?? null,
        [expandedPersonalityIdentifier, personalities],
    );
    const selectedPendingScenarioRuns = useMemo(() => {
        if (!selectedPersonality) {
            return [];
        }

        return Object.values(pendingScenarioRuns).filter(
            (run) =>
                run.personalityIdentifier === selectedPersonality.identifier,
        );
    }, [pendingScenarioRuns, selectedPersonality]);
    const selectedPendingScenarioRun = selectedPendingScenarioRuns[0] ?? null;
    const selectedVoiceCallForPersonality = useMemo(
        () =>
            getLatestVoiceSimCallForPersonality(
                voiceSimCalls,
                selectedPersonality?.identifier ?? null,
            ),
        [selectedPersonality?.identifier, voiceSimCalls],
    );

    useEffect(() => {
        if (!selectedIdentifier && personalities[0]) {
            setSelectedIdentifier(personalities[0].identifier);
        }
    }, [personalities, selectedIdentifier]);

    useEffect(() => {
        if (expandedPersonalityIdentifier && !expandedPersonality) {
            setExpandedPersonalityIdentifier(null);
        }
    }, [expandedPersonality, expandedPersonalityIdentifier]);

    useEffect(() => {
        if (personalities.length === 0) {
            return;
        }

        const previousMessageCounts = previousMessageCountsRef.current;
        const nextMessageCounts = new Map(previousMessageCounts);
        const newActivityIdentifiers = new Set<string>();

        for (const personality of personalities) {
            const previousCount = previousMessageCounts.get(
                personality.identifier,
            );
            if (
                previousCount !== undefined &&
                personality.message_count > previousCount &&
                personality.identifier !== selectedPersonality?.identifier
            ) {
                newActivityIdentifiers.add(personality.identifier);
            }
            nextMessageCounts.set(
                personality.identifier,
                personality.message_count,
            );
        }
        previousMessageCountsRef.current = nextMessageCounts;

        setNewActivityIdentifiers((current) => {
            const next = new Set(current);
            let changed = false;

            for (const identifier of newActivityIdentifiers) {
                if (!next.has(identifier)) {
                    changed = true;
                }
                next.add(identifier);
            }

            if (
                selectedPersonality &&
                next.delete(selectedPersonality.identifier)
            ) {
                changed = true;
            }

            return changed ? next : current;
        });
    }, [personalities, selectedPersonality]);

    useEffect(() => {
        if (!selectedPersonality) {
            return;
        }

        const selectedVoiceCounterparty =
            selectedVoiceCallForPersonality?.counterpartyPhone ?? null;
        const preferredCounterparty =
            selectedPendingScenarioRun?.counterpartyPhone ||
            selectedVoiceCounterparty ||
            stateQuery.data?.phoebe_home_care_number ||
            selectedPersonality.threads[0]?.counterparty_phone ||
            null;
        const selectedStillExists =
            selectedPersonality.threads.some(
                (thread) => thread.counterparty_phone === selectedCounterparty,
            ) ||
            Object.values(voiceSimCalls).some(
                (call) =>
                    call.personalityIdentifier ===
                        selectedPersonality.identifier &&
                    call.counterpartyPhone === selectedCounterparty,
            ) ||
            selectedPendingScenarioRuns.some(
                (run) => run.counterpartyPhone === selectedCounterparty,
            );

        if (!selectedCounterparty) {
            setSelectedCounterparty(preferredCounterparty);
            return;
        }

        if (preferredCounterparty && !selectedStillExists) {
            setSelectedCounterparty(preferredCounterparty);
        }
    }, [
        selectedPendingScenarioRun,
        selectedPendingScenarioRuns,
        selectedCounterparty,
        selectedPersonality,
        selectedVoiceCallForPersonality,
        stateQuery.data?.phoebe_home_care_number,
        voiceSimCalls,
    ]);

    const activeCounterparty =
        selectedCounterparty ??
        selectedPendingScenarioRun?.counterpartyPhone ??
        null;
    const logsQuery = useCaregiverSimLogs(
        selectedPersonality?.identifier ?? null,
        activeCounterparty,
    );
    const caregiverDetailsQuery = useCaregiverSimCaregiverDetails(
        selectedPersonality?.identifier ?? null,
    );
    const memoryEventsQuery = useCaregiverSimMemoryEvents(
        selectedPersonality?.identifier ?? null,
        activeCounterparty,
    );
    const logs = logsQuery.data;
    const mutateLogs = logsQuery.mutate;
    const caregiverDetails = caregiverDetailsQuery.data;
    const memoryEvents = memoryEventsQuery.data;
    const resetSupported = stateQuery.data?.reset_supported ?? false;
    const visiblePendingSimulation =
        pendingSimulation?.counterpartyPhone === activeCounterparty
            ? pendingSimulation
            : null;
    const selectedScenarioRunKey =
        selectedPersonality && activeCounterparty
            ? scenarioRunKey(selectedPersonality.identifier, activeCounterparty)
            : null;
    const visiblePendingScenarioRun = selectedScenarioRunKey
        ? pendingScenarioRuns[selectedScenarioRunKey]
        : selectedPendingScenarioRun;
    const visibleVoiceSimCall = selectedScenarioRunKey
        ? (voiceSimCalls[selectedScenarioRunKey] ?? null)
        : selectedVoiceCallForPersonality;
    const visibleVoiceCallId = visibleVoiceSimCall?.callId ?? null;
    const visibleVoiceTranscript = useMemo(
        () =>
            visibleVoiceCallId
                ? (voiceTranscripts[visibleVoiceCallId] ?? [])
                : [],
        [visibleVoiceCallId, voiceTranscripts],
    );
    const visibleReplayState =
        visibleVoiceSimCall &&
        voiceReplayState?.callId === visibleVoiceSimCall.callId
            ? voiceReplayState
            : null;
    const visibleReplayDisabled =
        !!replayingVoiceCallId &&
        replayingVoiceCallId !== visibleVoiceSimCall?.callId;
    const visibleVoiceCallIsStarting =
        !!selectedScenarioRunKey &&
        startingVoiceSimKeys.has(selectedScenarioRunKey);
    const runScenarioTargetPhone = scenarioCounterparty.trim();
    const runVoiceTargetKey =
        selectedPersonality && runScenarioTargetPhone
            ? scenarioRunKey(
                  selectedPersonality.identifier,
                  runScenarioTargetPhone,
              )
            : null;
    const runVoiceTargetCall = runVoiceTargetKey
        ? (voiceSimCalls[runVoiceTargetKey] ?? null)
        : null;
    const runVoiceTargetIsStarting =
        !!runVoiceTargetKey && startingVoiceSimKeys.has(runVoiceTargetKey);
    const runVoiceTargetIsActive =
        !!runVoiceTargetCall &&
        !isTerminalVoiceSimStatus(runVoiceTargetCall.status);
    const runVoiceTargetIsBusy =
        runVoiceTargetIsStarting || runVoiceTargetIsActive;
    const continueScenarioTargetPhone = (
        activeCounterparty ?? scenarioCounterparty
    ).trim();
    const continueVoiceTargetKey =
        selectedPersonality && continueScenarioTargetPhone
            ? scenarioRunKey(
                  selectedPersonality.identifier,
                  continueScenarioTargetPhone,
              )
            : null;
    const continueVoiceTargetCall = continueVoiceTargetKey
        ? (voiceSimCalls[continueVoiceTargetKey] ?? null)
        : null;
    const continueVoiceTargetIsStarting =
        !!continueVoiceTargetKey &&
        startingVoiceSimKeys.has(continueVoiceTargetKey);
    const continueVoiceTargetIsActive =
        !!continueVoiceTargetCall &&
        !isTerminalVoiceSimStatus(continueVoiceTargetCall.status);
    const continueVoiceTargetIsBusy =
        continueVoiceTargetIsStarting || continueVoiceTargetIsActive;
    const runScenarioTargetRun =
        selectedPersonality && runScenarioTargetPhone
            ? pendingScenarioRuns[
                  scenarioRunKey(
                      selectedPersonality.identifier,
                      runScenarioTargetPhone,
                  )
              ]
            : null;
    const continueScenarioTargetRun =
        selectedPersonality && continueScenarioTargetPhone
            ? pendingScenarioRuns[
                  scenarioRunKey(
                      selectedPersonality.identifier,
                      continueScenarioTargetPhone,
                  )
              ]
            : null;
    const hasAnyScenarioRun = Object.keys(pendingScenarioRuns).length > 0;
    const baseThreads =
        logs?.threads.length || !selectedPersonality
            ? (logs?.threads ?? [])
            : selectedPersonality.threads;
    const threads = withPendingScenarioThreads(
        baseThreads,
        selectedPendingScenarioRuns,
    );
    const stateErrorMessage = stateQuery.error
        ? getCaregiverSimErrorMessage(stateQuery.error)
        : null;
    const caregiverModelOption = getCaregiverModelOption(caregiverModelValue);
    const resetLiveVoicePlayback = useCallback(() => {
        for (const buffer of liveVoicePlaybackBuffersRef.current.values()) {
            if (buffer.flushTimeoutId !== null) {
                window.clearTimeout(buffer.flushTimeoutId);
            }
        }
        liveVoicePlaybackBuffersRef.current.clear();
        nextVoicePlaybackTimeRef.current =
            voiceAudioContextRef.current?.currentTime ?? 0;
    }, []);

    useEffect(() => {
        voiceSimCallsRef.current = voiceSimCalls;
    }, [voiceSimCalls]);

    useEffect(() => {
        voiceTranscriptsRef.current = voiceTranscripts;
    }, [voiceTranscripts]);

    useEffect(() => {
        selectedVoiceCallKeyRef.current = selectedScenarioRunKey;
        resetLiveVoicePlayback();
    }, [resetLiveVoicePlayback, selectedScenarioRunKey]);

    useEffect(() => {
        replayingVoiceCallIdRef.current = replayingVoiceCallId;
    }, [replayingVoiceCallId]);

    useEffect(() => {
        voiceReplayStateRef.current = voiceReplayState;
    }, [voiceReplayState]);

    useEffect(() => {
        if (logsRefreshToken === 0) {
            return;
        }

        void mutateLogs();
    }, [logsRefreshToken, mutateLogs]);

    const refreshAll = useCallback(async () => {
        await Promise.all([
            stateQuery.mutate(),
            caregiverDetailsQuery.mutate(),
            memoryEventsQuery.mutate(),
        ]);
        setLogsRefreshToken((token) => token + 1);
    }, [caregiverDetailsQuery, memoryEventsQuery, stateQuery]);

    const sendSimulationTurn = useCallback(
        async ({
            counterpartyPhone,
            messageBody,
        }: {
            readonly counterpartyPhone: string;
            readonly messageBody: string;
        }) => {
            if (!selectedPersonality) {
                return;
            }

            setSelectedCounterparty(counterpartyPhone);
            setPendingSimulation({
                counterpartyPhone,
                body: messageBody,
                personalityName: selectedPersonality.name,
                startedAt: new Date().toISOString(),
            });
            await simulateCaregiverSimMessage({
                personality_identifier: selectedPersonality.identifier,
                counterparty_phone: counterpartyPhone,
                body: messageBody,
                response_channel: 'sms',
                model_provider: caregiverModelOption.provider,
                model: caregiverModelOption.model,
            });
            setSelectedCounterparty(counterpartyPhone);
            await stateQuery.mutate();
            setLogsRefreshToken((token) => token + 1);
        },
        [caregiverModelOption, selectedPersonality, stateQuery],
    );

    const applyWorkflowTemplate = useCallback(
        (nextWorkflowType: WorkflowType) => {
            const option = WORKFLOW_OPTIONS.find(
                (item) => item.value === nextWorkflowType,
            );
            if (option) {
                setScenarioDetails(option.details);
            }
        },
        [],
    );

    const setVoiceSimCallForKey = useCallback(
        (
            callKey: string,
            updater: (call: VoiceSimCall | null) => VoiceSimCall | null,
        ) => {
            setVoiceSimCalls((current) => {
                const nextCall = updater(current[callKey] ?? null);
                if (!nextCall) {
                    if (!(callKey in current)) {
                        return current;
                    }

                    const next = { ...current };
                    delete next[callKey];
                    return next;
                }

                if (current[callKey] === nextCall) {
                    return current;
                }

                return {
                    ...current,
                    [callKey]: nextCall,
                };
            });
        },
        [],
    );

    const updateVoiceSimCallStatus = useCallback(
        ({
            callKey,
            callId,
            status,
            statusText,
            errorMessage = null,
        }: {
            readonly callKey: string;
            readonly callId: string;
            readonly status: VoiceSimStatus;
            readonly statusText: string;
            readonly errorMessage?: string | null;
        }) => {
            setVoiceSimCallForKey(callKey, (current) =>
                current?.callId === callId
                    ? {
                          ...current,
                          status,
                          statusText,
                          errorMessage,
                      }
                    : current,
            );
        },
        [setVoiceSimCallForKey],
    );

    const setVoiceSimStarting = useCallback(
        (callKey: string, isStarting: boolean) => {
            if (isStarting) {
                startingVoiceSimKeysRef.current.add(callKey);
            } else {
                startingVoiceSimKeysRef.current.delete(callKey);
            }
            setStartingVoiceSimKeys((current) => {
                const next = new Set(current);
                if (isStarting) {
                    next.add(callKey);
                } else {
                    next.delete(callKey);
                }
                return next.size === current.size &&
                    next.has(callKey) === current.has(callKey)
                    ? current
                    : next;
            });
        },
        [],
    );

    const stopVoiceTransport = useCallback(
        (callId: string, options: { readonly sendStop?: boolean } = {}) => {
            const transport = voiceTransportsRef.current.get(callId);
            if (!transport) {
                return;
            }

            if (transport.silenceIntervalId !== null) {
                window.clearInterval(transport.silenceIntervalId);
                transport.silenceIntervalId = null;
            }
            if (transport.timelineIntervalId !== null) {
                window.clearInterval(transport.timelineIntervalId);
                transport.timelineIntervalId = null;
            }
            const playbackBuffer = liveVoicePlaybackBuffersRef.current.get(
                transport.callKey,
            );
            if (playbackBuffer && playbackBuffer.flushTimeoutId !== null) {
                window.clearTimeout(playbackBuffer.flushTimeoutId);
            }
            liveVoicePlaybackBuffersRef.current.delete(transport.callKey);
            const sendStop = options.sendStop ?? true;
            if (sendStop && transport.socket.readyState === WebSocket.OPEN) {
                transport.socket.send(JSON.stringify({ event: 'stop' }));
            }
            if (
                transport.socket.readyState === WebSocket.OPEN ||
                transport.socket.readyState === WebSocket.CONNECTING
            ) {
                transport.socket.close();
            }
            voiceTransportsRef.current.delete(callId);
        },
        [],
    );

    const stopAllVoiceTransports = useCallback(() => {
        for (const callId of Array.from(voiceTransportsRef.current.keys())) {
            stopVoiceTransport(callId);
        }
    }, [stopVoiceTransport]);

    const appendVoiceTranscriptEntry = useCallback(
        ({
            callId,
            speaker,
            text,
            audioPayload = null,
        }: {
            readonly callId: string;
            readonly speaker: VoiceTranscriptSpeaker;
            readonly text: string;
            readonly audioPayload?: string | null;
        }) => {
            const trimmedText = text.trim();
            if (!trimmedText) {
                return;
            }

            const key = `${speaker}:${trimmedText}`;
            const transport = voiceTransportsRef.current.get(callId);
            if (transport?.transcriptKeys.has(key)) {
                return;
            }
            transport?.transcriptKeys.add(key);

            setVoiceTranscripts((current) => {
                const entries = current[callId] ?? [];
                if (
                    entries.some(
                        (entry) =>
                            entry.speaker === speaker &&
                            entry.text === trimmedText,
                    )
                ) {
                    return current;
                }

                const pendingAudioPayloads =
                    speaker === 'phoebe'
                        ? [...(transport?.pendingAssistantAudioPayloads ?? [])]
                        : [];
                if (speaker === 'phoebe' && transport) {
                    transport.pendingAssistantAudioPayloads = [];
                }
                const audioPayloads = audioPayload
                    ? [audioPayload]
                    : pendingAudioPayloads;

                return {
                    ...current,
                    [callId]: [
                        ...entries,
                        {
                            id: `${Date.now()}:${entries.length}:${speaker}`,
                            speaker,
                            text: trimmedText,
                            audioPayload: audioPayloads[0] ?? null,
                            audioPayloads,
                            createdAt: new Date().toISOString(),
                        },
                    ],
                };
            });
        },
        [],
    );

    const appendVoiceTranscriptAudioPayload = useCallback(
        ({
            callId,
            speaker,
            audioPayload,
        }: {
            readonly callId: string;
            readonly speaker: VoiceTranscriptSpeaker;
            readonly audioPayload: string;
        }) => {
            if (!audioPayload) {
                return;
            }

            const transport = voiceTransportsRef.current.get(callId);
            let appended = false;
            setVoiceTranscripts((current) => {
                const entries = current[callId] ?? [];
                let targetIndex = -1;
                for (let index = entries.length - 1; index >= 0; index -= 1) {
                    if (entries[index]?.speaker === speaker) {
                        targetIndex = index;
                        break;
                    }
                }
                if (targetIndex === -1) {
                    return current;
                }

                const nextEntries = [...entries];
                const targetEntry = nextEntries[targetIndex];
                if (!targetEntry) {
                    return current;
                }

                appended = true;
                const audioPayloads = [
                    ...targetEntry.audioPayloads,
                    audioPayload,
                ];
                nextEntries[targetIndex] = {
                    ...targetEntry,
                    audioPayload: targetEntry.audioPayload ?? audioPayload,
                    audioPayloads,
                };
                return {
                    ...current,
                    [callId]: nextEntries,
                };
            });

            if (!appended && speaker === 'phoebe' && transport) {
                transport.pendingAssistantAudioPayloads.push(audioPayload);
            }
        },
        [],
    );

    const endVoiceSimCall = useCallback(
        async (
            call: VoiceSimCall | null | undefined,
            options: {
                readonly reason?: string;
                readonly finalStatusText?: string;
            } = {},
        ) => {
            if (!call || !isEndableVoiceSimStatus(call.status)) {
                return;
            }

            const callKey = scenarioRunKey(
                call.personalityIdentifier,
                call.counterpartyPhone,
            );
            const transport = voiceTransportsRef.current.get(call.callId);
            if (transport) {
                transport.ending = true;
            }
            updateVoiceSimCallStatus({
                callKey,
                callId: call.callId,
                status: 'ending',
                statusText: 'Ending call',
            });
            stopVoiceTransport(call.callId);
            try {
                await endCaregiverSimVoiceSession(call.callId, {
                    reason: options.reason ?? 'caregiver_sim_voice_stop',
                });
            } catch (error) {
                toast.error(getCaregiverSimErrorMessage(error), {
                    persistNotification: false,
                });
            } finally {
                setVoiceSimCallForKey(callKey, (current) =>
                    current?.callId === call.callId
                        ? {
                              ...current,
                              status: 'ended',
                              statusText:
                                  options.finalStatusText ?? 'Call ended',
                          }
                        : current,
                );
            }
        },
        [setVoiceSimCallForKey, stopVoiceTransport, updateVoiceSimCallStatus],
    );

    const endActiveVoiceSimCalls = useCallback(
        async (predicate: (call: VoiceSimCall) => boolean) => {
            const calls = Object.values(voiceSimCallsRef.current).filter(
                (call) =>
                    predicate(call) && isEndableVoiceSimStatus(call.status),
            );
            await Promise.all(calls.map((call) => endVoiceSimCall(call)));
        },
        [endVoiceSimCall],
    );

    const removeVoiceSimCalls = useCallback(
        (predicate: (call: VoiceSimCall) => boolean) => {
            for (const call of Object.values(voiceSimCallsRef.current)) {
                if (predicate(call)) {
                    stopVoiceTransport(call.callId, { sendStop: false });
                }
            }
            setVoiceSimCalls((current) => {
                const next: VoiceSimCallsByKey = {};
                for (const [callKey, call] of Object.entries(current)) {
                    if (!predicate(call)) {
                        next[callKey] = call;
                    }
                }
                return next;
            });
            setVoiceTranscripts((current) => {
                const removedCallIds = new Set(
                    Object.values(voiceSimCallsRef.current)
                        .filter(predicate)
                        .map((call) => call.callId),
                );
                if (removedCallIds.size === 0) {
                    return current;
                }

                const next: VoiceTranscriptsByCallId = {};
                for (const [callId, entries] of Object.entries(current)) {
                    if (!removedCallIds.has(callId)) {
                        next[callId] = entries;
                    }
                }
                return next;
            });
            setVoiceToolCalls((current) => {
                const removedCallIds = new Set(
                    Object.values(voiceSimCallsRef.current)
                        .filter(predicate)
                        .map((call) => call.callId),
                );
                if (removedCallIds.size === 0) {
                    return current;
                }

                const next: VoiceToolCallsByCallId = {};
                for (const [callId, toolCalls] of Object.entries(current)) {
                    if (!removedCallIds.has(callId)) {
                        next[callId] = toolCalls;
                    }
                }
                return next;
            });
        },
        [stopVoiceTransport],
    );

    const isVoiceSimCallActive = useCallback((callId: string) => {
        const transport = voiceTransportsRef.current.get(callId);
        if (!transport || transport.ending) {
            return false;
        }

        const currentCall = voiceSimCallsRef.current[transport.callKey];
        return (
            !!currentCall &&
            currentCall.callId === callId &&
            !isTerminalVoiceSimStatus(currentCall.status)
        );
    }, []);

    const shouldPlayLiveVoiceCall = useCallback((call: VoiceSimCall) => {
        return (
            selectedVoiceCallKeyRef.current ===
            scenarioRunKey(call.personalityIdentifier, call.counterpartyPhone)
        );
    }, []);

    const clearLiveVoicePlaybackBuffer = useCallback((bufferKey: string) => {
        const buffer = liveVoicePlaybackBuffersRef.current.get(bufferKey);
        if (!buffer) {
            return;
        }
        if (buffer.flushTimeoutId !== null) {
            window.clearTimeout(buffer.flushTimeoutId);
        }
        liveVoicePlaybackBuffersRef.current.delete(bufferKey);
    }, []);

    const playMuLawBytes = useCallback(
        (
            mediaBytes: Uint8Array,
            options: { readonly playbackRate?: number } = {},
        ): number => {
            const AudioContextConstructor = getBrowserAudioContextConstructor();
            if (!AudioContextConstructor) {
                return 0;
            }

            const audioContext =
                voiceAudioContextRef.current ?? new AudioContextConstructor();
            voiceAudioContextRef.current = audioContext;
            if (audioContext.state === 'suspended') {
                void audioContext.resume();
            }

            if (mediaBytes.length === 0) {
                return 0;
            }

            const audioBuffer = audioContext.createBuffer(
                1,
                mediaBytes.length,
                8000,
            );
            const channel = audioBuffer.getChannelData(0);
            for (let index = 0; index < mediaBytes.length; index += 1) {
                channel[index] =
                    decodeMuLawSample(mediaBytes[index] ?? 0) / 32768;
            }

            const source = audioContext.createBufferSource();
            source.buffer = audioBuffer;
            const playbackRate = options.playbackRate ?? 1;
            source.playbackRate.value = playbackRate;
            source.connect(audioContext.destination);
            const startAt = Math.max(
                audioContext.currentTime,
                nextVoicePlaybackTimeRef.current,
            );
            source.start(startAt);
            const durationSeconds = audioBuffer.duration / playbackRate;
            nextVoicePlaybackTimeRef.current = startAt + durationSeconds;
            return durationSeconds * 1000;
        },
        [],
    );

    const playVoiceMediaPayload = useCallback(
        (
            payload: string,
            options: { readonly playbackRate?: number } = {},
        ): number => playMuLawBytes(base64ToUint8Array(payload), options),
        [playMuLawBytes],
    );

    const synthesizeVoicePayload = useCallback(
        async (callId: string, text: string): Promise<string> => {
            const result = await synthesizeCaregiverSimVoiceUtterance(callId, {
                text,
            });
            return result.audio_payload;
        },
        [],
    );

    const cancelVoiceReplayProgress = useCallback(() => {
        if (voiceReplayAnimationFrameRef.current !== null) {
            window.cancelAnimationFrame(voiceReplayAnimationFrameRef.current);
            voiceReplayAnimationFrameRef.current = null;
        }
    }, []);

    const updateVoiceReplayProgress = useCallback(function updateProgress() {
        const replayState = voiceReplayStateRef.current;
        const audioBuffer = voiceReplayBufferRef.current;
        const audioContext = voiceAudioContextRef.current;
        if (!replayState?.playing || !audioBuffer || !audioContext) {
            return;
        }

        const durationMs = audioBuffer.duration * 1000;
        const positionMs = Math.min(
            durationMs,
            Math.max(
                0,
                (audioContext.currentTime - voiceReplayStartedAtRef.current) *
                    1000,
            ),
        );
        setVoiceReplayState((current) =>
            current?.callId === replayState.callId
                ? {
                      ...current,
                      positionMs,
                      durationMs,
                  }
                : current,
        );

        if (positionMs < durationMs) {
            voiceReplayAnimationFrameRef.current =
                window.requestAnimationFrame(updateProgress);
        }
    }, []);

    const stopVoiceReplayAudio = useCallback(() => {
        cancelVoiceReplayProgress();
        const source = voiceReplaySourceRef.current;
        voiceReplaySourceRef.current = null;
        if (!source) {
            return;
        }

        source.onended = null;
        try {
            source.stop();
        } catch {
            // The source may already have ended naturally.
        }
    }, [cancelVoiceReplayProgress]);

    const startVoiceReplayAudio = useCallback(
        ({
            callId,
            audioBuffer,
            positionMs,
        }: {
            readonly callId: string;
            readonly audioBuffer: AudioBuffer;
            readonly positionMs: number;
        }) => {
            const audioContext = voiceAudioContextRef.current;
            if (!audioContext) {
                return;
            }

            stopVoiceReplayAudio();
            voiceReplayBufferRef.current = audioBuffer;
            const durationMs = audioBuffer.duration * 1000;
            const clampedPositionMs = clampNumber(positionMs, 0, durationMs);
            if (clampedPositionMs >= durationMs) {
                setReplayingVoiceCallId(null);
                setVoiceReplayState({
                    callId,
                    positionMs: durationMs,
                    durationMs,
                    playing: false,
                });
                return;
            }

            const source = audioContext.createBufferSource();
            source.buffer = audioBuffer;
            source.connect(audioContext.destination);
            voiceReplaySourceRef.current = source;
            voiceReplayStartedAtRef.current =
                audioContext.currentTime - clampedPositionMs / 1000;
            source.onended = () => {
                if (voiceReplaySourceRef.current !== source) {
                    return;
                }
                voiceReplaySourceRef.current = null;
                cancelVoiceReplayProgress();
                setReplayingVoiceCallId(null);
                setVoiceReplayState((current) =>
                    current?.callId === callId
                        ? {
                              ...current,
                              positionMs: durationMs,
                              durationMs,
                              playing: false,
                          }
                        : current,
                );
            };
            source.start(0, clampedPositionMs / 1000);
            const nextReplayState = {
                callId,
                positionMs: clampedPositionMs,
                durationMs,
                playing: true,
            };
            voiceReplayStateRef.current = nextReplayState;
            setReplayingVoiceCallId(callId);
            setVoiceReplayState(nextReplayState);
            updateVoiceReplayProgress();
        },
        [
            cancelVoiceReplayProgress,
            stopVoiceReplayAudio,
            updateVoiceReplayProgress,
        ],
    );

    const buildVoiceReplayAudioBuffer = useCallback(
        async (
            callId: string,
            entries: ReadonlyArray<VoiceTranscriptEntry>,
        ): Promise<AudioBuffer | null> => {
            await prepareBrowserAudioContext(voiceAudioContextRef, {
                resume: false,
            });
            const audioContext = voiceAudioContextRef.current;
            if (!audioContext) {
                return null;
            }

            const chunks: Array<Float32Array> = [];
            for (const entry of entries) {
                let audioPayloads = getVoiceTranscriptAudioPayloads(entry);
                if (audioPayloads.length === 0) {
                    const synthesizedPayload = await synthesizeVoicePayload(
                        callId,
                        entry.text,
                    );
                    audioPayloads = synthesizedPayload
                        ? [synthesizedPayload]
                        : [];
                }

                for (const audioPayload of audioPayloads) {
                    const samples = decodeMuLawPayloadToFloat32(audioPayload);
                    if (samples.length > 0) {
                        chunks.push(samples);
                    }
                }
                if (audioPayloads.length > 0) {
                    chunks.push(silenceSamples(VOICE_REPLAY_TURN_PAUSE_MS));
                }
            }

            const sampleCount = chunks.reduce(
                (total, chunk) => total + chunk.length,
                0,
            );
            if (sampleCount === 0) {
                return null;
            }

            const audioBuffer = audioContext.createBuffer(1, sampleCount, 8000);
            const channel = audioBuffer.getChannelData(0);
            let offset = 0;
            for (const chunk of chunks) {
                channel.set(chunk, offset);
                offset += chunk.length;
            }
            return audioBuffer;
        },
        [synthesizeVoicePayload],
    );

    useEffect(() => {
        if (
            !visibleVoiceSimCall ||
            !isTerminalVoiceSimStatus(visibleVoiceSimCall.status) ||
            visibleVoiceTranscript.length === 0
        ) {
            return;
        }

        const callId = visibleVoiceSimCall.callId;
        const existingReplayState = voiceReplayStateRef.current;
        if (
            existingReplayState?.callId === callId &&
            voiceReplayBufferRef.current
        ) {
            return;
        }

        let cancelled = false;
        void (async () => {
            const audioBuffer = await buildVoiceReplayAudioBuffer(
                callId,
                visibleVoiceTranscript,
            );
            if (cancelled || !audioBuffer) {
                return;
            }

            const currentReplayState = voiceReplayStateRef.current;
            if (
                currentReplayState?.callId === callId &&
                currentReplayState.playing
            ) {
                return;
            }

            voiceReplayBufferRef.current = audioBuffer;
            const durationMs = audioBuffer.duration * 1000;
            setVoiceReplayState((current) =>
                current?.callId === callId && current.playing
                    ? current
                    : {
                          callId,
                          positionMs:
                              current?.callId === callId
                                  ? clampNumber(
                                        current.positionMs,
                                        0,
                                        durationMs,
                                    )
                                  : 0,
                          durationMs,
                          playing: false,
                      },
            );
        })();

        return () => {
            cancelled = true;
        };
    }, [
        buildVoiceReplayAudioBuffer,
        visibleVoiceSimCall,
        visibleVoiceTranscript,
    ]);

    const seekVoiceReplay = useCallback(
        (callId: string, positionMs: number) => {
            const audioBuffer = voiceReplayBufferRef.current;
            const replayState = voiceReplayStateRef.current;
            if (!audioBuffer || replayState?.callId !== callId) {
                return;
            }

            const durationMs = audioBuffer.duration * 1000;
            const clampedPositionMs = clampNumber(positionMs, 0, durationMs);
            if (replayState.playing) {
                startVoiceReplayAudio({
                    callId,
                    audioBuffer,
                    positionMs: clampedPositionMs,
                });
                return;
            }

            setVoiceReplayState({
                callId,
                positionMs: clampedPositionMs,
                durationMs,
                playing: false,
            });
        },
        [startVoiceReplayAudio],
    );

    const flushLiveVoicePlaybackBuffer = useCallback(
        (bufferKey: string) => {
            const buffer = liveVoicePlaybackBuffersRef.current.get(bufferKey);
            if (!buffer) {
                return;
            }
            if (buffer.flushTimeoutId !== null) {
                window.clearTimeout(buffer.flushTimeoutId);
                buffer.flushTimeoutId = null;
            }
            if (buffer.byteLength === 0) {
                liveVoicePlaybackBuffersRef.current.delete(bufferKey);
                return;
            }

            const mergedBytes = new Uint8Array(buffer.byteLength);
            let offset = 0;
            for (const chunk of buffer.chunks) {
                mergedBytes.set(chunk, offset);
                offset += chunk.length;
            }
            buffer.chunks = [];
            buffer.byteLength = 0;
            playMuLawBytes(mergedBytes);
        },
        [playMuLawBytes],
    );

    const queueLiveVoiceMediaPayload = useCallback(
        (bufferKey: string, payload: string) => {
            const mediaBytes = base64ToUint8Array(payload);
            if (mediaBytes.length === 0) {
                return;
            }

            let buffer = liveVoicePlaybackBuffersRef.current.get(bufferKey);
            if (!buffer) {
                buffer = {
                    chunks: [],
                    byteLength: 0,
                    flushTimeoutId: null,
                };
                liveVoicePlaybackBuffersRef.current.set(bufferKey, buffer);
            }
            buffer.chunks.push(mediaBytes);
            buffer.byteLength += mediaBytes.length;

            if (buffer.byteLength >= VOICE_LIVE_AUDIO_BUFFER_BYTES) {
                flushLiveVoicePlaybackBuffer(bufferKey);
                return;
            }
            if (buffer.flushTimeoutId === null) {
                buffer.flushTimeoutId = window.setTimeout(() => {
                    flushLiveVoicePlaybackBuffer(bufferKey);
                }, VOICE_LIVE_AUDIO_BUFFER_MS);
            }
        },
        [flushLiveVoicePlaybackBuffer],
    );

    const playCaregiverVoiceResponse = useCallback(
        async (call: VoiceSimCall, text: string): Promise<boolean> => {
            const transport = voiceTransportsRef.current.get(call.callId);
            const socket = transport?.socket;
            if (!socket || socket.readyState !== WebSocket.OPEN) {
                throw new Error('Voice websocket is not connected.');
            }

            if (call.caregiverAudioMode === 'voice_agent') {
                pauseSyntheticTwilioSilence(transport);
                try {
                    const audioPayload = await synthesizeVoicePayload(
                        call.callId,
                        text,
                    );
                    if (shouldPlayLiveVoiceCall(call)) {
                        playVoiceMediaPayload(audioPayload);
                    }
                    const didStreamAudio =
                        await streamSyntheticTwilioMediaPayload({
                            websocket: socket,
                            streamSid: call.streamSid,
                            payload: audioPayload,
                        });
                    if (
                        !didStreamAudio ||
                        socket.readyState !== WebSocket.OPEN
                    ) {
                        return false;
                    }

                    appendVoiceTranscriptEntry({
                        callId: call.callId,
                        speaker: 'caregiver',
                        text,
                        audioPayload,
                    });
                    return true;
                } finally {
                    resumeSyntheticTwilioSilence(
                        transport,
                        socket,
                        call.streamSid,
                    );
                }
            }

            pauseSyntheticTwilioSilence(transport);
            try {
                const audioPayload = await synthesizeVoicePayload(
                    call.callId,
                    text,
                );
                const playbackDurationMs = shouldPlayLiveVoiceCall(call)
                    ? playVoiceMediaPayload(audioPayload)
                    : getMuLawPayloadDurationMs(audioPayload);
                if (playbackDurationMs > 0) {
                    await sleepMs(playbackDurationMs);
                }
                if (socket.readyState !== WebSocket.OPEN) {
                    return false;
                }

                await injectCaregiverSimVoiceUtterance(call.callId, {
                    text,
                    user_id: 'caregiver-sim',
                });
                appendVoiceTranscriptEntry({
                    callId: call.callId,
                    speaker: 'caregiver',
                    text,
                    audioPayload,
                });
                return true;
            } finally {
                resumeSyntheticTwilioSilence(transport, socket, call.streamSid);
            }
        },
        [
            appendVoiceTranscriptEntry,
            playVoiceMediaPayload,
            shouldPlayLiveVoiceCall,
            synthesizeVoicePayload,
        ],
    );

    const finishVoiceHandledTurn = useCallback(
        ({
            call,
            status,
            statusText,
        }: {
            readonly call: VoiceSimCall;
            readonly status: VoiceSimStatus;
            readonly statusText: string;
        }): boolean => {
            const transport = voiceTransportsRef.current.get(call.callId);
            if (!transport) {
                return false;
            }

            transport.turnsCompleted += 1;
            const turnsCompleted = transport.turnsCompleted;
            setVoiceSimCallForKey(transport.callKey, (current) =>
                current?.callId === call.callId
                    ? {
                          ...current,
                          turnsCompleted,
                          status,
                          statusText,
                      }
                    : current,
            );
            return turnsCompleted >= VOICE_SIM_MAX_TURNS;
        },
        [setVoiceSimCallForKey],
    );

    const respondToVoiceAssistantText = useCallback(
        async (callId: string, assistantText: string): Promise<boolean> => {
            const transport = voiceTransportsRef.current.get(callId);
            const call = transport
                ? voiceSimCallsRef.current[transport.callKey]
                : null;
            if (!transport || !call || !isVoiceSimCallActive(callId)) {
                return false;
            }
            if (transport.turnsCompleted >= VOICE_SIM_MAX_TURNS) {
                await endVoiceSimCall(call);
                return true;
            }

            updateVoiceSimCallStatus({
                callKey: transport.callKey,
                callId,
                status: 'caregiver-thinking',
                statusText: 'Caregiver thinking',
            });

            const result = await simulateCaregiverSimMessage({
                personality_identifier: call.personalityIdentifier,
                counterparty_phone: call.counterpartyPhone,
                body: assistantText,
                response_channel: 'voice',
                scenario_details: call.scenarioContext,
                model_provider: caregiverModelOption.provider,
                model: caregiverModelOption.model,
            });
            if (!isVoiceSimCallActive(callId)) {
                return false;
            }

            const response = result.response?.trim();
            if (result.decision === 'ghost' && !response) {
                await endVoiceSimCall(call, {
                    reason: 'caregiver_sim_voice_no_answer_memory',
                    finalStatusText: 'No answer: caregiver prefers SMS',
                });
                return true;
            }

            if (!response) {
                const reachedTurnCap = finishVoiceHandledTurn({
                    call,
                    status: 'listening',
                    statusText: 'Listening',
                });
                if (reachedTurnCap) {
                    await endVoiceSimCall(call);
                }
                return true;
            }

            updateVoiceSimCallStatus({
                callKey: transport.callKey,
                callId,
                status: 'caregiver-speaking',
                statusText: 'Caregiver speaking',
            });
            const didInjectCaregiverResponse = await playCaregiverVoiceResponse(
                call,
                response,
            );
            if (!didInjectCaregiverResponse) {
                transport.ending = true;
                updateVoiceSimCallStatus({
                    callKey: transport.callKey,
                    callId,
                    status: 'error',
                    statusText: 'Voice socket closed',
                    errorMessage:
                        'Voice websocket closed before the caregiver response could be sent.',
                });
                stopVoiceTransport(callId, { sendStop: false });
                void endCaregiverSimVoiceSession(callId, {
                    reason: 'caregiver_sim_voice_inject_skipped',
                }).catch(() => undefined);
                return false;
            }
            if (!isVoiceSimCallActive(callId)) {
                return false;
            }

            const reachedTurnCap = finishVoiceHandledTurn({
                call,
                status: 'listening',
                statusText: 'Listening',
            });
            if (reachedTurnCap) {
                await endVoiceSimCall(call);
            }
            return true;
        },
        [
            caregiverModelOption,
            endVoiceSimCall,
            finishVoiceHandledTurn,
            isVoiceSimCallActive,
            playCaregiverVoiceResponse,
            stopVoiceTransport,
            updateVoiceSimCallStatus,
        ],
    );

    const pollVoiceTimeline = useCallback(
        async (callId: string) => {
            const transport = voiceTransportsRef.current.get(callId);
            if (!transport || transport.timelinePolling) {
                return;
            }

            transport.timelinePolling = true;
            try {
                const timeline = await getCaregiverSimVoiceTimeline(callId);
                const timelineEntries =
                    timeline.entries as Array<VoiceTimelineEntry>;
                const toolCalls =
                    extractVoiceTimelineToolCalls(timelineEntries);
                setVoiceToolCalls((current) => ({
                    ...current,
                    [callId]: toolCalls,
                }));
                const transcriptEntries =
                    extractVoiceTimelineTranscriptEntries(timelineEntries);
                for (const entry of transcriptEntries) {
                    appendVoiceTranscriptEntry({
                        callId,
                        speaker: entry.speaker,
                        text: entry.text,
                    });
                }
                const assistantPrompts =
                    extractCompletedVoiceAssistantPrompts(timelineEntries);
                for (const prompt of assistantPrompts) {
                    if (transport.seenAssistantTexts.has(prompt.text)) {
                        continue;
                    }
                    const didRespond = await respondToVoiceAssistantText(
                        callId,
                        prompt.text,
                    );
                    if (didRespond) {
                        transport.seenAssistantTexts.add(prompt.text);
                    }
                }
            } catch (error) {
                const call = voiceSimCallsRef.current[transport.callKey];
                if (
                    !call ||
                    call.callId !== callId ||
                    transport.ending ||
                    isTerminalVoiceSimStatus(call.status)
                ) {
                    return;
                }
                updateVoiceSimCallStatus({
                    callKey: transport.callKey,
                    callId,
                    status: 'error',
                    statusText: 'Voice timeline error',
                    errorMessage: getCaregiverSimErrorMessage(error),
                });
            } finally {
                const currentTransport = voiceTransportsRef.current.get(callId);
                if (currentTransport) {
                    currentTransport.timelinePolling = false;
                }
            }
        },
        [
            appendVoiceTranscriptEntry,
            respondToVoiceAssistantText,
            updateVoiceSimCallStatus,
        ],
    );

    const startVoiceTimelinePolling = useCallback(
        (callId: string) => {
            const transport = voiceTransportsRef.current.get(callId);
            if (!transport) {
                return;
            }

            if (transport.timelineIntervalId !== null) {
                window.clearInterval(transport.timelineIntervalId);
            }
            void pollVoiceTimeline(callId);
            transport.timelineIntervalId = window.setInterval(() => {
                void pollVoiceTimeline(callId);
            }, VOICE_TIMELINE_POLL_INTERVAL_MS);
        },
        [pollVoiceTimeline],
    );

    const replayVoiceEntries = useCallback(
        async (
            callId: string,
            transcriptEntries: ReadonlyArray<VoiceTranscriptEntry>,
        ) => {
            if (transcriptEntries.length === 0) {
                return;
            }
            if (replayingVoiceCallIdRef.current) {
                toast.error('A voice replay is already running.', {
                    persistNotification: false,
                });
                return;
            }

            try {
                const audioBuffer = await buildVoiceReplayAudioBuffer(
                    callId,
                    transcriptEntries,
                );
                if (!audioBuffer) {
                    toast.error('No replay audio is available for this call.', {
                        persistNotification: false,
                    });
                    return;
                }
                const durationMs = audioBuffer.duration * 1000;
                const currentReplayState = voiceReplayStateRef.current;
                const positionMs =
                    currentReplayState?.callId === callId &&
                    currentReplayState.positionMs < durationMs
                        ? currentReplayState.positionMs
                        : 0;
                await prepareBrowserAudioContext(voiceAudioContextRef);
                startVoiceReplayAudio({
                    callId,
                    audioBuffer,
                    positionMs,
                });
            } catch (error) {
                setReplayingVoiceCallId(null);
                toast.error(getCaregiverSimErrorMessage(error), {
                    persistNotification: false,
                });
            }
        },
        [buildVoiceReplayAudioBuffer, startVoiceReplayAudio],
    );

    const replayVoiceTranscript = useCallback(
        async (call: VoiceSimCall | null) => {
            if (!call) {
                return;
            }
            const currentCall =
                Object.values(voiceSimCallsRef.current).find(
                    (voiceCall) => voiceCall.callId === call.callId,
                ) ?? call;
            const transcriptEntries =
                voiceTranscriptsRef.current[currentCall.callId] ?? [];
            if (transcriptEntries.length === 0) {
                return;
            }
            if (!isTerminalVoiceSimStatus(currentCall.status)) {
                toast.error('End the active voice call before replay.', {
                    persistNotification: false,
                });
                return;
            }
            if (
                hasActiveVoiceSimCall(
                    voiceSimCallsRef.current,
                    currentCall.callId,
                )
            ) {
                toast.error('End active voice calls before replay.', {
                    persistNotification: false,
                });
                return;
            }

            await replayVoiceEntries(currentCall.callId, transcriptEntries);
        },
        [replayVoiceEntries],
    );

    const replayRealVoiceCallTranscript = useCallback(
        async (voiceCall: VoiceCallRead | null) => {
            if (!voiceCall) {
                return;
            }
            if (!isTerminalPersistedVoiceCallStatus(voiceCall.call_status)) {
                toast.error('End the active voice call before replay.', {
                    persistNotification: false,
                });
                return;
            }

            const transcriptEntries =
                voiceCallTranscriptToReplayEntries(voiceCall);
            if (transcriptEntries.length === 0) {
                toast.error('No transcript is available for this call yet.', {
                    persistNotification: false,
                });
                return;
            }

            await replayVoiceEntries(voiceCall.id, transcriptEntries);
        },
        [replayVoiceEntries],
    );

    const startVoiceSimCall = useCallback(
        async ({
            targetPersonality = selectedPersonality,
            targetCounterparty,
            targetCaregiverDetails,
            direction = 'outbound',
        }: {
            readonly targetPersonality?: CaregiverSimPersonalityRead;
            readonly targetCounterparty?: string | null;
            readonly targetCaregiverDetails?: CaregiverSimCaregiverDetailsResponse;
            readonly direction?: VoiceSimDirection;
        } = {}) => {
            if (!targetPersonality) {
                return;
            }

            const counterpartyPhone = (
                targetCounterparty ?? scenarioCounterparty
            ).trim();
            if (!counterpartyPhone) {
                toast.error('No simulator phone number is available.', {
                    persistNotification: false,
                });
                return;
            }
            if (!scenarioDetails.trim()) {
                toast.error('Add scenario notes before running.', {
                    persistNotification: false,
                });
                return;
            }
            const resolvedCaregiverDetails =
                targetCaregiverDetails ??
                (targetPersonality.identifier ===
                selectedPersonality?.identifier
                    ? caregiverDetails
                    : undefined);
            const scenarioContext = buildScenarioContext(
                scenarioDetails,
                resolvedCaregiverDetails,
            );
            const callKey = scenarioRunKey(
                targetPersonality.identifier,
                counterpartyPhone,
            );
            if (startingVoiceSimKeysRef.current.has(callKey)) {
                return;
            }
            const existingVoiceCall = voiceSimCallsRef.current[callKey];
            if (
                existingVoiceCall &&
                !isTerminalVoiceSimStatus(existingVoiceCall.status)
            ) {
                toast.error('That voice call is already running.', {
                    persistNotification: false,
                });
                return;
            }
            if (existingVoiceCall?.status === 'error') {
                stopVoiceTransport(existingVoiceCall.callId, {
                    sendStop: false,
                });
                await endCaregiverSimVoiceSession(existingVoiceCall.callId, {
                    reason: 'caregiver_sim_voice_restart_cleanup',
                }).catch(() => undefined);
                setVoiceSimCallForKey(callKey, () => null);
            }

            setVoiceSimStarting(callKey, true);
            setSelectedIdentifier(targetPersonality.identifier);
            setSelectedCounterparty(counterpartyPhone);
            selectedVoiceCallKeyRef.current = callKey;
            resetLiveVoicePlayback();
            setPendingSimulation(null);
            setNewActivityIdentifiers((current) => {
                if (!current.has(targetPersonality.identifier)) {
                    return current;
                }

                const next = new Set(current);
                next.delete(targetPersonality.identifier);
                return next;
            });

            try {
                if (direction === 'outbound') {
                    const result = await startCaregiverSimRealVoiceCall({
                        personality_identifier: targetPersonality.identifier,
                        counterparty_phone: counterpartyPhone,
                        simulator_world: 'standard',
                        workflow_type: workflowType,
                        direction,
                        scenario_details: scenarioContext,
                        caregiver_audio_mode: caregiverVoiceAudioMode,
                        open_shift_discovery_fixture_count: 0,
                        seed_outreach_fixture: false,
                    });
                    setLogsRefreshToken((token) => token + 1);
                    toast.success(
                        result.created
                            ? 'Voice call queued.'
                            : 'Voice call is already active.',
                        { persistNotification: false },
                    );
                    return;
                }

                if (!syntheticVoiceHarnessEnabled()) {
                    toast.error(
                        'Inbound simulator call-in must use a real simulator-owned Twilio number.',
                        { persistNotification: false },
                    );
                    return;
                }

                const session = await startCaregiverSimVoiceSession({
                    personality_identifier: targetPersonality.identifier,
                    counterparty_phone: counterpartyPhone,
                    simulator_world: 'standard',
                    workflow_type: workflowType,
                    direction,
                    scenario_details: scenarioContext,
                    caregiver_audio_mode: caregiverVoiceAudioMode,
                    open_shift_discovery_fixture_count: 0,
                    seed_outreach_fixture: false,
                });
                await prepareBrowserAudioContext(voiceAudioContextRef);
                const call = buildVoiceSimCall({
                    session,
                    personalityIdentifier: targetPersonality.identifier,
                    counterpartyPhone,
                    workflowType,
                    caregiverAudioMode: caregiverVoiceAudioMode,
                    scenarioContext,
                });
                const socket = new WebSocket(session.websocket_url);
                const transport: VoiceSimTransport = {
                    callId: call.callId,
                    callKey,
                    socket,
                    silenceIntervalId: null,
                    timelineIntervalId: null,
                    timelinePolling: false,
                    seenAssistantTexts: new Set(),
                    transcriptKeys: new Set(),
                    pendingAssistantAudioPayloads: [],
                    turnsCompleted: 0,
                    ending: false,
                    cleanupRequested: false,
                };
                voiceTransportsRef.current.set(call.callId, transport);
                setVoiceSimCallForKey(callKey, () => call);
                setVoiceTranscripts((current) => ({
                    ...current,
                    [call.callId]: [],
                }));
                setVoiceToolCalls((current) => ({
                    ...current,
                    [call.callId]: [],
                }));
                const cleanupFailedTransport = (reason: string) => {
                    if (transport.cleanupRequested || transport.ending) {
                        return;
                    }
                    transport.cleanupRequested = true;
                    transport.ending = true;
                    stopVoiceTransport(call.callId, { sendStop: false });
                    void endCaregiverSimVoiceSession(session.call_id, {
                        reason,
                    }).catch(() => undefined);
                };
                socket.addEventListener('open', () => {
                    sendSyntheticTwilioStart({
                        websocket: socket,
                        streamSid: session.stream_sid,
                        callSid: session.call_sid,
                        accountSid: session.account_sid,
                    });
                    transport.silenceIntervalId = startSyntheticTwilioSilence({
                        websocket: socket,
                        streamSid: session.stream_sid,
                    });
                    updateVoiceSimCallStatus({
                        callKey,
                        callId: call.callId,
                        status: 'listening',
                        statusText: 'Listening',
                    });
                    startVoiceTimelinePolling(session.call_id);
                });
                socket.addEventListener('message', (event) => {
                    const payload = readVoiceSocketMessage(event.data);
                    if (payload?.event !== 'media' || !payload.media?.payload) {
                        return;
                    }
                    if (selectedVoiceCallKeyRef.current === callKey) {
                        queueLiveVoiceMediaPayload(
                            callKey,
                            payload.media.payload,
                        );
                    }
                    appendVoiceTranscriptAudioPayload({
                        callId: call.callId,
                        speaker: 'phoebe',
                        audioPayload: payload.media.payload,
                    });
                    updateVoiceSimCallStatus({
                        callKey,
                        callId: call.callId,
                        status: 'phoebe-speaking',
                        statusText: 'Phoebe speaking',
                    });
                });
                socket.addEventListener('error', () => {
                    updateVoiceSimCallStatus({
                        callKey,
                        callId: call.callId,
                        status: 'error',
                        statusText: 'Voice socket error',
                        errorMessage: 'Voice websocket connection failed.',
                    });
                    cleanupFailedTransport('caregiver_sim_voice_socket_error');
                });
                socket.addEventListener('close', () => {
                    if (transport.ending) {
                        clearLiveVoicePlaybackBuffer(callKey);
                    } else {
                        flushLiveVoicePlaybackBuffer(callKey);
                    }
                    if (transport.silenceIntervalId !== null) {
                        window.clearInterval(transport.silenceIntervalId);
                        transport.silenceIntervalId = null;
                    }
                    if (transport.timelineIntervalId !== null) {
                        window.clearInterval(transport.timelineIntervalId);
                        transport.timelineIntervalId = null;
                    }
                    voiceTransportsRef.current.delete(call.callId);
                    if (transport.ending) {
                        return;
                    }
                    cleanupFailedTransport('caregiver_sim_voice_socket_closed');
                    const currentCall = voiceSimCallsRef.current[callKey];
                    if (currentCall?.status === 'error') {
                        return;
                    }
                    updateVoiceSimCallStatus({
                        callKey,
                        callId: call.callId,
                        status: 'ended',
                        statusText: 'Call ended',
                    });
                });
            } catch (error) {
                const message = getCaregiverSimErrorMessage(error);
                toast.error(message, { persistNotification: false });
            } finally {
                setVoiceSimStarting(callKey, false);
            }
        },
        [
            appendVoiceTranscriptAudioPayload,
            caregiverDetails,
            clearLiveVoicePlaybackBuffer,
            flushLiveVoicePlaybackBuffer,
            queueLiveVoiceMediaPayload,
            scenarioCounterparty,
            scenarioDetails,
            selectedPersonality,
            setVoiceSimCallForKey,
            setVoiceSimStarting,
            startVoiceTimelinePolling,
            stopVoiceTransport,
            resetLiveVoicePlayback,
            updateVoiceSimCallStatus,
            caregiverVoiceAudioMode,
            workflowType,
        ],
    );

    useEffect(() => {
        return () => {
            stopAllVoiceTransports();
        };
    }, [stopAllVoiceTransports]);

    const selectPersonality = useCallback(
        (
            personality: CaregiverSimPersonalityRead,
            counterpartyPhone: string | null = null,
        ) => {
            setSelectedIdentifier(personality.identifier);
            setSelectedCounterparty(counterpartyPhone);
            setPendingSimulation(null);
            setNewActivityIdentifiers((current) => {
                if (!current.has(personality.identifier)) {
                    return current;
                }

                const next = new Set(current);
                next.delete(personality.identifier);
                return next;
            });
        },
        [],
    );

    const runScenarioLoop = useCallback(
        async ({
            targetPersonality,
            targetCounterparty,
            startWithOpening,
            pendingActionValue,
        }: {
            readonly targetPersonality: CaregiverSimPersonalityRead;
            readonly targetCounterparty?: string | null;
            readonly startWithOpening: boolean;
            readonly pendingActionValue: ScenarioAction;
        }) => {
            const counterpartyPhone = (
                startWithOpening
                    ? targetCounterparty || scenarioCounterparty
                    : targetCounterparty ||
                      activeCounterparty ||
                      scenarioCounterparty
            ).trim();
            if (!counterpartyPhone) {
                toast.error('No simulator phone number is available.', {
                    persistNotification: false,
                });
                return;
            }
            if (!scenarioDetails.trim()) {
                toast.error('Add scenario notes before running.', {
                    persistNotification: false,
                });
                return;
            }

            const runKey = scenarioRunKey(
                targetPersonality.identifier,
                counterpartyPhone,
            );
            if (
                pendingScenarioRunKeysRef.current.has(runKey) ||
                pendingScenarioRuns[runKey]
            ) {
                toast.error('That thread is already running.', {
                    persistNotification: false,
                });
                return;
            }
            const activeVoiceCall = voiceSimCallsRef.current[runKey];
            if (
                workflowChannel !== 'voice' &&
                (startingVoiceSimKeysRef.current.has(runKey) ||
                    (!!activeVoiceCall &&
                        !isTerminalVoiceSimStatus(activeVoiceCall.status)))
            ) {
                toast.error(
                    'End the active voice call before running a text scenario.',
                    {
                        persistNotification: false,
                    },
                );
                return;
            }

            pendingScenarioRunKeysRef.current.add(runKey);
            selectPersonality(targetPersonality, counterpartyPhone);
            setPendingScenarioRuns((current) => ({
                ...current,
                [runKey]: {
                    action: pendingActionValue,
                    counterpartyPhone,
                    workflowType,
                    workflowChannel,
                    personalityIdentifier: targetPersonality.identifier,
                    personalityName: targetPersonality.name,
                    startedAt: new Date().toISOString(),
                },
            }));
            try {
                const result = await runCaregiverSimScenario({
                    personality_identifier: targetPersonality.identifier,
                    counterparty_phone: counterpartyPhone,
                    workflow_type: workflowType,
                    workflow_channel: workflowChannel,
                    scenario_details: buildScenarioContext(
                        scenarioDetails,
                        caregiverDetails,
                    ),
                    model_provider: caregiverModelOption.provider,
                    model: caregiverModelOption.model,
                    start_with_opening: startWithOpening,
                    max_turns: 20,
                });
                await refreshAll();
                toast.success(
                    result.turn_count > 0
                        ? `Scenario ran ${result.turn_count} turns.`
                        : 'No next scenario turn.',
                    {
                        description: result.stop_reason ?? undefined,
                        persistNotification: false,
                    },
                );
            } catch (error) {
                const message =
                    error instanceof Error
                        ? error.message
                        : 'Caregiver simulator action failed.';
                toast.error(message, { persistNotification: false });
            } finally {
                pendingScenarioRunKeysRef.current.delete(runKey);
                setPendingScenarioRuns((current) => {
                    const next = { ...current };
                    delete next[runKey];
                    return next;
                });
            }
        },
        [
            caregiverDetails,
            caregiverModelOption,
            activeCounterparty,
            pendingScenarioRuns,
            refreshAll,
            scenarioCounterparty,
            scenarioDetails,
            selectPersonality,
            workflowChannel,
            workflowType,
        ],
    );

    const runAutoScenario = useCallback(
        async ({
            targetPersonality = selectedPersonality,
            targetCounterparty = null,
            targetCaregiverDetails,
            voiceDirection = 'outbound',
        }: {
            readonly targetPersonality?: CaregiverSimPersonalityRead;
            readonly targetCounterparty?: string | null;
            readonly targetCaregiverDetails?: CaregiverSimCaregiverDetailsResponse;
            readonly voiceDirection?: VoiceSimDirection;
        } = {}) => {
            if (!targetPersonality) {
                return;
            }

            if (workflowChannel === 'voice') {
                await startVoiceSimCall({
                    targetPersonality,
                    targetCounterparty,
                    targetCaregiverDetails,
                    direction: voiceDirection,
                });
                return;
            }

            await runScenarioLoop({
                targetPersonality,
                targetCounterparty,
                startWithOpening: true,
                pendingActionValue: 'scenario',
            });
        },
        [
            runScenarioLoop,
            selectedPersonality,
            startVoiceSimCall,
            workflowChannel,
        ],
    );

    const continueScenario = useCallback(
        async ({
            targetPersonality = selectedPersonality,
            targetCounterparty = activeCounterparty,
        }: {
            readonly targetPersonality?: CaregiverSimPersonalityRead;
            readonly targetCounterparty?: string | null;
        } = {}) => {
            if (!targetPersonality) {
                return;
            }

            await runScenarioLoop({
                targetPersonality,
                targetCounterparty,
                startWithOpening: false,
                pendingActionValue: 'continue-scenario',
            });
        },
        [activeCounterparty, runScenarioLoop, selectedPersonality],
    );

    const sendIntervention = useCallback(async () => {
        if (!selectedPersonality) {
            return;
        }

        const counterpartyPhone = activeCounterparty?.trim();
        const messageBody = interventionBody.trim();
        if (!counterpartyPhone) {
            toast.error('Select a thread or run a scenario first.', {
                persistNotification: false,
            });
            return;
        }
        if (!messageBody) {
            toast.error('Enter a message.', { persistNotification: false });
            return;
        }

        setPendingAction('intervention');
        try {
            await sendSimulationTurn({
                counterpartyPhone,
                messageBody,
            });
            setInterventionBody('');
            toast.success('Message sent.', { persistNotification: false });
        } catch (error) {
            const message =
                error instanceof Error
                    ? error.message
                    : 'Caregiver simulator action failed.';
            toast.error(message, { persistNotification: false });
        } finally {
            setPendingSimulation(null);
            setPendingAction(null);
        }
    }, [
        interventionBody,
        activeCounterparty,
        selectedPersonality,
        sendSimulationTurn,
    ]);

    const saveCaregiverDetails = useCallback(
        async (body: CaregiverSimCaregiverDetailsUpdateRequest) => {
            if (!selectedPersonality) {
                return;
            }

            await updateCaregiverSimCaregiverDetails(
                selectedPersonality.identifier,
                body,
            );
            await caregiverDetailsQuery.mutate();
            toast.success('Caregiver details saved.', {
                persistNotification: false,
            });
        },
        [caregiverDetailsQuery, selectedPersonality],
    );

    const reflectCaregiverMemory = useCallback(async () => {
        if (!selectedPersonality) {
            return;
        }

        const result = await reflectCaregiverSimMemory(
            selectedPersonality.identifier,
            {
                counterparty_phone: activeCounterparty,
            },
        );
        await memoryEventsQuery.mutate();
        toast.success(
            result.reflection
                ? 'Reflection added.'
                : 'No simulator memory to reflect.',
            { persistNotification: false },
        );
    }, [activeCounterparty, memoryEventsQuery, selectedPersonality]);

    const runAction = useCallback(
        async (action: ControlAction) => {
            if (action !== 'reset-all-personalities' && !selectedPersonality) {
                return;
            }
            if (
                action === 'reset-all-personalities' &&
                personalities.length === 0
            ) {
                return;
            }

            setPendingAction(action);
            try {
                if (action === 'reseed') {
                    const result = await reseedCaregiverSimCaregivers({});
                    setLastReseed(result as CaregiverSimReseedResponse);
                    toast.success('Simulator caregivers seeded.', {
                        persistNotification: false,
                    });
                } else {
                    const resetVoiceCall = (call: VoiceSimCall) => {
                        if (action === 'reset-all-personalities') {
                            return true;
                        }
                        if (!selectedPersonality) {
                            return false;
                        }
                        if (
                            call.personalityIdentifier !==
                            selectedPersonality.identifier
                        ) {
                            return false;
                        }
                        return (
                            action !== 'reset-thread' ||
                            call.counterpartyPhone === activeCounterparty
                        );
                    };
                    await endActiveVoiceSimCalls(resetVoiceCall);
                    if (action === 'reset-all-personalities') {
                        await Promise.all(
                            personalities.map((personality) =>
                                resetCaregiverSimPersonality(
                                    personality.identifier,
                                    { counterparty_phone: null },
                                ),
                            ),
                        );
                        toast.success('All simulator personalities reset.', {
                            persistNotification: false,
                        });
                    } else if (selectedPersonality) {
                        await resetCaregiverSimPersonality(
                            selectedPersonality.identifier,
                            {
                                counterparty_phone:
                                    action === 'reset-thread'
                                        ? activeCounterparty
                                        : null,
                            },
                        );
                        toast.success(
                            action === 'reset-thread'
                                ? 'Simulator thread reset.'
                                : 'Simulator personality reset.',
                            { persistNotification: false },
                        );
                    }
                    removeVoiceSimCalls(resetVoiceCall);
                }
                await refreshAll();
            } catch (error) {
                const message =
                    error instanceof Error
                        ? error.message
                        : 'Caregiver simulator action failed.';
                toast.error(message, { persistNotification: false });
            } finally {
                setPendingAction(null);
            }
        },
        [
            activeCounterparty,
            endActiveVoiceSimCalls,
            personalities,
            refreshAll,
            removeVoiceSimCalls,
            selectedPersonality,
        ],
    );

    return (
        <div className="flex min-h-screen flex-col p-8">
            <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h1 className="text-foreground text-2xl font-semibold">
                        Caregiver Sim
                    </h1>
                    <div className="text-muted-foreground mt-1 flex flex-wrap items-center gap-2 text-sm">
                        <span className="font-mono">
                            {stateQuery.data?.phoebe_home_care_number ??
                                'Phoebe number unset'}
                        </span>
                        <Badge variant="outline">
                            {personalities.length} personas
                        </Badge>
                    </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <Button
                        variant="outline"
                        onClick={() =>
                            void runAction('reset-all-personalities')
                        }
                        disabled={
                            !resetSupported ||
                            personalities.length === 0 ||
                            pendingAction !== null ||
                            hasAnyScenarioRun
                        }
                    >
                        {pendingAction === 'reset-all-personalities' ? (
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        ) : (
                            <RotateCcw className="mr-2 h-4 w-4" />
                        )}
                        Reset all
                    </Button>
                    <Button
                        variant="outline"
                        onClick={() => void refreshAll()}
                        disabled={
                            stateQuery.isValidating || logsQuery.isValidating
                        }
                    >
                        <RefreshCw
                            className={cn(
                                'mr-2 h-4 w-4',
                                (stateQuery.isValidating ||
                                    logsQuery.isValidating) &&
                                    'animate-spin',
                            )}
                        />
                        Refresh
                    </Button>
                </div>
            </div>
            {stateErrorMessage ? (
                <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/50 dark:text-red-300">
                    {stateErrorMessage}
                </div>
            ) : null}
            <div className="grid min-h-0 gap-4 xl:grid-cols-[minmax(0,1fr)_320px]">
                <div className="min-w-0">
                    {stateQuery.isLoading ? (
                        <ConversationGridSkeleton />
                    ) : personalities.length === 0 ? (
                        <Card className="min-h-[320px]">
                            <CardContent className="text-muted-foreground flex min-h-[320px] items-center justify-center text-sm">
                                No simulator personalities.
                            </CardContent>
                        </Card>
                    ) : (
                        <div className="grid min-w-0 grid-cols-1 gap-3 md:grid-cols-2 2xl:grid-cols-3">
                            {personalities.map((personality) => {
                                const selected =
                                    personality.identifier ===
                                    selectedPersonality?.identifier;

                                return (
                                    <PersonalityConversationCard
                                        key={personality.identifier}
                                        personality={personality}
                                        selected={selected}
                                        selectedCounterparty={
                                            selected ? activeCounterparty : null
                                        }
                                        defaultCounterparty={
                                            stateQuery.data
                                                ?.phoebe_home_care_number ??
                                            null
                                        }
                                        pendingScenarioRuns={
                                            pendingScenarioRuns
                                        }
                                        pendingSimulation={
                                            selected
                                                ? visiblePendingSimulation
                                                : null
                                        }
                                        scenarioCounterparty={
                                            scenarioCounterparty
                                        }
                                        scenarioDetails={scenarioDetails}
                                        workflowChannel={workflowChannel}
                                        pendingAction={pendingAction}
                                        startingVoiceSimKeys={
                                            startingVoiceSimKeys
                                        }
                                        voiceSimCalls={voiceSimCalls}
                                        voiceTranscripts={voiceTranscripts}
                                        voiceToolCalls={voiceToolCalls}
                                        replayingVoiceCallId={
                                            replayingVoiceCallId
                                        }
                                        voiceReplayState={voiceReplayState}
                                        hasNewActivity={newActivityIdentifiers.has(
                                            personality.identifier,
                                        )}
                                        logsRefreshToken={logsRefreshToken}
                                        interventionBody={interventionBody}
                                        isInterventionSending={
                                            pendingAction === 'intervention'
                                        }
                                        onInterventionBodyChange={
                                            setInterventionBody
                                        }
                                        onSendIntervention={() =>
                                            void sendIntervention()
                                        }
                                        onReplayVoiceTranscript={(call) =>
                                            void replayVoiceTranscript(call)
                                        }
                                        onReplayRealVoiceCallTranscript={(
                                            voiceCall,
                                        ) =>
                                            void replayRealVoiceCallTranscript(
                                                voiceCall,
                                            )
                                        }
                                        onVoiceReplaySeek={(
                                            callId,
                                            positionMs,
                                        ) =>
                                            seekVoiceReplay(callId, positionMs)
                                        }
                                        onSelect={() => {
                                            if (selected) {
                                                return;
                                            }
                                            selectPersonality(personality);
                                        }}
                                        onExpand={(counterparty) => {
                                            selectPersonality(
                                                personality,
                                                counterparty,
                                            );
                                            setExpandedPersonalityIdentifier(
                                                personality.identifier,
                                            );
                                        }}
                                        onCounterpartyChange={(
                                            counterparty,
                                        ) => {
                                            if (!selected) {
                                                selectPersonality(
                                                    personality,
                                                    counterparty,
                                                );
                                                return;
                                            }

                                            setSelectedCounterparty(
                                                counterparty,
                                            );
                                            setPendingSimulation(null);
                                            setNewActivityIdentifiers(
                                                (current) => {
                                                    if (
                                                        !current.has(
                                                            personality.identifier,
                                                        )
                                                    ) {
                                                        return current;
                                                    }

                                                    const next = new Set(
                                                        current,
                                                    );
                                                    next.delete(
                                                        personality.identifier,
                                                    );
                                                    return next;
                                                },
                                            );
                                        }}
                                        onRun={(
                                            counterparty,
                                            targetCaregiverDetails,
                                            voiceDirection,
                                        ) =>
                                            void runAutoScenario({
                                                targetPersonality: personality,
                                                targetCounterparty:
                                                    counterparty,
                                                targetCaregiverDetails,
                                                voiceDirection,
                                            })
                                        }
                                        onContinue={(counterparty) =>
                                            void continueScenario({
                                                targetPersonality: personality,
                                                targetCounterparty:
                                                    counterparty,
                                            })
                                        }
                                        now={now}
                                    />
                                );
                            })}
                        </div>
                    )}
                </div>
                <Card className="min-h-[820px]">
                    <CardHeader>
                        <CardTitle>Controls</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="border-border space-y-3 border-b pb-4">
                            <div className="text-sm font-medium">
                                Scenario setup
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                                <div className="space-y-1">
                                    <div className="text-muted-foreground text-xs font-medium">
                                        Workflow
                                    </div>
                                    <Select
                                        value={workflowType}
                                        onValueChange={(value) => {
                                            const nextWorkflowType =
                                                value as WorkflowType;
                                            setWorkflowType(nextWorkflowType);
                                            applyWorkflowTemplate(
                                                nextWorkflowType,
                                            );
                                        }}
                                        disabled={pendingAction !== null}
                                    >
                                        <SelectTrigger>
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {WORKFLOW_OPTIONS.map((option) => (
                                                <SelectItem
                                                    key={option.value}
                                                    value={option.value}
                                                >
                                                    {option.label}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-1">
                                    <div className="text-muted-foreground text-xs font-medium">
                                        Channel
                                    </div>
                                    <Select
                                        value={workflowChannel}
                                        onValueChange={(value) =>
                                            setWorkflowChannel(
                                                value as WorkflowChannel,
                                            )
                                        }
                                        disabled={pendingAction !== null}
                                    >
                                        <SelectTrigger data-testid="caregiver-sim-channel-select">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {WORKFLOW_CHANNEL_OPTIONS.map(
                                                (option) => (
                                                    <SelectItem
                                                        key={option.value}
                                                        value={option.value}
                                                    >
                                                        {option.label}
                                                    </SelectItem>
                                                ),
                                            )}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                            {workflowChannel === 'voice' ? (
                                <div className="border-border flex items-center justify-between gap-3 rounded-md border px-3 py-2">
                                    <div className="min-w-0">
                                        <div className="text-muted-foreground text-xs font-medium">
                                            Caregiver audio
                                        </div>
                                        <div className="text-foreground text-sm font-medium">
                                            {caregiverVoiceAudioMode === 'tts'
                                                ? 'TTS'
                                                : 'Voice agent'}
                                        </div>
                                    </div>
                                    <Switch
                                        checked={
                                            caregiverVoiceAudioMode ===
                                            'voice_agent'
                                        }
                                        onCheckedChange={(checked) =>
                                            setCaregiverVoiceAudioMode(
                                                checked ? 'voice_agent' : 'tts',
                                            )
                                        }
                                        disabled={pendingAction !== null}
                                        aria-label="Toggle caregiver voice agent audio"
                                    />
                                </div>
                            ) : null}
                            <div className="space-y-1">
                                <div className="text-muted-foreground text-xs font-medium">
                                    Scenario notes
                                </div>
                                <Textarea
                                    className="min-h-36 resize-none text-sm"
                                    value={scenarioDetails}
                                    onChange={(event) =>
                                        setScenarioDetails(event.target.value)
                                    }
                                    placeholder="Shift requirements, client details, constraints…"
                                />
                            </div>
                            <div className="space-y-1">
                                <div className="text-muted-foreground text-xs font-medium">
                                    Caregiver model
                                </div>
                                <ModelSelect
                                    value={caregiverModelValue}
                                    selectedOption={caregiverModelOption}
                                    onChange={setCaregiverModelValue}
                                    disabled={pendingAction !== null}
                                />
                            </div>
                            <Button
                                className="w-full justify-start"
                                disabled={
                                    !selectedPersonality ||
                                    !scenarioDetails.trim() ||
                                    pendingAction !== null ||
                                    !!runScenarioTargetRun ||
                                    runVoiceTargetIsBusy
                                }
                                onClick={() => void runAutoScenario()}
                            >
                                {runScenarioTargetRun?.action === 'scenario' ||
                                runVoiceTargetIsStarting ? (
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                ) : workflowChannel === 'voice' ? (
                                    <PhoneCall className="mr-2 h-4 w-4" />
                                ) : (
                                    <Play className="mr-2 h-4 w-4" />
                                )}
                                {workflowChannel === 'voice'
                                    ? 'Start voice call'
                                    : 'Run scenario'}
                            </Button>
                            <Button
                                className="w-full justify-start"
                                variant="outline"
                                disabled={
                                    !selectedPersonality ||
                                    !scenarioDetails.trim() ||
                                    pendingAction !== null ||
                                    !!continueScenarioTargetRun ||
                                    continueVoiceTargetIsBusy ||
                                    workflowChannel === 'voice' ||
                                    !(
                                        activeCounterparty?.trim() ||
                                        scenarioCounterparty.trim()
                                    )
                                }
                                onClick={() => void continueScenario()}
                            >
                                {continueScenarioTargetRun?.action ===
                                'continue-scenario' ? (
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                ) : (
                                    <Send className="mr-2 h-4 w-4" />
                                )}
                                Continue scenario
                            </Button>
                        </div>
                        <VoiceSimCallCard
                            call={visibleVoiceSimCall}
                            transcript={visibleVoiceTranscript}
                            showEmpty={workflowChannel === 'voice'}
                            isStarting={visibleVoiceCallIsStarting}
                            isReplaying={
                                visibleVoiceSimCall !== null &&
                                replayingVoiceCallId ===
                                    visibleVoiceSimCall.callId
                            }
                            replayState={visibleReplayState}
                            replayDisabled={visibleReplayDisabled}
                            onEnd={() =>
                                void endVoiceSimCall(visibleVoiceSimCall)
                            }
                            onReplay={() =>
                                void replayVoiceTranscript(visibleVoiceSimCall)
                            }
                            onReplaySeek={(positionMs) => {
                                if (visibleVoiceSimCall) {
                                    seekVoiceReplay(
                                        visibleVoiceSimCall.callId,
                                        positionMs,
                                    );
                                }
                            }}
                        />
                        <Button
                            className="w-full justify-start"
                            variant="outline"
                            disabled={
                                !selectedPersonality ||
                                !resetSupported ||
                                pendingAction !== null ||
                                !!visiblePendingScenarioRun
                            }
                            onClick={() => void runAction('reset-personality')}
                        >
                            <RotateCcw className="mr-2 h-4 w-4" />
                            Reset personality
                        </Button>
                        <Button
                            className="w-full justify-start"
                            variant="outline"
                            disabled={
                                !selectedPersonality ||
                                !activeCounterparty ||
                                !resetSupported ||
                                pendingAction !== null ||
                                !!visiblePendingScenarioRun
                            }
                            onClick={() => void runAction('reset-thread')}
                        >
                            <Trash2 className="mr-2 h-4 w-4" />
                            Reset thread
                        </Button>
                        <Button
                            className="w-full justify-start"
                            disabled={
                                pendingAction !== null || hasAnyScenarioRun
                            }
                            onClick={() => void runAction('reseed')}
                        >
                            {pendingAction === 'reseed' ? (
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            ) : (
                                <Send className="mr-2 h-4 w-4" />
                            )}
                            Re-seed caregivers
                        </Button>
                        {!resetSupported ? (
                            <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-900 dark:bg-amber-950/50 dark:text-amber-300">
                                Reset is disabled because this simulator
                                instance does not expose reset endpoints.
                                Restart the local caregiver-sim worker from this
                                branch.
                            </div>
                        ) : null}
                        <div className="border-border rounded-md border p-3 text-sm">
                            <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2">
                                <dt className="text-muted-foreground">
                                    Selected
                                </dt>
                                <dd className="truncate font-medium">
                                    {selectedPersonality?.name ?? 'None'}
                                </dd>
                                <dt className="text-muted-foreground">Phone</dt>
                                <dd className="truncate font-mono">
                                    {selectedPersonality?.phone_number ?? '-'}
                                </dd>
                                <dt className="text-muted-foreground">
                                    Thread
                                </dt>
                                <dd className="truncate font-mono">
                                    {activeCounterparty ?? '-'}
                                </dd>
                                <dt className="text-muted-foreground">
                                    Workflow
                                </dt>
                                <dd className="truncate">
                                    {workflowLabel(workflowType)}
                                </dd>
                                <dt className="text-muted-foreground">
                                    Channel
                                </dt>
                                <dd className="flex items-center gap-1">
                                    {workflowChannel === 'voice' ? (
                                        <Phone className="h-3.5 w-3.5" />
                                    ) : (
                                        <MessageSquare className="h-3.5 w-3.5" />
                                    )}
                                    {workflowChannel.toUpperCase()}
                                </dd>
                                <dt className="text-muted-foreground">
                                    Messages
                                </dt>
                                <dd>
                                    {selectedPersonality?.message_count ?? 0}
                                </dd>
                                <dt className="text-muted-foreground">
                                    Threads
                                </dt>
                                <dd>
                                    {selectedPersonality?.thread_count ?? 0}
                                </dd>
                                <dt className="text-muted-foreground">Model</dt>
                                <dd className="truncate">
                                    {caregiverModelOption.label}
                                </dd>
                            </dl>
                            <Button
                                className="mt-3 w-full justify-start"
                                variant="outline"
                                size="sm"
                                disabled={!selectedPersonality}
                                onClick={() => setDetailsOpen(true)}
                            >
                                <FileText className="mr-2 h-4 w-4" />
                                Caregiver details
                            </Button>
                        </div>
                        <MemoryStatsCard
                            selectedPersonality={selectedPersonality}
                            selectedCounterparty={activeCounterparty}
                            logs={logs}
                            threads={threads}
                            now={now}
                        />
                        <ScenarioRunsCard
                            scenarioRuns={logs?.scenario_runs ?? []}
                            now={now}
                        />
                        {lastReseed ? (
                            <div className="border-border rounded-md border p-3 text-sm">
                                <div className="mb-2 font-medium">
                                    Last seed
                                </div>
                                <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2">
                                    <dt className="text-muted-foreground">
                                        Organization
                                    </dt>
                                    <dd className="truncate">
                                        {lastReseed.organization_name}
                                    </dd>
                                    <dt className="text-muted-foreground">
                                        Caregivers
                                    </dt>
                                    <dd>
                                        {lastReseed.created_caregivers} new,{' '}
                                        {lastReseed.updated_caregivers} updated
                                    </dd>
                                    <dt className="text-muted-foreground">
                                        Shifts
                                    </dt>
                                    <dd>
                                        {lastReseed.created_shifts} new,{' '}
                                        {lastReseed.updated_shifts} updated
                                    </dd>
                                    <dt className="text-muted-foreground">
                                        Clients
                                    </dt>
                                    <dd>
                                        {lastReseed.created_clients} new,{' '}
                                        {lastReseed.updated_clients} updated
                                    </dd>
                                    <dt className="text-muted-foreground">
                                        Availability
                                    </dt>
                                    <dd>
                                        {lastReseed.created_availability_slots}{' '}
                                        one-off new,{' '}
                                        {lastReseed.updated_availability_slots}{' '}
                                        updated;{' '}
                                        {
                                            lastReseed.created_weekly_availability_slots
                                        }{' '}
                                        weekly new,{' '}
                                        {
                                            lastReseed.updated_weekly_availability_slots
                                        }{' '}
                                        updated
                                    </dd>
                                    <dt className="text-muted-foreground">
                                        Preferences
                                    </dt>
                                    <dd>
                                        {lastReseed.created_client_preferences}{' '}
                                        new,{' '}
                                        {lastReseed.updated_client_preferences}{' '}
                                        updated
                                    </dd>
                                    <dt className="text-muted-foreground">
                                        Memory
                                    </dt>
                                    <dd>
                                        {lastReseed.created_caregiver_memories}{' '}
                                        summaries new,{' '}
                                        {lastReseed.updated_caregiver_memories}{' '}
                                        updated;{' '}
                                        {lastReseed.created_memory_fragments}{' '}
                                        fragments new,{' '}
                                        {lastReseed.updated_memory_fragments}{' '}
                                        updated
                                    </dd>
                                </dl>
                            </div>
                        ) : null}
                    </CardContent>
                </Card>
            </div>
            <CaregiverDetailsDialog
                open={detailsOpen}
                onOpenChange={setDetailsOpen}
                details={caregiverDetails}
                isLoading={caregiverDetailsQuery.isLoading}
                error={caregiverDetailsQuery.error}
                memoryEvents={memoryEvents}
                isMemoryLoading={memoryEventsQuery.isLoading}
                memoryError={memoryEventsQuery.error}
                now={now}
                onSave={saveCaregiverDetails}
                onReflect={reflectCaregiverMemory}
            />
            <Dialog
                open={!!expandedPersonality}
                onOpenChange={(open) => {
                    if (!open) {
                        setExpandedPersonalityIdentifier(null);
                    }
                }}
            >
                {expandedPersonality ? (
                    <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-6xl">
                        <DialogHeader>
                            <DialogTitle>
                                {expandedPersonality.name}
                            </DialogTitle>
                            <DialogDescription className="font-mono">
                                {expandedPersonality.phone_number}
                            </DialogDescription>
                        </DialogHeader>
                        <PersonalityConversationCard
                            personality={expandedPersonality}
                            selected={true}
                            selectedCounterparty={
                                expandedPersonality.identifier ===
                                selectedPersonality?.identifier
                                    ? activeCounterparty
                                    : null
                            }
                            defaultCounterparty={
                                stateQuery.data?.phoebe_home_care_number ?? null
                            }
                            pendingScenarioRuns={pendingScenarioRuns}
                            pendingSimulation={
                                expandedPersonality.identifier ===
                                selectedPersonality?.identifier
                                    ? visiblePendingSimulation
                                    : null
                            }
                            scenarioCounterparty={scenarioCounterparty}
                            scenarioDetails={scenarioDetails}
                            workflowChannel={workflowChannel}
                            pendingAction={pendingAction}
                            startingVoiceSimKeys={startingVoiceSimKeys}
                            voiceSimCalls={voiceSimCalls}
                            voiceTranscripts={voiceTranscripts}
                            voiceToolCalls={voiceToolCalls}
                            replayingVoiceCallId={replayingVoiceCallId}
                            voiceReplayState={voiceReplayState}
                            hasNewActivity={newActivityIdentifiers.has(
                                expandedPersonality.identifier,
                            )}
                            logsRefreshToken={logsRefreshToken}
                            interventionBody={interventionBody}
                            isInterventionSending={
                                pendingAction === 'intervention'
                            }
                            onInterventionBodyChange={setInterventionBody}
                            onSendIntervention={() => void sendIntervention()}
                            onReplayVoiceTranscript={(call) =>
                                void replayVoiceTranscript(call)
                            }
                            onReplayRealVoiceCallTranscript={(voiceCall) =>
                                void replayRealVoiceCallTranscript(voiceCall)
                            }
                            onVoiceReplaySeek={(callId, positionMs) =>
                                seekVoiceReplay(callId, positionMs)
                            }
                            onSelect={() => undefined}
                            onExpand={() => undefined}
                            onCounterpartyChange={(counterparty) => {
                                selectPersonality(
                                    expandedPersonality,
                                    counterparty,
                                );
                                setPendingSimulation(null);
                                setNewActivityIdentifiers((current) => {
                                    if (
                                        !current.has(
                                            expandedPersonality.identifier,
                                        )
                                    ) {
                                        return current;
                                    }

                                    const next = new Set(current);
                                    next.delete(expandedPersonality.identifier);
                                    return next;
                                });
                            }}
                            onRun={(
                                counterparty,
                                targetCaregiverDetails,
                                voiceDirection,
                            ) =>
                                void runAutoScenario({
                                    targetPersonality: expandedPersonality,
                                    targetCounterparty: counterparty,
                                    targetCaregiverDetails,
                                    voiceDirection,
                                })
                            }
                            onContinue={(counterparty) =>
                                void continueScenario({
                                    targetPersonality: expandedPersonality,
                                    targetCounterparty: counterparty,
                                })
                            }
                            now={now}
                            showExpandButton={false}
                            modalView={true}
                        />
                    </DialogContent>
                ) : null}
            </Dialog>
        </div>
    );
}

function CaregiverDetailsDialog({
    open,
    onOpenChange,
    details,
    isLoading,
    error,
    memoryEvents,
    isMemoryLoading,
    memoryError,
    now,
    onSave,
    onReflect,
}: {
    readonly open: boolean;
    readonly onOpenChange: (open: boolean) => void;
    readonly details: CaregiverSimCaregiverDetailsResponse | undefined;
    readonly isLoading: boolean;
    readonly error: unknown;
    readonly memoryEvents: CaregiverSimMemoryEventsResponse | undefined;
    readonly isMemoryLoading: boolean;
    readonly memoryError: unknown;
    readonly now: number;
    readonly onSave: (
        body: CaregiverSimCaregiverDetailsUpdateRequest,
    ) => Promise<void>;
    readonly onReflect: () => Promise<void>;
}) {
    const [memoryDraft, setMemoryDraft] = useState('');
    const [fragmentDraft, setFragmentDraft] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [isReflecting, setIsReflecting] = useState(false);

    useEffect(() => {
        if (!open || !details) {
            return;
        }

        setMemoryDraft(details.memory.compiled_markdown);
        setFragmentDraft(
            details.memory.fragments
                .map((fragment) => fragment.content)
                .join('\n'),
        );
    }, [details, open]);

    const initialFragmentDraft = useMemo(
        () =>
            details?.memory.fragments
                .map((fragment) => fragment.content)
                .join('\n') ?? '',
        [details?.memory.fragments],
    );
    const hasChanges =
        !!details &&
        (memoryDraft !== details.memory.compiled_markdown ||
            fragmentDraft !== initialFragmentDraft);

    const save = useCallback(async () => {
        if (!details || !hasChanges) {
            return;
        }

        setIsSaving(true);
        try {
            await onSave({
                compiled_memory: memoryDraft,
                context_fragments: fragmentDraft
                    .split('\n')
                    .map((fragment) => fragment.trim())
                    .filter(Boolean),
            });
        } finally {
            setIsSaving(false);
        }
    }, [details, fragmentDraft, hasChanges, memoryDraft, onSave]);

    const reflect = useCallback(async () => {
        setIsReflecting(true);
        try {
            await onReflect();
        } finally {
            setIsReflecting(false);
        }
    }, [onReflect]);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-h-[90vh] overflow-hidden sm:max-w-5xl">
                <DialogHeader>
                    <DialogTitle>
                        {details?.profile.name ?? 'Caregiver details'}
                    </DialogTitle>
                    <DialogDescription>
                        {details
                            ? `${details.profile.phone_number} · ${details.organization_name}`
                            : 'Local simulator caregiver data'}
                    </DialogDescription>
                </DialogHeader>
                {isLoading ? (
                    <div className="grid gap-4 lg:grid-cols-2">
                        <DetailsSkeleton />
                        <DetailsSkeleton />
                    </div>
                ) : error ? (
                    <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/50 dark:text-red-300">
                        {getCaregiverSimErrorMessage(error)}
                    </div>
                ) : details ? (
                    <div className="grid min-h-0 gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(360px,0.85fr)]">
                        <ScrollArea className="max-h-[64vh] pr-3">
                            <div className="space-y-4">
                                <DetailSection title="Profile">
                                    <DetailGrid
                                        rows={[
                                            [
                                                'Address',
                                                details.profile.address,
                                            ],
                                            [
                                                'Roles',
                                                details.profile.roles
                                                    .map(enumLabel)
                                                    .join(', ') || '-',
                                            ],
                                            [
                                                'Skills',
                                                details.profile.skills.join(
                                                    ', ',
                                                ) || '-',
                                            ],
                                            [
                                                'Tags',
                                                details.profile.tags.join(
                                                    ', ',
                                                ) || '-',
                                            ],
                                        ]}
                                    />
                                </DetailSection>
                                <DetailSection title="Weekly availability">
                                    <StackedRows
                                        rows={details.weekly_availability.map(
                                            (slot) => ({
                                                key: `${slot.start_minute_of_week}:${slot.end_minute_of_week}`,
                                                title: slot.label,
                                                meta: enumLabel(slot.status),
                                            }),
                                        )}
                                        emptyText="No weekly availability."
                                    />
                                </DetailSection>
                                <DetailSection title="One-off availability">
                                    <StackedRows
                                        rows={details.availability.map(
                                            (slot) => ({
                                                key: slot.id,
                                                title: `${formatDateTime(slot.start_time)} - ${formatDateTime(slot.end_time)}`,
                                                meta: enumLabel(slot.status),
                                            }),
                                        )}
                                        emptyText="No one-off availability."
                                    />
                                </DetailSection>
                                <DetailSection title="Client preferences">
                                    <StackedRows
                                        rows={details.client_preferences.map(
                                            (preference) => ({
                                                key: preference.id,
                                                title: preference.client_name,
                                                meta: `${enumLabel(preference.preference_value)} · ${preference.client_address}`,
                                            }),
                                        )}
                                        emptyText="No client preferences."
                                    />
                                </DetailSection>
                                <DetailSection title="Seeded shifts">
                                    <StackedRows
                                        rows={details.shifts.map((shift) => ({
                                            key: shift.id,
                                            title:
                                                shift.patient_name ??
                                                'Unnamed shift',
                                            meta: [
                                                enumLabel(shift.status),
                                                shift.shift_start_time
                                                    ? formatDateTime(
                                                          shift.shift_start_time,
                                                      )
                                                    : null,
                                                shift.patient_address,
                                            ]
                                                .filter(Boolean)
                                                .join(' · '),
                                        }))}
                                        emptyText="No seeded shifts."
                                    />
                                </DetailSection>
                            </div>
                        </ScrollArea>
                        <div className="space-y-4">
                            <DetailSection title="Memory summary">
                                <Textarea
                                    className="min-h-64 resize-none font-mono text-xs"
                                    value={memoryDraft}
                                    onChange={(event) =>
                                        setMemoryDraft(event.target.value)
                                    }
                                />
                                <div className="text-muted-foreground mt-2 text-xs">
                                    Compiled{' '}
                                    {details.memory.compiled_at
                                        ? formatTimestamp(
                                              details.memory.compiled_at,
                                              now,
                                          )
                                        : 'never'}
                                </div>
                            </DetailSection>
                            <DetailSection title="Context fragments">
                                <Textarea
                                    className="min-h-40 resize-none font-mono text-xs"
                                    value={fragmentDraft}
                                    onChange={(event) =>
                                        setFragmentDraft(event.target.value)
                                    }
                                />
                            </DetailSection>
                            <DetailSection title="Simulator reflections">
                                <div className="mb-3 flex justify-end">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        disabled={isReflecting}
                                        onClick={() => void reflect()}
                                    >
                                        {isReflecting ? (
                                            <Loader2 className="h-4 w-4 animate-spin" />
                                        ) : (
                                            <RefreshCw className="h-4 w-4" />
                                        )}
                                        Reflect now
                                    </Button>
                                </div>
                                <MemoryEventRows
                                    rows={memoryEvents?.reflections ?? []}
                                    isLoading={isMemoryLoading}
                                    error={memoryError}
                                    emptyText="No reflections yet."
                                    now={now}
                                />
                            </DetailSection>
                            <DetailSection title="Simulator memory">
                                <MemoryEventRows
                                    rows={memoryEvents?.memory_events ?? []}
                                    isLoading={isMemoryLoading}
                                    error={memoryError}
                                    emptyText="No simulator memory yet."
                                    now={now}
                                />
                            </DetailSection>
                        </div>
                    </div>
                ) : (
                    <div className="border-border text-muted-foreground rounded-md border px-3 py-2 text-sm">
                        Select a caregiver.
                    </div>
                )}
                <DialogFooter>
                    <Button
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                    >
                        Close
                    </Button>
                    <Button
                        disabled={!details || !hasChanges || isSaving}
                        onClick={() => void save()}
                    >
                        {isSaving ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                            <Save className="h-4 w-4" />
                        )}
                        Save
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function DetailsSkeleton() {
    return (
        <div className="border-border rounded-md border p-3">
            <Skeleton className="h-4 w-36" />
            <Skeleton className="mt-4 h-3 w-full" />
            <Skeleton className="mt-2 h-3 w-5/6" />
            <Skeleton className="mt-6 h-20 w-full" />
            <Skeleton className="mt-3 h-20 w-full" />
        </div>
    );
}

function DetailSection({
    title,
    children,
}: {
    readonly title: string;
    readonly children: ReactNode;
}) {
    return (
        <section className="border-border rounded-md border p-3">
            <h3 className="text-foreground mb-3 text-sm font-medium">
                {title}
            </h3>
            {children}
        </section>
    );
}

function DetailGrid({
    rows,
}: {
    readonly rows: ReadonlyArray<readonly [string, string]>;
}) {
    return (
        <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
            {rows.map(([label, value]) => (
                <Fragment key={label}>
                    <dt className="text-muted-foreground">{label}</dt>
                    <dd className="min-w-0 break-words">{value}</dd>
                </Fragment>
            ))}
        </dl>
    );
}

function StackedRows({
    rows,
    emptyText,
}: {
    readonly rows: ReadonlyArray<{
        readonly key: string;
        readonly title: string;
        readonly meta: string;
    }>;
    readonly emptyText: string;
}) {
    if (rows.length === 0) {
        return <div className="text-muted-foreground text-sm">{emptyText}</div>;
    }

    return (
        <div className="space-y-2">
            {rows.map((row) => (
                <div
                    key={row.key}
                    className="border-border/60 bg-muted/50 rounded-md border px-3 py-2 text-sm"
                >
                    <div className="text-foreground font-medium">
                        {row.title}
                    </div>
                    <div className="text-muted-foreground mt-1 text-xs">
                        {row.meta}
                    </div>
                </div>
            ))}
        </div>
    );
}

function MemoryEventRows({
    rows,
    isLoading,
    error,
    emptyText,
    now,
}: {
    readonly rows: Array<CaregiverSimMemoryEventRead>;
    readonly isLoading: boolean;
    readonly error: unknown;
    readonly emptyText: string;
    readonly now: number;
}) {
    if (isLoading) {
        return (
            <div className="space-y-2">
                <Skeleton className="h-16 w-full" />
                <Skeleton className="h-16 w-full" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/50 dark:text-red-300">
                {getCaregiverSimErrorMessage(error)}
            </div>
        );
    }

    if (rows.length === 0) {
        return <div className="text-muted-foreground text-sm">{emptyText}</div>;
    }

    return (
        <div className="max-h-80 space-y-2 overflow-auto pr-1">
            {rows.map((row) => (
                <div
                    key={row.id}
                    className="border-border/60 bg-muted/50 rounded-md border px-3 py-2 text-sm"
                >
                    <div className="text-muted-foreground mb-1 flex flex-wrap items-center gap-2 text-xs">
                        <Badge variant="outline">{enumLabel(row.kind)}</Badge>
                        <span>importance {row.importance}</span>
                        <span>{formatTimestamp(row.created_at, now)}</span>
                    </div>
                    <div className="text-foreground whitespace-pre-wrap">
                        {row.description}
                    </div>
                    {row.keywords.length > 0 ? (
                        <div className="mt-2 flex flex-wrap gap-1">
                            {row.keywords.slice(0, 8).map((keyword) => (
                                <span
                                    key={keyword}
                                    className="bg-background text-muted-foreground rounded-full px-2 py-0.5 text-[11px]"
                                >
                                    {keyword}
                                </span>
                            ))}
                        </div>
                    ) : null}
                </div>
            ))}
        </div>
    );
}

function getCaregiverModelOption(value: string): CaregiverModelOption {
    return (
        CAREGIVER_MODEL_OPTIONS.find((option) => option.value === value) ??
        CAREGIVER_MODEL_OPTIONS[0]
    );
}

function workflowLabel(workflowType: WorkflowType): string {
    return (
        WORKFLOW_OPTIONS.find((option) => option.value === workflowType)
            ?.label ?? workflowType
    );
}

function ModelSelect({
    value,
    selectedOption,
    onChange,
    disabled,
}: {
    readonly value: string;
    readonly selectedOption: CaregiverModelOption;
    readonly onChange: (value: string) => void;
    readonly disabled: boolean;
}) {
    return (
        <Select value={value} onValueChange={onChange} disabled={disabled}>
            <SelectTrigger className="w-full">
                <div className="flex min-w-0 items-center gap-2">
                    <img
                        src={selectedOption.iconSrc}
                        alt={selectedOption.iconAlt}
                        className="h-4 w-4 shrink-0"
                    />
                    <span className="truncate">{selectedOption.label}</span>
                </div>
            </SelectTrigger>
            <SelectContent>
                {CAREGIVER_MODEL_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                        <span className="flex items-center gap-2">
                            <img
                                src={option.iconSrc}
                                alt={option.iconAlt}
                                className="h-4 w-4 shrink-0"
                            />
                            <span>{option.label}</span>
                        </span>
                    </SelectItem>
                ))}
            </SelectContent>
        </Select>
    );
}

function MemoryStatsCard({
    selectedPersonality,
    selectedCounterparty,
    logs,
    threads,
    now,
}: {
    readonly selectedPersonality: CaregiverSimPersonalityRead | undefined;
    readonly selectedCounterparty: string | null;
    readonly logs:
        | components['schemas']['CaregiverSimLogsResponse']
        | undefined;
    readonly threads: Array<CaregiverSimThreadRead>;
    readonly now: number;
}) {
    const selectedThread = threads.find(
        (thread) => thread.counterparty_phone === selectedCounterparty,
    );
    const summary = logs?.summary;

    return (
        <div className="border-border rounded-md border p-3 text-sm">
            <div className="mb-3 font-medium">Memory</div>
            <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2">
                <dt className="text-muted-foreground">Persona msgs</dt>
                <dd>{selectedPersonality?.message_count ?? 0}</dd>
                <dt className="text-muted-foreground">Persona threads</dt>
                <dd>{selectedPersonality?.thread_count ?? 0}</dd>
                <dt className="text-muted-foreground">Thread msgs</dt>
                <dd>
                    {selectedThread?.message_count ??
                        logs?.messages.length ??
                        0}
                </dd>
                <dt className="text-muted-foreground">Decisions</dt>
                <dd>{logs?.decision_logs.length ?? 0}</dd>
                <dt className="text-muted-foreground">Last activity</dt>
                <dd className="truncate">
                    {formatTimestamp(
                        selectedThread?.last_message_at ??
                            selectedPersonality?.last_message_at,
                        now,
                    )}
                </dd>
                <dt className="text-muted-foreground">Summarized</dt>
                <dd>{summary?.summarized_message_count ?? 0} msgs</dd>
            </dl>
            <div className="border-border mt-3 border-t pt-3">
                <div className="text-muted-foreground mb-1 text-xs font-medium">
                    Thread memory
                </div>
                <div className="bg-muted/50 text-foreground/80 max-h-32 overflow-auto rounded-md p-2 text-xs whitespace-pre-wrap">
                    {summary?.text || 'No rolling summary yet.'}
                </div>
            </div>
        </div>
    );
}

function ScenarioRunsCard({
    scenarioRuns,
    now,
}: {
    readonly scenarioRuns: Array<CaregiverSimScenarioRunRead>;
    readonly now: number;
}) {
    if (scenarioRuns.length === 0) {
        return null;
    }

    return (
        <div className="border-border rounded-md border p-3 text-sm">
            <div className="mb-3 font-medium">Workflow runs</div>
            <div className="space-y-2">
                {scenarioRuns.slice(0, 4).map((run) => (
                    <ScenarioRunItem key={run.id} run={run} now={now} />
                ))}
            </div>
        </div>
    );
}

function ScenarioRunItem({
    run,
    now,
}: {
    readonly run: CaregiverSimScenarioRunRead;
    readonly now: number;
}) {
    const checks = run.checks ?? [];
    const severity =
        checks.length > 0 ? scenarioCheckSummarySeverity(checks) : null;
    const visibleIssueChecks =
        severity === 'pass'
            ? checks.slice(0, 1)
            : checks.filter((check) => check.severity !== 'pass').slice(0, 3);

    return (
        <div
            className={cn(
                'bg-muted/50 rounded-md border p-2',
                scenarioRunBorderClassName(severity),
            )}
        >
            <div className="text-muted-foreground mb-1 flex flex-wrap items-center gap-2 text-xs">
                <Badge variant="outline">{enumLabel(run.workflow_type)}</Badge>
                <Badge variant="outline">
                    {run.workflow_channel.toUpperCase()}
                </Badge>
                {severity ? (
                    <Badge
                        variant="secondary"
                        className={cn(
                            'text-xs',
                            scenarioCheckBadgeClassName(severity),
                        )}
                    >
                        {scenarioCheckSeverityLabel(severity)}
                    </Badge>
                ) : null}
                <span>{run.turn_count} turns</span>
                <span>{formatTimestamp(run.created_at, now)}</span>
            </div>
            <div className="text-foreground/80 text-xs">
                {run.status}
                {run.stop_reason ? `: ${run.stop_reason}` : ''}
            </div>
            {visibleIssueChecks.length > 0 ? (
                <div className="mt-2 space-y-1 text-xs">
                    {visibleIssueChecks.map((check) => (
                        <div
                            key={check.id}
                            className={cn(
                                'font-medium',
                                scenarioCheckTextClassName(check.severity),
                            )}
                        >
                            {check.title}
                        </div>
                    ))}
                </div>
            ) : null}
        </div>
    );
}

function scenarioCheckSummarySeverity(
    checks: ReadonlyArray<CaregiverSimScenarioRunCheckRead>,
): ScenarioCheckSeverity {
    if (checks.some((check) => check.severity === 'fail')) {
        return 'fail';
    }
    if (checks.some((check) => check.severity === 'warn')) {
        return 'warn';
    }
    return 'pass';
}

function scenarioCheckSeverityLabel(severity: ScenarioCheckSeverity): string {
    switch (severity) {
        case 'fail':
            return 'Fail';
        case 'warn':
            return 'Warn';
        case 'pass':
            return 'Pass';
        default:
            return 'Warn';
    }
}

function scenarioCheckBadgeClassName(severity: ScenarioCheckSeverity): string {
    switch (severity) {
        case 'fail':
            return 'bg-red-50 text-red-700 dark:bg-red-950/50 dark:text-red-300';
        case 'warn':
            return 'bg-amber-50 text-amber-700 dark:bg-amber-950/50 dark:text-amber-300';
        case 'pass':
            return 'bg-green-50 text-green-700 dark:bg-green-950/50 dark:text-green-300';
        default:
            return 'bg-amber-50 text-amber-700 dark:bg-amber-950/50 dark:text-amber-300';
    }
}

function scenarioRunBorderClassName(
    severity: ScenarioCheckSeverity | null,
): string {
    if (severity === null) {
        return 'border-border/60';
    }

    switch (severity) {
        case 'fail':
            return 'border-red-300 bg-red-50/40 dark:border-red-900 dark:bg-red-950/40';
        case 'warn':
            return 'border-amber-300 bg-amber-50/40 dark:border-amber-900 dark:bg-amber-950/40';
        case 'pass':
            return 'border-green-200 bg-green-50/30 dark:border-green-900 dark:bg-green-950/30';
        default:
            return 'border-border/60';
    }
}

function scenarioCheckTextClassName(severity: ScenarioCheckSeverity): string {
    switch (severity) {
        case 'fail':
            return 'text-red-700 dark:text-red-400';
        case 'warn':
            return 'text-amber-700 dark:text-amber-400';
        case 'pass':
            return 'text-green-700 dark:text-green-400';
        default:
            return 'text-amber-700 dark:text-amber-400';
    }
}

function personalityCardHealthClassName({
    selected,
    severity,
}: {
    readonly selected: boolean;
    readonly severity: ScenarioCheckSeverity | null;
}): string {
    if (severity === 'fail') {
        return selected
            ? 'border-red-400 bg-red-50/40 ring-1 ring-red-200 dark:border-red-700 dark:bg-red-950/40 dark:ring-red-900'
            : 'border-red-300 hover:bg-red-50/40 dark:border-red-900 dark:hover:bg-red-950/40';
    }
    if (severity === 'warn') {
        return selected
            ? 'border-amber-400 bg-amber-50/40 ring-1 ring-amber-200 dark:border-amber-700 dark:bg-amber-950/40 dark:ring-amber-900'
            : 'border-amber-300 hover:bg-amber-50/40 dark:border-amber-900 dark:hover:bg-amber-950/40';
    }
    if (selected) {
        return 'border-blue-300 bg-blue-50/40 ring-1 ring-blue-200 dark:border-blue-700 dark:bg-blue-950/40 dark:ring-blue-900';
    }
    return 'border-border hover:bg-muted/50';
}

function voiceHealthPanelClassName(
    severity: ScenarioCheckSeverity | null,
): string {
    if (severity === 'fail') {
        return 'border-red-300 bg-red-50/20 dark:border-red-900 dark:bg-red-950/20';
    }
    if (severity === 'warn') {
        return 'border-amber-300 bg-amber-50/20 dark:border-amber-900 dark:bg-amber-950/20';
    }
    return 'border-border';
}

function getVoiceHealthIssue({
    call,
    transcript,
    realVoiceCall,
    turnsCompleted,
}: {
    readonly call: VoiceSimCall | null;
    readonly transcript: ReadonlyArray<VoiceTranscriptEntry>;
    readonly realVoiceCall: VoiceCallRead | null;
    readonly turnsCompleted: number;
}): VoiceHealthIssue | null {
    if (call?.status === 'error') {
        return {
            severity: 'fail',
            title: call.errorMessage || 'Voice session entered an error state.',
        };
    }

    if (call && isTerminalVoiceSimStatus(call.status)) {
        if (transcript.length === 0) {
            return {
                severity: 'fail',
                title: 'Voice call ended without a transcript.',
            };
        }
        if (!transcript.some((entry) => entry.speaker === 'caregiver')) {
            return {
                severity: 'warn',
                title: 'Voice call ended without a caregiver transcript turn.',
            };
        }
    }

    if (
        realVoiceCall &&
        isTerminalPersistedVoiceCallStatus(realVoiceCall.call_status)
    ) {
        if (!realVoiceCall.transcript?.trim()) {
            return {
                severity: 'warn',
                title: 'Persisted voice call transcript is empty.',
            };
        }
        if (turnsCompleted === 0) {
            return {
                severity: 'warn',
                title: 'Persisted voice call has no caregiver turns.',
            };
        }
    }

    return null;
}

function InboundVoiceCallCue({
    caregiverPhoneNumber,
    inboundPhoneNumber,
}: {
    readonly caregiverPhoneNumber: string;
    readonly inboundPhoneNumber: string | null;
}) {
    return (
        <div className="rounded-md border border-blue-100 bg-blue-50 px-3 py-2 text-xs dark:border-blue-900 dark:bg-blue-950/50">
            <div className="mb-2 flex items-center gap-2 font-medium text-blue-900 dark:text-blue-300">
                <PhoneIncoming className="h-4 w-4" />
                Inbound individual call
            </div>
            <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-1 text-blue-900/80 dark:text-blue-300/80">
                <dt>From</dt>
                <dd className="truncate font-mono">{caregiverPhoneNumber}</dd>
                <dt>To</dt>
                <dd className="truncate font-mono">
                    {inboundPhoneNumber ?? 'Phoebe number unset'}
                </dd>
            </dl>
        </div>
    );
}

function VoiceMonitorPanel({
    call,
    isStarting,
    transcript,
    toolCalls,
    realVoiceCall,
    isRealVoiceCallLoading,
    realRecordingUrl,
    isRealRecordingUrlLoading,
    realRecordingUrlError,
    isReplaying,
    replayState,
    isEndingRealVoiceCall,
    replayDisabled = false,
    onReplay,
    onReplaySeek,
    onEndRealVoiceCall,
    heightClassName = SIM_SCROLL_PANEL_HEIGHT_CLASS,
    compact = false,
}: {
    readonly call: VoiceSimCall | null;
    readonly isStarting: boolean;
    readonly transcript: Array<VoiceTranscriptEntry>;
    readonly toolCalls: Array<ToolCallDisplayEntry>;
    readonly realVoiceCall: VoiceCallRead | null;
    readonly isRealVoiceCallLoading: boolean;
    readonly realRecordingUrl: string | undefined;
    readonly isRealRecordingUrlLoading: boolean;
    readonly realRecordingUrlError: unknown;
    readonly isReplaying: boolean;
    readonly replayState: VoiceReplayState | null;
    readonly isEndingRealVoiceCall: boolean;
    readonly replayDisabled?: boolean;
    readonly onReplay: () => void;
    readonly onReplaySeek: (positionMs: number) => void;
    readonly onEndRealVoiceCall: () => void;
    readonly heightClassName?: string;
    readonly compact?: boolean;
}) {
    const status = call?.status ?? (isStarting ? 'connecting' : 'idle');
    const showingRealVoiceCall = !call && realVoiceCall !== null;
    const active =
        (!isTerminalVoiceSimStatus(status) && status !== 'idle') ||
        (showingRealVoiceCall &&
            !isTerminalPersistedVoiceCallStatus(realVoiceCall.call_status));
    const canReplay =
        !!call &&
        isTerminalVoiceSimStatus(status) &&
        transcript.length > 0 &&
        !isReplaying &&
        !replayDisabled;
    const turnsCompleted = showingRealVoiceCall
        ? countCaregiverTranscriptTurns(realVoiceCall.transcript)
        : (call?.turnsCompleted ?? 0);
    const displayedToolCalls = finalizeTerminalVoiceToolCalls(
        toolCalls,
        !!call && isTerminalVoiceSimStatus(status),
    );
    const voiceHealthIssue = getVoiceHealthIssue({
        call,
        transcript,
        realVoiceCall: showingRealVoiceCall ? realVoiceCall : null,
        turnsCompleted,
    });

    return (
        <div
            className={cn(
                heightClassName,
                'flex min-h-0 flex-col overflow-hidden rounded-md border',
                voiceHealthPanelClassName(voiceHealthIssue?.severity ?? null),
                compact ? 'p-3' : 'p-6',
            )}
        >
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex min-w-0 items-center gap-2">
                    <Headphones className="text-foreground/80 h-5 w-5" />
                    <div className="min-w-0">
                        <div className="text-foreground font-medium">
                            Voice call
                        </div>
                        <div className="text-muted-foreground mt-1 truncate text-sm">
                            {call
                                ? workflowLabel(call.workflowType)
                                : showingRealVoiceCall
                                  ? 'Real Twilio call'
                                  : isRealVoiceCallLoading
                                    ? 'Loading latest call'
                                    : 'No active call'}
                        </div>
                    </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    {voiceHealthIssue ? (
                        <Badge
                            variant="secondary"
                            className={cn(
                                'text-xs',
                                scenarioCheckBadgeClassName(
                                    voiceHealthIssue.severity,
                                ),
                            )}
                        >
                            {scenarioCheckSeverityLabel(
                                voiceHealthIssue.severity,
                            )}
                        </Badge>
                    ) : null}
                    <Badge
                        variant={status === 'error' ? 'destructive' : 'outline'}
                    >
                        {isEndingRealVoiceCall
                            ? 'Ending'
                            : showingRealVoiceCall
                              ? enumLabel(realVoiceCall.call_status)
                              : voiceSimStatusLabel(status)}
                    </Badge>
                </div>
            </div>
            {voiceHealthIssue ? (
                <div
                    className={cn(
                        'mt-3 rounded-md border px-3 py-2 text-xs font-medium',
                        voiceHealthIssue.severity === 'fail'
                            ? 'border-red-200 bg-red-50 text-red-700 dark:border-red-900 dark:bg-red-950/50 dark:text-red-300'
                            : 'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900 dark:bg-amber-950/50 dark:text-amber-300',
                    )}
                >
                    {voiceHealthIssue.title}
                </div>
            ) : null}
            {active || !!call ? (
                <div
                    className={cn(
                        'flex flex-none items-center justify-center',
                        compact ? 'min-h-[52px]' : 'min-h-[140px]',
                    )}
                >
                    <div
                        className={cn(
                            'flex items-center gap-2',
                            compact ? 'h-12' : 'h-20',
                        )}
                    >
                        {Array.from({ length: 18 }, (_, index) => (
                            <div
                                key={index}
                                className={cn(
                                    'bg-muted w-2 rounded-full',
                                    active && 'animate-pulse bg-blue-500',
                                )}
                                style={{
                                    height: compact
                                        ? `${12 + ((index * 11) % 36)}px`
                                        : `${24 + ((index * 17) % 72)}px`,
                                    animationDelay: `${index * 60}ms`,
                                }}
                            />
                        ))}
                    </div>
                </div>
            ) : null}
            {showingRealVoiceCall ? (
                <RealVoiceCallPanel
                    voiceCall={realVoiceCall}
                    recordingUrl={realRecordingUrl}
                    isRecordingUrlLoading={isRealRecordingUrlLoading}
                    recordingUrlError={realRecordingUrlError}
                    isEnding={isEndingRealVoiceCall}
                    isReplaying={isReplaying}
                    replayState={replayState}
                    replayDisabled={replayDisabled}
                    onReplay={onReplay}
                    onReplaySeek={onReplaySeek}
                    onEnd={onEndRealVoiceCall}
                    compact={compact}
                />
            ) : (
                <div className="border-border/60 flex min-h-0 flex-1 flex-col overflow-hidden rounded-md border">
                    <div className="border-border/60 flex flex-none flex-wrap items-center justify-between gap-2 border-b px-3 py-2">
                        <div className="text-foreground text-sm font-medium">
                            Call transcript
                        </div>
                        <Button
                            variant="outline"
                            size="sm"
                            disabled={!canReplay}
                            onClick={onReplay}
                        >
                            {isReplaying ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                                <Play className="h-4 w-4" />
                            )}
                            {isReplaying ? 'Playing' : 'Listen back'}
                        </Button>
                    </div>
                    <VoiceReplayControls
                        replayState={replayState}
                        disabled={replayDisabled}
                        onSeek={onReplaySeek}
                    />
                    <ScrollArea
                        className={
                            compact ? 'h-[104px] flex-none' : 'min-h-0 flex-1'
                        }
                    >
                        {transcript.length === 0 &&
                        displayedToolCalls.length === 0 ? (
                            <div className="text-muted-foreground px-3 py-6 text-sm">
                                {isRealVoiceCallLoading
                                    ? 'Loading latest call.'
                                    : 'No transcript yet.'}
                            </div>
                        ) : (
                            <div className="space-y-2 p-3">
                                {transcript.map((entry) => (
                                    <VoiceTranscriptRow
                                        key={entry.id}
                                        entry={entry}
                                    />
                                ))}
                                <ToolCallRows
                                    toolCalls={displayedToolCalls}
                                    compact={compact}
                                />
                            </div>
                        )}
                    </ScrollArea>
                </div>
            )}
            {!showingRealVoiceCall && !compact ? (
                <dl className="border-border/60 bg-muted/50 mt-3 grid flex-none grid-cols-[auto_1fr] gap-x-4 gap-y-2 rounded-md border p-3 text-sm">
                    {call || isStarting ? (
                        <Fragment>
                            <dt className="text-muted-foreground">State</dt>
                            <dd className="truncate">
                                {call?.statusText ?? 'Starting call'}
                            </dd>
                            <dt className="text-muted-foreground">Turns</dt>
                            <dd>{turnsCompleted}</dd>
                            <dt className="text-muted-foreground">Call SID</dt>
                            <dd className="truncate font-mono text-xs">
                                {call?.callSid ?? '-'}
                            </dd>
                        </Fragment>
                    ) : (
                        <div className="text-muted-foreground col-span-2">
                            Start a voice call to see live state.
                        </div>
                    )}
                </dl>
            ) : null}
        </div>
    );
}

function VoiceReplayControls({
    replayState,
    disabled,
    onSeek,
}: {
    readonly replayState: VoiceReplayState | null;
    readonly disabled: boolean;
    readonly onSeek: (positionMs: number) => void;
}) {
    if (!replayState || replayState.durationMs <= 0) {
        return null;
    }

    const positionMs = clampNumber(
        replayState.positionMs,
        0,
        replayState.durationMs,
    );

    return (
        <div className="border-border/60 text-muted-foreground flex items-center gap-2 border-b px-3 py-2 text-xs">
            <span className="w-10 text-right font-mono">
                {formatReplayClock(positionMs)}
            </span>
            <input
                type="range"
                min={0}
                max={Math.max(1, Math.round(replayState.durationMs))}
                step={100}
                value={Math.round(positionMs)}
                disabled={disabled}
                aria-label="Playback position"
                onChange={(event) =>
                    onSeek(Number.parseInt(event.currentTarget.value, 10))
                }
                className="h-2 min-w-0 flex-1 cursor-pointer accent-blue-600 disabled:cursor-not-allowed disabled:opacity-50"
            />
            <span className="w-10 font-mono">
                {formatReplayClock(replayState.durationMs)}
            </span>
        </div>
    );
}

function RealVoiceCallPanel({
    voiceCall,
    recordingUrl,
    isRecordingUrlLoading,
    recordingUrlError,
    isEnding,
    isReplaying,
    replayState,
    replayDisabled,
    onReplay,
    onReplaySeek,
    onEnd,
    compact,
}: {
    readonly voiceCall: VoiceCallRead;
    readonly recordingUrl: string | undefined;
    readonly isRecordingUrlLoading: boolean;
    readonly recordingUrlError: unknown;
    readonly isEnding: boolean;
    readonly isReplaying: boolean;
    readonly replayState: VoiceReplayState | null;
    readonly replayDisabled: boolean;
    readonly onReplay: () => void;
    readonly onReplaySeek: (positionMs: number) => void;
    readonly onEnd: () => void;
    readonly compact: boolean;
}) {
    const transcriptText = voiceCall.transcript?.trim() ?? '';
    const turnsCompleted = countCaregiverTranscriptTurns(voiceCall.transcript);
    const statusLabel = enumLabel(voiceCall.call_status);
    const canEnd = voiceCall.call_status === 'ongoing';
    const callIsTerminal = isTerminalPersistedVoiceCallStatus(
        voiceCall.call_status,
    );
    const playbackTitle = callIsTerminal ? 'Listen back' : 'Recording playback';
    const hasRecordingPlayback =
        !!recordingUrl || !!voiceCall.recording_url?.trim();
    const canReplayTranscript =
        callIsTerminal &&
        !hasRecordingPlayback &&
        transcriptText.length > 0 &&
        !isReplaying &&
        !replayDisabled;
    const recordingPlaceholderText = isRecordingUrlLoading
        ? 'Checking for recording playback.'
        : callIsTerminal
          ? transcriptText
              ? 'Replay the current transcript while the recording is still being prepared.'
              : 'No persisted transcript is available. Listen back will appear when the recording is ready.'
          : 'Recording playback will be available after the call ends.';

    return (
        <div className="flex min-h-0 flex-1 flex-col gap-3">
            <div className="border-border/60 bg-background rounded-md border p-3">
                <div className="mb-3 flex items-start justify-between gap-3">
                    <div className="min-w-0">
                        <div className="text-foreground text-sm font-medium">
                            {playbackTitle}
                        </div>
                        <div className="text-muted-foreground mt-0.5 text-xs">
                            {statusLabel} · {turnsCompleted} turns
                        </div>
                    </div>
                    <div className="flex flex-none items-center gap-2">
                        {canEnd ? (
                            <Button
                                variant="outline"
                                size="sm"
                                disabled={isEnding}
                                onClick={onEnd}
                            >
                                {isEnding ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                    <Square className="h-4 w-4" />
                                )}
                                End
                            </Button>
                        ) : null}
                        <Button variant="outline" size="sm" asChild={true}>
                            <a href={`/admin/voice-qa/${voiceCall.id}`}>
                                <ExternalLink className="h-4 w-4" />
                                Full call
                            </a>
                        </Button>
                    </div>
                </div>
                {hasRecordingPlayback ? (
                    <VoiceCallAudioPlayer
                        voiceCall={{
                            id: voiceCall.id,
                            recording_url: voiceCall.recording_url,
                        }}
                        prefetchedRecordingUrl={recordingUrl}
                        isRecordingUrlLoading={isRecordingUrlLoading}
                        recordingUrlError={recordingUrlError}
                        className={cn(
                            'w-full min-w-0 focus:outline-none',
                            compact ? 'h-10' : 'h-12',
                        )}
                    />
                ) : (
                    <div className="border-border bg-muted/50 rounded-md border border-dashed px-3 py-2">
                        <div
                            role="status"
                            className="text-muted-foreground text-xs"
                        >
                            {recordingPlaceholderText}
                        </div>
                        {callIsTerminal && transcriptText ? (
                            <div className="mt-2 space-y-2">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    disabled={!canReplayTranscript}
                                    onClick={onReplay}
                                >
                                    {isReplaying ? (
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                    ) : (
                                        <Play className="h-4 w-4" />
                                    )}
                                    {isReplaying
                                        ? 'Playing transcript'
                                        : 'Listen back transcript'}
                                </Button>
                                <div className="border-border/60 bg-background overflow-hidden rounded-md border">
                                    <VoiceReplayControls
                                        replayState={replayState}
                                        disabled={replayDisabled}
                                        onSeek={onReplaySeek}
                                    />
                                </div>
                            </div>
                        ) : null}
                    </div>
                )}
                <dl className="mt-3 grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-1.5 text-xs">
                    <dt className="text-muted-foreground">Call SID</dt>
                    <dd className="truncate font-mono">
                        {voiceCall.external_call_id ?? '-'}
                    </dd>
                    <dt className="text-muted-foreground">Voice ID</dt>
                    <dd className="truncate font-mono text-blue-600 dark:text-blue-400">
                        {voiceCall.id}
                    </dd>
                </dl>
            </div>
            <ScrollArea
                className={cn(
                    'border-border/60 min-h-0 rounded-md border',
                    compact ? 'flex-1' : 'h-[clamp(22rem,42vh,34rem)]',
                )}
            >
                <div className="space-y-3 p-3">
                    {voiceCall.call_summary ? (
                        <div className="rounded-md bg-blue-50 p-3 dark:bg-blue-950/50">
                            <p className="mb-2 text-sm font-medium">
                                Call Summary
                            </p>
                            <p className="text-foreground/80 text-sm">
                                {voiceCall.call_summary}
                            </p>
                        </div>
                    ) : null}
                    <div className="bg-muted/50 rounded-md p-3">
                        <p className="mb-2 text-sm font-medium">Transcript</p>
                        {transcriptText ? (
                            <VoiceCallTranscriptLines
                                transcriptText={transcriptText}
                            />
                        ) : (
                            <p className="text-muted-foreground text-sm italic">
                                No transcript yet.
                            </p>
                        )}
                    </div>
                </div>
            </ScrollArea>
        </div>
    );
}

function VoiceCallTranscriptLines({
    transcriptText,
}: {
    readonly transcriptText: string;
}) {
    return (
        <div className="space-y-2">
            {transcriptText
                .split('\n')
                .filter((line) => line.trim() !== '')
                .map((line, index) => {
                    const match = line.match(
                        /^(Agent|Caregiver|Phoebe|User):\s*(.+)$/,
                    );
                    if (!match) {
                        return (
                            <div key={index} className="text-sm">
                                {line}
                            </div>
                        );
                    }

                    const [, speaker, message] = match;
                    const displaySpeaker =
                        speaker === 'User' ? 'Caregiver' : speaker;

                    return (
                        <div key={index} className="flex gap-3 text-sm">
                            <span className="min-w-[72px] font-medium">
                                {displaySpeaker}:
                            </span>
                            <span className="flex-1">{message}</span>
                        </div>
                    );
                })}
        </div>
    );
}

function voiceCallTranscriptToReplayEntries(
    voiceCall: VoiceCallRead,
): Array<VoiceTranscriptEntry> {
    const transcriptText = voiceCall.transcript?.trim();
    if (!transcriptText) {
        return [];
    }

    const entries: Array<VoiceTranscriptEntry> = [];
    transcriptText.split('\n').forEach((line, index) => {
        const match = line
            .trim()
            .match(/^(Agent|Caregiver|Phoebe|User):\s*(.+)$/);
        const speaker = match?.[1];
        const message = match?.[2]?.trim();
        if (!speaker || !message) {
            return;
        }

        entries.push({
            id: `${voiceCall.id}:transcript:${index}`,
            speaker:
                speaker === 'User' || speaker === 'Caregiver'
                    ? 'caregiver'
                    : 'phoebe',
            text: message,
            audioPayload: null,
            audioPayloads: [],
            createdAt: voiceCall.created_at,
        });
    });
    return entries;
}

function countCaregiverTranscriptTurns(
    transcriptText: string | null | undefined,
): number {
    if (!transcriptText) {
        return 0;
    }

    return transcriptText
        .split('\n')
        .filter((line) => /^(Caregiver|User):\s*\S/.test(line.trim())).length;
}

function isTerminalPersistedVoiceCallStatus(
    status: VoiceCallRead['call_status'],
): boolean {
    return new Set(['ended', 'failed', 'no_answer', 'cancelled']).has(status);
}

function VoiceTranscriptRow({
    entry,
}: {
    readonly entry: VoiceTranscriptEntry;
}) {
    const isCaregiver = entry.speaker === 'caregiver';
    return (
        <div
            data-testid={`voice-transcript-row-${entry.speaker}`}
            className={cn(
                'rounded-md border px-3 py-2 text-sm',
                isCaregiver
                    ? 'border-blue-100 bg-blue-50 dark:border-blue-900 dark:bg-blue-950/50'
                    : 'border-border/60 bg-muted/50',
            )}
        >
            <div className="text-muted-foreground mb-1 flex items-center gap-2 text-xs font-medium">
                <Headphones className="h-3.5 w-3.5" />
                <span>{isCaregiver ? 'Caregiver' : 'Phoebe'}</span>
            </div>
            <div className="text-foreground whitespace-pre-wrap">
                {entry.text}
            </div>
        </div>
    );
}

function ToolCallRows({
    toolCalls,
    compact = false,
}: {
    readonly toolCalls: Array<ToolCallDisplayEntry>;
    readonly compact?: boolean;
}) {
    if (toolCalls.length === 0) {
        return null;
    }

    return (
        <div
            data-testid="caregiver-sim-tool-calls"
            className={cn(
                'text-muted-foreground space-y-1 text-[11px]',
                compact ? 'px-1' : 'border-border/60 border-t pt-1.5',
            )}
        >
            {toolCalls.map((toolCall) => {
                const details = [
                    toolCall.arguments !== null &&
                    toolCall.arguments !== undefined
                        ? `args: ${formatToolCallValue(toolCall.arguments)}`
                        : null,
                    toolCall.result !== null && toolCall.result !== undefined
                        ? `result: ${formatToolCallValue(toolCall.result)}`
                        : null,
                    toolCall.error ? `error: ${toolCall.error}` : null,
                ]
                    .filter((item): item is string => !!item)
                    .join(' · ');

                return (
                    <div
                        key={`${toolCall.id}:${toolCall.status}`}
                        className="flex min-w-0 items-center gap-1.5"
                        title={details || undefined}
                    >
                        <Wrench className="text-muted-foreground/70 h-3 w-3 flex-none" />
                        <span className="text-muted-foreground min-w-0 truncate font-mono">
                            {toolCall.name || 'tool'}
                        </span>
                        <span className="text-muted-foreground/70">·</span>
                        <span
                            className={cn(
                                'flex-none font-medium',
                                toolCallStatusTextClass(toolCall.status),
                            )}
                        >
                            {toolCall.status}
                        </span>
                        {!compact && details ? (
                            <span className="text-muted-foreground/70 min-w-0 truncate">
                                · {details}
                            </span>
                        ) : null}
                    </div>
                );
            })}
        </div>
    );
}

function VoiceSimCallCard({
    call,
    transcript,
    showEmpty,
    isStarting,
    isReplaying,
    replayState,
    replayDisabled,
    onEnd,
    onReplay,
    onReplaySeek,
}: {
    readonly call: VoiceSimCall | null;
    readonly transcript: Array<VoiceTranscriptEntry>;
    readonly showEmpty: boolean;
    readonly isStarting: boolean;
    readonly isReplaying: boolean;
    readonly replayState: VoiceReplayState | null;
    readonly replayDisabled: boolean;
    readonly onEnd: () => void;
    readonly onReplay: () => void;
    readonly onReplaySeek: (positionMs: number) => void;
}) {
    if (!call && !showEmpty && !isStarting) {
        return null;
    }

    const status = call?.status ?? (isStarting ? 'connecting' : 'idle');
    const isEnding = status === 'ending';
    const isEnded = status === 'ended';
    const canEnd = !!call && isEndableVoiceSimStatus(status) && !isEnding;
    const endButtonLabel = isEnded ? 'Ended' : isEnding ? 'Ending' : 'End call';
    const canReplay =
        !!call &&
        isTerminalVoiceSimStatus(status) &&
        transcript.length > 0 &&
        !isReplaying &&
        !replayDisabled;

    return (
        <div className="border-border rounded-md border p-3 text-sm">
            <div className="mb-3 flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <div className="flex items-center gap-2 font-medium">
                        <Headphones className="h-4 w-4" />
                        Voice call
                    </div>
                    <div className="text-muted-foreground mt-1 truncate text-xs">
                        {call
                            ? workflowLabel(call.workflowType)
                            : 'No active call'}
                    </div>
                </div>
                <Badge variant={status === 'error' ? 'destructive' : 'outline'}>
                    {voiceSimStatusLabel(status)}
                </Badge>
            </div>
            <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2">
                {call || isStarting ? (
                    <Fragment>
                        <dt className="text-muted-foreground">State</dt>
                        <dd className="truncate">
                            {call?.statusText ?? 'Starting call'}
                        </dd>
                        <dt className="text-muted-foreground">Turns</dt>
                        <dd>{call?.turnsCompleted ?? 0}</dd>
                        <dt className="text-muted-foreground">Call SID</dt>
                        <dd className="truncate font-mono text-xs">
                            {call?.callSid ?? '-'}
                        </dd>
                    </Fragment>
                ) : (
                    <div className="text-muted-foreground col-span-2">
                        Start a voice call to see live state.
                    </div>
                )}
            </dl>
            {call?.errorMessage ? (
                <div className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700 dark:border-red-900 dark:bg-red-950/50 dark:text-red-300">
                    {call.errorMessage}
                </div>
            ) : null}
            <Button
                className="mt-3 w-full justify-start"
                variant="outline"
                disabled={!canEnd}
                onClick={onEnd}
            >
                {isEnded ? (
                    <CheckCircle2 className="mr-2 h-4 w-4" />
                ) : isEnding ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                    <Square className="mr-2 h-4 w-4" />
                )}
                {endButtonLabel}
            </Button>
            {call &&
            isTerminalVoiceSimStatus(status) &&
            transcript.length > 0 ? (
                <div className="mt-2 space-y-2">
                    <Button
                        className="w-full justify-start"
                        variant="outline"
                        disabled={!canReplay}
                        onClick={onReplay}
                    >
                        {isReplaying ? (
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        ) : (
                            <Play className="mr-2 h-4 w-4" />
                        )}
                        {isReplaying ? 'Playing' : 'Listen back'}
                    </Button>
                    <div className="border-border/60 rounded-md border">
                        <VoiceReplayControls
                            replayState={replayState}
                            disabled={replayDisabled}
                            onSeek={onReplaySeek}
                        />
                    </div>
                </div>
            ) : null}
        </div>
    );
}

function PersonalityConversationCard({
    personality,
    selected,
    selectedCounterparty,
    defaultCounterparty,
    pendingScenarioRuns,
    pendingSimulation,
    scenarioCounterparty,
    scenarioDetails,
    workflowChannel,
    pendingAction,
    startingVoiceSimKeys,
    voiceSimCalls,
    voiceTranscripts,
    voiceToolCalls,
    replayingVoiceCallId,
    voiceReplayState,
    hasNewActivity,
    logsRefreshToken,
    interventionBody,
    isInterventionSending,
    onInterventionBodyChange,
    onSendIntervention,
    onReplayVoiceTranscript,
    onReplayRealVoiceCallTranscript,
    onVoiceReplaySeek,
    onSelect,
    onExpand,
    onCounterpartyChange,
    onRun,
    onContinue,
    now,
    showExpandButton = true,
    modalView = false,
}: {
    readonly personality: CaregiverSimPersonalityRead;
    readonly selected: boolean;
    readonly selectedCounterparty: string | null;
    readonly defaultCounterparty: string | null;
    readonly pendingScenarioRuns: Record<string, PendingScenarioRun>;
    readonly pendingSimulation: PendingSimulation | null;
    readonly scenarioCounterparty: string;
    readonly scenarioDetails: string;
    readonly workflowChannel: WorkflowChannel;
    readonly pendingAction: PendingAction;
    readonly startingVoiceSimKeys: ReadonlySet<string>;
    readonly voiceSimCalls: VoiceSimCallsByKey;
    readonly voiceTranscripts: VoiceTranscriptsByCallId;
    readonly voiceToolCalls: VoiceToolCallsByCallId;
    readonly replayingVoiceCallId: string | null;
    readonly voiceReplayState: VoiceReplayState | null;
    readonly hasNewActivity: boolean;
    readonly logsRefreshToken: number;
    readonly interventionBody: string;
    readonly isInterventionSending: boolean;
    readonly onInterventionBodyChange: (value: string) => void;
    readonly onSendIntervention: () => void;
    readonly onReplayVoiceTranscript: (call: VoiceSimCall | null) => void;
    readonly onReplayRealVoiceCallTranscript: (
        voiceCall: VoiceCallRead | null,
    ) => void;
    readonly onVoiceReplaySeek: (callId: string, positionMs: number) => void;
    readonly onSelect: () => void;
    readonly onExpand: (counterparty: string | null) => void;
    readonly onCounterpartyChange: (counterparty: string) => void;
    readonly onRun: (
        counterparty: string | null,
        caregiverDetails: CaregiverSimCaregiverDetailsResponse | undefined,
        voiceDirection?: VoiceSimDirection,
    ) => void;
    readonly onContinue: (counterparty: string | null) => void;
    readonly now: number;
    readonly showExpandButton?: boolean;
    readonly modalView?: boolean;
}) {
    const [cardCounterparty, setCardCounterparty] = useState<string | null>(
        null,
    );
    const [endingRealVoiceCallId, setEndingRealVoiceCallId] = useState<
        string | null
    >(null);
    const pendingRuns = useMemo(
        () =>
            Object.values(pendingScenarioRuns).filter(
                (run) => run.personalityIdentifier === personality.identifier,
            ),
        [pendingScenarioRuns, personality.identifier],
    );
    const threads = useMemo(
        () => withPendingScenarioThreads(personality.threads, pendingRuns),
        [pendingRuns, personality.threads],
    );
    const voiceSimCallsForPersonality = useMemo(
        () =>
            Object.values(voiceSimCalls).filter(
                (call) => call.personalityIdentifier === personality.identifier,
            ),
        [personality.identifier, voiceSimCalls],
    );
    const latestVoiceSimCallForPersonality = getLatestVoiceSimCall(
        voiceSimCallsForPersonality,
    );
    const preferredCounterparty =
        latestVoiceSimCallForPersonality?.counterpartyPhone ??
        getLatestThreadCounterparty(threads) ??
        defaultCounterparty;
    const activeCounterparty = selected
        ? (selectedCounterparty ?? cardCounterparty ?? preferredCounterparty)
        : (cardCounterparty ?? preferredCounterparty);
    const activeVoiceCallKey = activeCounterparty
        ? scenarioRunKey(personality.identifier, activeCounterparty)
        : null;
    const voiceSimCallForThread = activeVoiceCallKey
        ? (voiceSimCalls[activeVoiceCallKey] ?? null)
        : latestVoiceSimCallForPersonality;
    const voiceTranscript = voiceSimCallForThread
        ? (voiceTranscripts[voiceSimCallForThread.callId] ?? [])
        : [];
    const voiceToolCallEntries = voiceSimCallForThread
        ? (voiceToolCalls[voiceSimCallForThread.callId] ?? [])
        : [];
    const activeVoiceCallIsStarting =
        !!activeVoiceCallKey && startingVoiceSimKeys.has(activeVoiceCallKey);
    const logsQuery = useCaregiverSimLogs(
        personality.identifier,
        activeCounterparty,
    );
    const mutateLogs = logsQuery.mutate;
    const latestScenarioRun = logsQuery.data?.scenario_runs?.[0] ?? null;
    const latestScenarioChecks = latestScenarioRun?.checks ?? [];
    const latestScenarioSeverity =
        latestScenarioChecks.length > 0
            ? scenarioCheckSummarySeverity(latestScenarioChecks)
            : null;
    const latestVoiceCallQuery = useCaregiverSimLatestVoiceCall(
        personality.identifier,
        activeCounterparty,
        { enabled: workflowChannel === 'voice' },
    );
    const mutateLatestVoiceCall = latestVoiceCallQuery.mutate;
    const latestVoiceCall = latestVoiceCallQuery.data?.voice_call ?? null;
    const [endedRealVoiceCallIds, setEndedRealVoiceCallIds] = useState<
        Set<string>
    >(() => new Set());
    const [observedLiveRealVoiceCallIds, setObservedLiveRealVoiceCallIds] =
        useState<Set<string>>(() => new Set());
    const latestVoiceCallId = latestVoiceCall?.id ?? null;
    const latestVoiceCallIsTerminal =
        latestVoiceCall !== null &&
        isTerminalPersistedVoiceCallStatus(latestVoiceCall.call_status);
    useEffect(() => {
        if (latestVoiceCallId === null || latestVoiceCallIsTerminal) {
            return;
        }

        setObservedLiveRealVoiceCallIds((current) => {
            if (current.has(latestVoiceCallId)) {
                return current;
            }

            const next = new Set(current);
            next.add(latestVoiceCallId);
            return next;
        });
    }, [latestVoiceCallId, latestVoiceCallIsTerminal]);
    const latestVoiceCallEndedInThisSession =
        latestVoiceCall !== null &&
        endedRealVoiceCallIds.has(latestVoiceCall.id);
    const latestVoiceCallWasLiveInThisSession =
        latestVoiceCall !== null &&
        observedLiveRealVoiceCallIds.has(latestVoiceCall.id);
    const displayedLatestVoiceCall: VoiceCallRead | null =
        latestVoiceCall && latestVoiceCallEndedInThisSession
            ? {
                  ...latestVoiceCall,
                  call_status: 'ended',
              }
            : latestVoiceCall;
    const shouldShowLatestVoiceCall =
        displayedLatestVoiceCall !== null &&
        (!latestVoiceCallIsTerminal ||
            latestVoiceCallEndedInThisSession ||
            latestVoiceCallWasLiveInThisSession);
    const showingRealVoiceCall =
        !voiceSimCallForThread && shouldShowLatestVoiceCall;
    const displayedReplayCallId =
        voiceSimCallForThread?.callId ??
        (showingRealVoiceCall ? displayedLatestVoiceCall.id : null);
    const displayedReplayState =
        displayedReplayCallId &&
        voiceReplayState?.callId === displayedReplayCallId
            ? voiceReplayState
            : null;
    const displayedIsReplaying =
        displayedReplayCallId !== null &&
        replayingVoiceCallId === displayedReplayCallId;
    const voiceHealthIssue = getVoiceHealthIssue({
        call: voiceSimCallForThread,
        transcript: voiceTranscript,
        realVoiceCall: showingRealVoiceCall ? displayedLatestVoiceCall : null,
        turnsCompleted: showingRealVoiceCall
            ? countCaregiverTranscriptTurns(
                  displayedLatestVoiceCall?.transcript,
              )
            : (voiceSimCallForThread?.turnsCompleted ?? 0),
    });
    const cardSeverity =
        workflowChannel === 'voice'
            ? (voiceHealthIssue?.severity ?? latestScenarioSeverity)
            : latestScenarioSeverity;
    const latestVoiceCallIds = useMemo(
        () =>
            showingRealVoiceCall && latestVoiceCall
                ? [latestVoiceCall.id]
                : null,
        [latestVoiceCall, showingRealVoiceCall],
    );
    const latestVoiceCallRecordingUrls = useAdminVoiceCallRecordingUrls(
        latestVoiceCallIds,
        { pollUntilResolved: true },
    );
    const cardCaregiverDetailsQuery = useCaregiverSimCaregiverDetails(
        personality.identifier,
        workflowChannel === 'voice',
    );
    const runTargetPhone = (activeCounterparty ?? scenarioCounterparty).trim();
    const runVoiceCallKey = runTargetPhone
        ? scenarioRunKey(personality.identifier, runTargetPhone)
        : null;
    const runVoiceCall = runVoiceCallKey
        ? (voiceSimCalls[runVoiceCallKey] ?? null)
        : null;
    const runVoiceCallIsStarting =
        !!runVoiceCallKey && startingVoiceSimKeys.has(runVoiceCallKey);
    const runVoiceCallIsActive =
        !!runVoiceCall && !isTerminalVoiceSimStatus(runVoiceCall.status);
    const latestRealVoiceCallIsActive =
        latestVoiceCall !== null &&
        !isTerminalPersistedVoiceCallStatus(latestVoiceCall.call_status);
    const runVoiceCallIsBusy =
        runVoiceCallIsStarting ||
        runVoiceCallIsActive ||
        latestRealVoiceCallIsActive;
    const runScenarioTargetRun = runTargetPhone
        ? pendingScenarioRuns[
              scenarioRunKey(personality.identifier, runTargetPhone)
          ]
        : null;
    const continueScenarioTargetRun = activeCounterparty
        ? pendingScenarioRuns[
              scenarioRunKey(personality.identifier, activeCounterparty)
          ]
        : null;
    const continueVoiceCallKey = activeCounterparty
        ? scenarioRunKey(personality.identifier, activeCounterparty)
        : null;
    const continueVoiceCall = continueVoiceCallKey
        ? (voiceSimCalls[continueVoiceCallKey] ?? null)
        : null;
    const continueVoiceCallIsStarting =
        !!continueVoiceCallKey &&
        startingVoiceSimKeys.has(continueVoiceCallKey);
    const continueVoiceCallIsActive =
        !!continueVoiceCall &&
        !isTerminalVoiceSimStatus(continueVoiceCall.status);
    const continueVoiceCallIsBusy =
        continueVoiceCallIsStarting || continueVoiceCallIsActive;
    const visiblePendingScenarioRun =
        continueScenarioTargetRun ??
        (activeCounterparty ? null : pendingRuns[0]);
    const runDisabled =
        !scenarioDetails.trim() ||
        pendingAction !== null ||
        !!runScenarioTargetRun ||
        runVoiceCallIsBusy;
    const continueDisabled =
        !scenarioDetails.trim() ||
        pendingAction !== null ||
        !!continueScenarioTargetRun ||
        continueVoiceCallIsBusy ||
        workflowChannel === 'voice' ||
        !activeCounterparty?.trim();
    const replayDisabled =
        hasActiveVoiceSimCall(
            voiceSimCalls,
            voiceSimCallForThread?.callId ?? null,
        ) ||
        (replayingVoiceCallId !== null &&
            replayingVoiceCallId !== displayedReplayCallId);
    const panelHeightClassName = modalView
        ? SIM_MODAL_PANEL_SCROLL_HEIGHT_CLASS
        : SIM_GRID_PANEL_SCROLL_HEIGHT_CLASS;

    useEffect(() => {
        if (!preferredCounterparty) {
            return;
        }

        const activeStillExists =
            threads.some(
                (thread) => thread.counterparty_phone === activeCounterparty,
            ) ||
            voiceSimCallsForPersonality.some(
                (call) => call.counterpartyPhone === activeCounterparty,
            );
        if (
            selected &&
            activeCounterparty &&
            activeCounterparty !== selectedCounterparty &&
            activeStillExists
        ) {
            onCounterpartyChange(activeCounterparty);
            return;
        }

        if (activeCounterparty && activeStillExists) {
            return;
        }

        if (selected && selectedCounterparty === preferredCounterparty) {
            return;
        }

        if (!selected && cardCounterparty === preferredCounterparty) {
            return;
        }

        if (selected) {
            onCounterpartyChange(preferredCounterparty);
        } else {
            setCardCounterparty(preferredCounterparty);
        }
    }, [
        activeCounterparty,
        cardCounterparty,
        onCounterpartyChange,
        preferredCounterparty,
        selected,
        selectedCounterparty,
        threads,
        voiceSimCallsForPersonality,
    ]);

    useEffect(() => {
        if (logsRefreshToken === 0) {
            return;
        }

        void mutateLogs();
        void mutateLatestVoiceCall();
    }, [logsRefreshToken, mutateLatestVoiceCall, mutateLogs]);

    const handleCounterpartyChange = useCallback(
        (counterparty: string) => {
            setCardCounterparty(counterparty);
            onCounterpartyChange(counterparty);
        },
        [onCounterpartyChange],
    );

    const endRealVoiceCall = useCallback(async () => {
        if (!latestVoiceCall) {
            return;
        }

        setEndingRealVoiceCallId(latestVoiceCall.id);
        try {
            await endCaregiverSimVoiceSession(latestVoiceCall.id, {
                reason: 'caregiver_sim_real_voice_stop',
            });
            setEndedRealVoiceCallIds((current) => {
                const next = new Set(current);
                next.add(latestVoiceCall.id);
                return next;
            });
            await mutateLatestVoiceCall();
            toast.success('Voice call ended.', {
                persistNotification: false,
            });
        } catch (error) {
            toast.error(getCaregiverSimErrorMessage(error), {
                persistNotification: false,
            });
        } finally {
            setEndingRealVoiceCallId(null);
        }
    }, [latestVoiceCall, mutateLatestVoiceCall]);

    return (
        <Card
            data-testid={`caregiver-sim-personality-card-${personality.identifier}`}
            className={cn(
                'min-w-0 overflow-hidden transition-colors',
                personalityCardHealthClassName({
                    selected,
                    severity: cardSeverity,
                }),
            )}
        >
            <CardHeader className="space-y-3 p-3" onClick={onSelect}>
                <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                        <div className="flex min-w-0 items-center gap-2">
                            <CardTitle className="truncate text-sm">
                                {personality.name}
                            </CardTitle>
                            {hasNewActivity ? (
                                <span
                                    className="h-2 w-2 flex-none rounded-full bg-blue-600"
                                    aria-label="New activity"
                                />
                            ) : null}
                            {cardSeverity && cardSeverity !== 'pass' ? (
                                <Badge
                                    variant="secondary"
                                    className={cn(
                                        'text-xs',
                                        scenarioCheckBadgeClassName(
                                            cardSeverity,
                                        ),
                                    )}
                                >
                                    {scenarioCheckSeverityLabel(cardSeverity)}
                                </Badge>
                            ) : null}
                        </div>
                        <div className="text-muted-foreground mt-1 truncate font-mono text-xs">
                            {personality.phone_number}
                        </div>
                    </div>
                    <div className="flex flex-none items-center gap-2">
                        {showExpandButton ? (
                            <Button
                                type="button"
                                variant="outline"
                                size="icon"
                                className="h-8 w-8"
                                aria-label={`Open ${personality.name} expanded view`}
                                onClick={(event) => {
                                    event.stopPropagation();
                                    onExpand(activeCounterparty);
                                }}
                            >
                                <Maximize2 className="h-4 w-4" />
                            </Button>
                        ) : null}
                        {pendingRuns.length > 0 ? (
                            <Loader2 className="h-4 w-4 animate-spin text-blue-600 dark:text-blue-400" />
                        ) : null}
                        <Badge
                            variant={
                                personality.enabled ? 'default' : 'outline'
                            }
                        >
                            {personality.enabled ? 'On' : 'Off'}
                        </Badge>
                    </div>
                </div>
                <div className="text-muted-foreground flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
                    <span>{personality.message_count} msgs</span>
                    <span>{personality.thread_count} threads</span>
                    <span>
                        {formatTimestamp(personality.last_message_at, now)}
                    </span>
                </div>
                {threads.length > 1 ? (
                    <div onClick={(event) => event.stopPropagation()}>
                        <ThreadSelect
                            threads={threads}
                            selectedCounterparty={activeCounterparty}
                            onChange={handleCounterpartyChange}
                            triggerClassName="h-8 w-full text-xs"
                        />
                    </div>
                ) : null}
            </CardHeader>
            <CardContent className="space-y-2 p-3 pt-0 pb-2">
                <div onClick={onSelect}>
                    {workflowChannel === 'voice' ? (
                        <div className="space-y-2">
                            <InboundVoiceCallCue
                                caregiverPhoneNumber={personality.phone_number}
                                inboundPhoneNumber={defaultCounterparty}
                            />
                            <VoiceMonitorPanel
                                call={voiceSimCallForThread}
                                isStarting={activeVoiceCallIsStarting}
                                transcript={voiceTranscript}
                                toolCalls={voiceToolCallEntries}
                                realVoiceCall={
                                    showingRealVoiceCall
                                        ? displayedLatestVoiceCall
                                        : null
                                }
                                isRealVoiceCallLoading={
                                    latestVoiceCallQuery.isLoading ||
                                    latestVoiceCallQuery.isValidating
                                }
                                realRecordingUrl={
                                    latestVoiceCall
                                        ? latestVoiceCallRecordingUrls.data[
                                              latestVoiceCall.id
                                          ]
                                        : undefined
                                }
                                isRealRecordingUrlLoading={
                                    latestVoiceCallRecordingUrls.isLoading
                                }
                                realRecordingUrlError={
                                    latestVoiceCallRecordingUrls.error
                                }
                                isReplaying={displayedIsReplaying}
                                replayState={displayedReplayState}
                                isEndingRealVoiceCall={
                                    endingRealVoiceCallId ===
                                    latestVoiceCall?.id
                                }
                                replayDisabled={replayDisabled}
                                onReplay={() => {
                                    if (showingRealVoiceCall) {
                                        onReplayRealVoiceCallTranscript(
                                            displayedLatestVoiceCall,
                                        );
                                        return;
                                    }

                                    onReplayVoiceTranscript(
                                        voiceSimCallForThread,
                                    );
                                }}
                                onReplaySeek={(positionMs) => {
                                    if (displayedReplayCallId) {
                                        onVoiceReplaySeek(
                                            displayedReplayCallId,
                                            positionMs,
                                        );
                                    }
                                }}
                                onEndRealVoiceCall={() =>
                                    void endRealVoiceCall()
                                }
                                heightClassName={panelHeightClassName}
                                compact={!modalView}
                            />
                        </div>
                    ) : (
                        <ConversationPanel
                            messages={logsQuery.data?.messages ?? []}
                            decisionLogs={logsQuery.data?.decision_logs ?? []}
                            isLoading={logsQuery.isLoading}
                            personalityName={personality.name}
                            pendingSimulation={pendingSimulation}
                            pendingScenarioRun={visiblePendingScenarioRun}
                            now={now}
                            heightClassName={panelHeightClassName}
                        />
                    )}
                </div>
                <div
                    className="grid grid-cols-2 gap-2"
                    onClick={(event) => event.stopPropagation()}
                >
                    <Button
                        size="sm"
                        className="justify-start"
                        disabled={runDisabled}
                        onClick={() =>
                            onRun(
                                activeCounterparty,
                                cardCaregiverDetailsQuery.data,
                            )
                        }
                    >
                        {runScenarioTargetRun?.action === 'scenario' ||
                        (workflowChannel === 'voice' &&
                            runVoiceCallIsStarting) ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                        ) : workflowChannel === 'voice' ? (
                            <PhoneCall className="h-4 w-4" />
                        ) : (
                            <Play className="h-4 w-4" />
                        )}
                        {workflowChannel === 'voice' ? 'Voice' : 'Run'}
                    </Button>
                    <Button
                        size="sm"
                        variant="outline"
                        className="justify-start"
                        disabled={continueDisabled}
                        onClick={() => onContinue(activeCounterparty)}
                    >
                        {continueScenarioTargetRun?.action ===
                        'continue-scenario' ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                            <Send className="h-4 w-4" />
                        )}
                        Continue
                    </Button>
                </div>
                {selected ? (
                    <div onClick={(event) => event.stopPropagation()}>
                        <ConversationComposer
                            body={interventionBody}
                            disabled={
                                !activeCounterparty ||
                                pendingAction !== null ||
                                !!visiblePendingScenarioRun
                            }
                            isSending={isInterventionSending}
                            onBodyChange={onInterventionBodyChange}
                            onSend={onSendIntervention}
                        />
                    </div>
                ) : null}
            </CardContent>
        </Card>
    );
}

function ThreadSelect({
    threads,
    selectedCounterparty,
    onChange,
    triggerClassName = 'w-[280px] max-w-full',
}: {
    readonly threads: Array<CaregiverSimThreadRead>;
    readonly selectedCounterparty: string | null;
    readonly onChange: (value: string) => void;
    readonly triggerClassName?: string;
}) {
    return (
        <Select
            value={selectedCounterparty ?? undefined}
            onValueChange={onChange}
            disabled={threads.length === 0}
        >
            <SelectTrigger className={triggerClassName}>
                <SelectValue placeholder="No threads" />
            </SelectTrigger>
            <SelectContent>
                {threads.map((thread) => (
                    <SelectItem
                        key={thread.counterparty_phone}
                        value={thread.counterparty_phone}
                    >
                        {thread.counterparty_phone} ({thread.message_count})
                    </SelectItem>
                ))}
            </SelectContent>
        </Select>
    );
}

function withPendingScenarioThreads(
    threads: Array<CaregiverSimThreadRead>,
    pendingRuns: Array<PendingScenarioRun>,
): Array<CaregiverSimThreadRead> {
    if (pendingRuns.length === 0) {
        return threads;
    }

    const existingPhones = new Set(
        threads.map((thread) => thread.counterparty_phone),
    );
    const pendingThreads = pendingRuns
        .filter((run) => !existingPhones.has(run.counterpartyPhone))
        .map((run) => ({
            counterparty_phone: run.counterpartyPhone,
            message_count: 0,
            last_message_at: run.startedAt,
            last_summary_at: null,
        }));

    return [...pendingThreads, ...threads];
}

function getLatestThreadCounterparty(
    threads: Array<CaregiverSimThreadRead>,
): string | null {
    let latestThread: CaregiverSimThreadRead | null = null;
    let latestTimestamp = -Infinity;

    for (const thread of threads) {
        const timestamp = getSortableTimestamp(thread.last_message_at);
        if (!latestThread || timestamp > latestTimestamp) {
            latestThread = thread;
            latestTimestamp = timestamp;
        }
    }

    return latestThread?.counterparty_phone ?? null;
}

function getSortableTimestamp(value: string | null | undefined): number {
    if (!value) {
        return 0;
    }

    const timestamp = new Date(normalizeTimestamp(value)).getTime();
    return Number.isNaN(timestamp) ? 0 : timestamp;
}

function ConversationPanel({
    messages,
    decisionLogs,
    isLoading,
    personalityName,
    pendingSimulation,
    pendingScenarioRun,
    now,
    heightClassName = SIM_SCROLL_PANEL_HEIGHT_CLASS,
}: {
    readonly messages: Array<CaregiverSimMessageRead>;
    readonly decisionLogs: Array<CaregiverSimDecisionLogRead>;
    readonly isLoading: boolean;
    readonly personalityName: string | undefined;
    readonly pendingSimulation: PendingSimulation | null;
    readonly pendingScenarioRun: PendingScenarioRun | null | undefined;
    readonly now: number;
    readonly heightClassName?: string;
}) {
    const [expandedDecisionIds, setExpandedDecisionIds] = useState<Set<number>>(
        () => new Set(),
    );
    const scrollAreaRef = useRef<HTMLDivElement | null>(null);

    const toggleDecisionExpanded = useCallback((decisionId: number) => {
        setExpandedDecisionIds((current) => {
            const next = new Set(current);
            if (next.has(decisionId)) {
                next.delete(decisionId);
            } else {
                next.add(decisionId);
            }
            return next;
        });
    }, []);

    const { decisionByMessageId, orphanDecisions } = useMemo(
        () => attachDecisionsToMessages(messages, decisionLogs),
        [messages, decisionLogs],
    );
    const pendingRenderState = pendingSimulation
        ? getPendingRenderState(messages, pendingSimulation)
        : null;
    const scrollKey = [
        messages[messages.length - 1]?.id ?? '',
        decisionLogs[decisionLogs.length - 1]?.id ?? '',
        pendingSimulation?.startedAt ?? '',
        pendingSimulation?.body ?? '',
        pendingScenarioRun?.startedAt ?? '',
        pendingScenarioRun?.action ?? '',
    ].join(':');

    useEffect(() => {
        const animationFrame = window.requestAnimationFrame(() => {
            const viewport = scrollAreaRef.current?.querySelector<HTMLElement>(
                '[data-radix-scroll-area-viewport]',
            );
            if (viewport) {
                viewport.scrollTop = viewport.scrollHeight;
            }
        });

        return () => window.cancelAnimationFrame(animationFrame);
    }, [scrollKey]);

    if (isLoading) {
        return <PanelSkeleton heightClassName={heightClassName} />;
    }

    if (
        messages.length === 0 &&
        decisionLogs.length === 0 &&
        !pendingSimulation &&
        !pendingScenarioRun
    ) {
        return (
            <div
                className={cn(
                    heightClassName,
                    'text-muted-foreground flex items-center justify-center rounded-md border border-dashed text-sm',
                )}
            >
                No activity
            </div>
        );
    }

    return (
        <ScrollArea
            ref={scrollAreaRef}
            className={cn(
                heightClassName,
                'border-border rounded-md border p-3',
            )}
        >
            <div className="space-y-3 pr-3">
                {messages.map((message) => {
                    const outbound = message.role === 'outbound';
                    const decision = decisionByMessageId.get(message.id);
                    return (
                        <div
                            key={message.id}
                            className={cn(
                                'flex flex-col gap-1',
                                outbound ? 'items-end' : 'items-start',
                            )}
                        >
                            <div
                                className={cn(
                                    'max-w-[78%] rounded-md border px-3 py-2 text-sm',
                                    outbound
                                        ? 'border-blue-200 bg-blue-50 dark:border-blue-900 dark:bg-blue-950/50'
                                        : 'border-border bg-muted/50',
                                )}
                            >
                                <div className="text-muted-foreground mb-1 text-xs">
                                    {outbound
                                        ? (personalityName ?? 'Persona')
                                        : 'Phoebe'}{' '}
                                    · {formatTimestamp(message.timestamp, now)}
                                </div>
                                <div className="break-words whitespace-pre-wrap">
                                    {message.body}
                                </div>
                            </div>
                            {decision ? (
                                <DecisionFooter
                                    decision={decision}
                                    align={outbound ? 'end' : 'start'}
                                    expanded={expandedDecisionIds.has(
                                        decision.id,
                                    )}
                                    onToggle={() =>
                                        toggleDecisionExpanded(decision.id)
                                    }
                                    now={now}
                                />
                            ) : null}
                        </div>
                    );
                })}
                {pendingSimulation && pendingRenderState?.showPending ? (
                    <PendingSimulationMessages
                        pendingSimulation={pendingSimulation}
                        showPhoebeMessage={pendingRenderState.showPhoebeMessage}
                        showTyping={pendingRenderState.showTyping}
                        now={now}
                    />
                ) : null}
                {pendingScenarioRun ? (
                    <PendingScenarioMessages
                        pendingScenarioRun={pendingScenarioRun}
                        messages={messages}
                        now={now}
                    />
                ) : null}
                {orphanDecisions.length > 0 ? (
                    <div className="border-border space-y-2 border-t border-dashed pt-3">
                        <div className="text-muted-foreground text-xs font-medium">
                            Unmatched decisions
                        </div>
                        {orphanDecisions.map((decision) => (
                            <Fragment key={decision.id}>
                                <DecisionFooter
                                    decision={decision}
                                    align="start"
                                    expanded={expandedDecisionIds.has(
                                        decision.id,
                                    )}
                                    onToggle={() =>
                                        toggleDecisionExpanded(decision.id)
                                    }
                                    now={now}
                                />
                            </Fragment>
                        ))}
                    </div>
                ) : null}
            </div>
        </ScrollArea>
    );
}

function PendingScenarioMessages({
    pendingScenarioRun,
    messages,
    now,
}: {
    readonly pendingScenarioRun: PendingScenarioRun;
    readonly messages: Array<CaregiverSimMessageRead>;
    readonly now: number;
}) {
    const typingState = getPendingScenarioTypingState(
        messages,
        pendingScenarioRun,
    );

    return (
        typingState && (
            <TypingBubble
                align={typingState.align}
                label={typingState.label}
                timestamp={pendingScenarioRun.startedAt}
                now={now}
            />
        )
    );
}

function TypingBubble({
    align,
    label,
    timestamp,
    now,
}: {
    readonly align: 'end' | 'start';
    readonly label: string;
    readonly timestamp: string;
    readonly now: number;
}) {
    const outbound = align === 'end';
    return (
        <div
            className={cn(
                'flex flex-col gap-1',
                outbound ? 'items-end' : 'items-start',
            )}
        >
            <div
                className={cn(
                    'max-w-[78%] rounded-md border px-3 py-2 text-sm',
                    outbound
                        ? 'border-blue-200 bg-blue-50 dark:border-blue-900 dark:bg-blue-950/50'
                        : 'border-border bg-muted/50',
                )}
            >
                <div className="text-muted-foreground mb-1 text-xs">
                    {label} · {formatTimestamp(timestamp, now)}
                </div>
                <div className="flex h-5 items-center gap-1">
                    <span className="bg-muted-foreground h-1.5 w-1.5 animate-bounce rounded-full [animation-delay:-0.2s]" />
                    <span className="bg-muted-foreground h-1.5 w-1.5 animate-bounce rounded-full [animation-delay:-0.1s]" />
                    <span className="bg-muted-foreground h-1.5 w-1.5 animate-bounce rounded-full" />
                </div>
            </div>
        </div>
    );
}

function ConversationComposer({
    body,
    disabled,
    isSending,
    onBodyChange,
    onSend,
}: {
    readonly body: string;
    readonly disabled: boolean;
    readonly isSending: boolean;
    readonly onBodyChange: (value: string) => void;
    readonly onSend: () => void;
}) {
    const canSend = !disabled && body.trim().length > 0;

    return (
        <div className="border-border bg-background flex items-end gap-2 rounded-md border p-2">
            <Textarea
                className="max-h-24 min-h-10 flex-1 resize-none py-2"
                disabled={disabled}
                value={body}
                onChange={(event) => onBodyChange(event.target.value)}
                onKeyDown={(event) => {
                    if (event.key === 'Enter' && !event.shiftKey && canSend) {
                        event.preventDefault();
                        onSend();
                    }
                }}
                placeholder="Message as Phoebe…"
            />
            <Button
                size="sm"
                disabled={!canSend}
                onClick={onSend}
                className="h-10 min-w-24 shrink-0"
            >
                {isSending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                    <Send className="h-4 w-4" />
                )}
                Send
            </Button>
        </div>
    );
}

function PendingSimulationMessages({
    pendingSimulation,
    showPhoebeMessage,
    showTyping,
    now,
}: {
    readonly pendingSimulation: PendingSimulation;
    readonly showPhoebeMessage: boolean;
    readonly showTyping: boolean;
    readonly now: number;
}) {
    return (
        <Fragment>
            {showPhoebeMessage ? (
                <div className="flex flex-col items-start gap-1">
                    <div className="border-border bg-muted/50 max-w-[78%] rounded-md border px-3 py-2 text-sm">
                        <div className="text-muted-foreground mb-1 text-xs">
                            Phoebe ·{' '}
                            {formatTimestamp(pendingSimulation.startedAt, now)}
                        </div>
                        <div className="break-words whitespace-pre-wrap">
                            {pendingSimulation.body}
                        </div>
                    </div>
                </div>
            ) : null}
            {showTyping ? (
                <div className="flex flex-col items-end gap-1">
                    <div className="max-w-[78%] rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm dark:border-blue-900 dark:bg-blue-950/50">
                        <div className="text-muted-foreground mb-1 text-xs">
                            {pendingSimulation.personalityName} ·{' '}
                            {formatTimestamp(pendingSimulation.startedAt, now)}
                        </div>
                        <div className="flex h-5 items-center gap-1">
                            <span className="bg-muted-foreground h-1.5 w-1.5 animate-bounce rounded-full [animation-delay:-0.2s]" />
                            <span className="bg-muted-foreground h-1.5 w-1.5 animate-bounce rounded-full [animation-delay:-0.1s]" />
                            <span className="bg-muted-foreground h-1.5 w-1.5 animate-bounce rounded-full" />
                        </div>
                    </div>
                </div>
            ) : null}
        </Fragment>
    );
}

function DecisionFooter({
    decision,
    align,
    expanded,
    onToggle,
    now,
}: {
    readonly decision: CaregiverSimDecisionLogRead;
    readonly align: 'start' | 'end';
    readonly expanded: boolean;
    readonly onToggle: () => void;
    readonly now: number;
}) {
    const isGhost = decision.decision === 'ghost';
    const latencyText =
        decision.llm_latency_ms !== null &&
        decision.llm_latency_ms !== undefined
            ? formatLatency(decision.llm_latency_ms)
            : null;
    const delayText =
        decision.delay_ms !== null && decision.delay_ms !== undefined
            ? `${formatLatency(decision.delay_ms)} delay`
            : null;
    const summaryParts = [latencyText, delayText, decision.llm_model].filter(
        (part): part is string => !!part,
    );
    const toolCalls = decision.tool_calls ?? [];
    if (toolCalls.length > 0) {
        summaryParts.push(`${toolCalls.length} tools`);
    }

    return (
        <div
            className={cn(
                'text-muted-foreground flex max-w-[78%] flex-col gap-1 text-xs',
                align === 'end' ? 'items-end' : 'items-start',
            )}
        >
            <button
                type="button"
                onClick={onToggle}
                className="hover:bg-accent flex items-center gap-1.5 rounded-sm px-1 py-0.5"
            >
                {expanded ? (
                    <ChevronDown className="h-3 w-3" />
                ) : (
                    <ChevronRight className="h-3 w-3" />
                )}
                <span
                    className={cn(
                        'rounded-full border px-1.5 py-0.5 text-[10px] font-medium tracking-wide uppercase',
                        decisionPillClass(decision.decision),
                    )}
                >
                    {isGhost ? 'ghosted' : decision.decision}
                </span>
                {summaryParts.length > 0 ? (
                    <span className="text-muted-foreground">
                        {summaryParts.join(' · ')}
                    </span>
                ) : null}
            </button>
            {expanded ? (
                <div
                    className={cn(
                        'border-border bg-background text-muted-foreground rounded-md border p-2 font-mono text-[11px]',
                        align === 'end' ? 'text-right' : 'text-left',
                    )}
                >
                    <div>
                        #{decision.id} ·{' '}
                        {formatTimestamp(decision.created_at, now)}
                    </div>
                    {decision.llm_provider || decision.llm_model ? (
                        <div>
                            {[decision.llm_provider, decision.llm_model]
                                .filter(Boolean)
                                .join(' / ')}
                        </div>
                    ) : null}
                    {decision.llm_stop_reason ? (
                        <div>stop: {decision.llm_stop_reason}</div>
                    ) : null}
                    {decision.llm_input_tokens !== null ||
                    decision.llm_output_tokens !== null ? (
                        <div>
                            tokens · in {decision.llm_input_tokens ?? '-'} · out{' '}
                            {decision.llm_output_tokens ?? '-'}
                            {decision.llm_cache_read_input_tokens ? (
                                <Fragment>
                                    {' '}
                                    · cache_read{' '}
                                    {decision.llm_cache_read_input_tokens}
                                </Fragment>
                            ) : null}
                            {decision.llm_cache_creation_input_tokens ? (
                                <Fragment>
                                    {' '}
                                    · cache_create{' '}
                                    {decision.llm_cache_creation_input_tokens}
                                </Fragment>
                            ) : null}
                        </div>
                    ) : null}
                    {decision.llm_response_id ? (
                        <div className="truncate">
                            id: {decision.llm_response_id}
                        </div>
                    ) : null}
                    <ToolCallRows
                        toolCalls={toolCalls.map((toolCall) => ({
                            id: toolCall.id,
                            name: toolCall.name,
                            status: toolCall.status,
                            arguments: toolCall.arguments ?? null,
                            result: toolCall.result,
                            error: toolCall.error ?? null,
                            createdAt: null,
                        }))}
                    />
                </div>
            ) : null}
        </div>
    );
}

function decisionPillClass(decision: string): string {
    switch (decision) {
        case 'ghost':
            return 'border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-900 dark:bg-amber-950/50 dark:text-amber-300';
        case 'unsolicited':
            return 'border-purple-300 bg-purple-50 text-purple-800 dark:border-purple-900 dark:bg-purple-950/50 dark:text-purple-300';
        case 'respond':
            return 'border-emerald-300 bg-emerald-50 text-emerald-800 dark:border-emerald-900 dark:bg-emerald-950/50 dark:text-emerald-300';
        default:
            return 'border-border bg-muted/50 text-foreground/80';
    }
}

function toolCallStatusTextClass(status: string): string {
    switch (status) {
        case 'success':
            return 'text-emerald-700 dark:text-emerald-400';
        case 'running':
        case 'queued':
            return 'text-blue-700 dark:text-blue-400';
        case 'skipped':
        case 'cancelled':
            return 'text-muted-foreground';
        case 'error':
            return 'text-red-700 dark:text-red-400';
        default:
            return 'text-muted-foreground';
    }
}

function formatToolCallValue(value: unknown): string {
    if (typeof value === 'string') {
        return value;
    }
    if (
        value === null ||
        typeof value === 'number' ||
        typeof value === 'boolean'
    ) {
        return String(value);
    }

    try {
        return JSON.stringify(value);
    } catch {
        return '[unserializable]';
    }
}

function formatLatency(milliseconds: number): string {
    if (milliseconds < 1000) {
        return `${milliseconds}ms`;
    }
    const seconds = milliseconds / 1000;
    return seconds >= 10 ? `${Math.round(seconds)}s` : `${seconds.toFixed(1)}s`;
}

function scenarioRunKey(
    personalityIdentifier: string,
    counterpartyPhone: string,
): string {
    return `${personalityIdentifier}:${counterpartyPhone}`;
}

function getLatestVoiceSimCallForPersonality(
    voiceSimCalls: VoiceSimCallsByKey,
    personalityIdentifier: string | null,
): VoiceSimCall | null {
    if (!personalityIdentifier) {
        return null;
    }

    return getLatestVoiceSimCall(
        Object.values(voiceSimCalls).filter(
            (call) => call.personalityIdentifier === personalityIdentifier,
        ),
    );
}

function getLatestVoiceSimCall(
    voiceSimCalls: Array<VoiceSimCall>,
): VoiceSimCall | null {
    return voiceSimCalls.reduce<VoiceSimCall | null>(
        (latest, call) =>
            !latest || call.startedAt > latest.startedAt ? call : latest,
        null,
    );
}

function buildScenarioContext(
    scenarioDetails: string,
    caregiverDetails: CaregiverSimCaregiverDetailsResponse | undefined,
): string {
    if (!caregiverDetails) {
        return scenarioDetails;
    }

    const sections = [
        scenarioDetails,
        [
            `Selected caregiver: ${caregiverDetails.profile.name}`,
            `Caregiver phone: ${caregiverDetails.profile.phone_number}`,
            `Caregiver address: ${caregiverDetails.profile.address}`,
        ].join('\n'),
    ];

    if (caregiverDetails.memory.compiled_markdown.trim()) {
        sections.push(
            `Caregiver memory:\n${caregiverDetails.memory.compiled_markdown.trim()}`,
        );
    }

    if (caregiverDetails.memory.fragments.length > 0) {
        sections.push(
            `Caregiver context fragments:\n${caregiverDetails.memory.fragments
                .map((fragment) => `- ${fragment.content}`)
                .join('\n')}`,
        );
    }

    if (caregiverDetails.weekly_availability.length > 0) {
        sections.push(
            `Weekly availability:\n${caregiverDetails.weekly_availability
                .map((slot) => `- ${slot.label}: ${enumLabel(slot.status)}`)
                .join('\n')}`,
        );
    }

    if (caregiverDetails.availability.length > 0) {
        sections.push(
            `One-off availability:\n${caregiverDetails.availability
                .map(
                    (slot) =>
                        `- ${formatDateTime(slot.start_time)} to ${formatDateTime(slot.end_time)}: ${enumLabel(slot.status)}`,
                )
                .join('\n')}`,
        );
    }

    if (caregiverDetails.client_preferences.length > 0) {
        sections.push(
            `Client preferences:\n${caregiverDetails.client_preferences
                .map(
                    (preference) =>
                        `- ${preference.client_name}: ${enumLabel(preference.preference_value)} (${preference.client_address})`,
                )
                .join('\n')}`,
        );
    }

    return sections
        .map((section) => section.trim())
        .filter(Boolean)
        .join('\n\n');
}

function buildVoiceSimCall({
    session,
    personalityIdentifier,
    counterpartyPhone,
    workflowType,
    caregiverAudioMode,
    scenarioContext,
}: {
    readonly session: CaregiverSimVoiceSessionStartResponse;
    readonly personalityIdentifier: string;
    readonly counterpartyPhone: string;
    readonly workflowType: WorkflowType;
    readonly caregiverAudioMode: CaregiverVoiceAudioMode;
    readonly scenarioContext: string;
}): VoiceSimCall {
    return {
        callId: session.call_id,
        callSid: session.call_sid,
        accountSid: session.account_sid,
        streamSid: session.stream_sid,
        websocketUrl: session.websocket_url,
        personalityIdentifier,
        counterpartyPhone,
        workflowType,
        caregiverAudioMode,
        scenarioContext,
        status: 'connecting',
        statusText: 'Connecting',
        startedAt: new Date().toISOString(),
        turnsCompleted: 0,
        errorMessage: null,
    };
}

function sendSyntheticTwilioStart({
    websocket,
    streamSid,
    callSid,
    accountSid,
}: {
    readonly websocket: WebSocket;
    readonly streamSid: string;
    readonly callSid: string;
    readonly accountSid: string;
}) {
    websocket.send(
        JSON.stringify({
            event: 'start',
            start: {
                streamSid,
                callSid,
                accountSid,
                customParameters: {
                    phoebe_voice_smoke: '1',
                },
            },
        }),
    );
}

function startSyntheticTwilioSilence({
    websocket,
    streamSid,
}: {
    readonly websocket: WebSocket;
    readonly streamSid: string;
}): number {
    const payload = buildSyntheticTwilioSilencePayload();
    return window.setInterval(() => {
        sendSyntheticTwilioMediaFrame({ websocket, streamSid, payload });
    }, SYNTHETIC_TWILIO_MEDIA_FRAME_INTERVAL_MS);
}

function pauseSyntheticTwilioSilence(transport: VoiceSimTransport) {
    if (transport.silenceIntervalId === null) {
        return;
    }

    window.clearInterval(transport.silenceIntervalId);
    transport.silenceIntervalId = null;
}

function resumeSyntheticTwilioSilence(
    transport: VoiceSimTransport,
    websocket: WebSocket,
    streamSid: string,
) {
    if (
        transport.ending ||
        transport.silenceIntervalId !== null ||
        websocket.readyState !== WebSocket.OPEN
    ) {
        return;
    }

    transport.silenceIntervalId = startSyntheticTwilioSilence({
        websocket,
        streamSid,
    });
}

function buildSyntheticTwilioSilencePayload(): string {
    const bytes = new Uint8Array(SYNTHETIC_TWILIO_MEDIA_FRAME_BYTES);
    bytes.fill(0xff);
    return uint8ArrayToBase64(bytes);
}

function sendSyntheticTwilioMediaFrame({
    websocket,
    streamSid,
    payload,
}: {
    readonly websocket: WebSocket;
    readonly streamSid: string;
    readonly payload: string;
}) {
    if (websocket.readyState !== WebSocket.OPEN) {
        return;
    }

    websocket.send(
        JSON.stringify({
            event: 'media',
            streamSid,
            media: { payload },
        }),
    );
}

async function streamSyntheticTwilioMediaPayload({
    websocket,
    streamSid,
    payload,
}: {
    readonly websocket: WebSocket;
    readonly streamSid: string;
    readonly payload: string;
}): Promise<boolean> {
    const mediaBytes = base64ToUint8Array(payload);
    if (mediaBytes.length === 0) {
        return false;
    }

    for (
        let offset = 0;
        offset < mediaBytes.length;
        offset += SYNTHETIC_TWILIO_MEDIA_FRAME_BYTES
    ) {
        if (websocket.readyState !== WebSocket.OPEN) {
            return false;
        }

        const frameBytes = new Uint8Array(SYNTHETIC_TWILIO_MEDIA_FRAME_BYTES);
        frameBytes.fill(0xff);
        frameBytes.set(
            mediaBytes.subarray(
                offset,
                offset + SYNTHETIC_TWILIO_MEDIA_FRAME_BYTES,
            ),
        );
        sendSyntheticTwilioMediaFrame({
            websocket,
            streamSid,
            payload: uint8ArrayToBase64(frameBytes),
        });
        await sleepMs(SYNTHETIC_TWILIO_MEDIA_FRAME_INTERVAL_MS);
    }

    return websocket.readyState === WebSocket.OPEN;
}

function uint8ArrayToBase64(bytes: Uint8Array): string {
    let binary = '';
    for (const byte of bytes) {
        binary += String.fromCharCode(byte);
    }
    return window.btoa(binary);
}

function readVoiceSocketMessage(value: unknown): {
    readonly event?: string;
    readonly media?: { readonly payload?: string };
} | null {
    if (typeof value !== 'string') {
        return null;
    }

    try {
        const parsed = JSON.parse(value) as unknown;
        if (!parsed || typeof parsed !== 'object') {
            return null;
        }
        return parsed as {
            readonly event?: string;
            readonly media?: { readonly payload?: string };
        };
    } catch {
        return null;
    }
}

function extractVoiceTimelineTranscriptEntries(
    entries: Array<VoiceTimelineEntry>,
): Array<VoiceTimelineTranscriptEntry> {
    const transcriptEntries: Array<VoiceTimelineTranscriptEntry> = [];
    for (const entry of entries) {
        const text = entry.text?.trim();
        if (!text) {
            continue;
        }
        if (entry.kind === 'tts_request') {
            transcriptEntries.push({
                speaker: 'phoebe',
                text,
            });
        }
        if (entry.kind === 'assistant_text' && entry.role === 'assistant') {
            transcriptEntries.push({
                speaker: 'phoebe',
                text,
            });
        }
        if (entry.kind === 'user_text' && entry.role === 'user') {
            transcriptEntries.push({
                speaker: 'caregiver',
                text,
            });
        }
    }
    return transcriptEntries;
}

function extractVoiceTimelineToolCalls(
    entries: Array<VoiceTimelineEntry>,
): Array<ToolCallDisplayEntry> {
    const toolCallsById = new Map<string, ToolCallDisplayEntry>();

    for (const entry of entries) {
        if (entry.kind === 'tool_batch') {
            for (const toolCall of voiceTimelineFunctionCalls(
                entry.function_calls,
            )) {
                upsertVoiceToolCall(toolCallsById, {
                    id: toolCall.toolCallId,
                    name: toolCall.functionName,
                    status: 'queued',
                    arguments: toolCall.argumentsValue,
                    result: null,
                    error: null,
                    createdAt: entry.timestamp_iso ?? null,
                });
            }
            continue;
        }

        if (entry.kind === 'tool_start') {
            const id = entry.tool_call_id?.trim();
            if (!id) {
                continue;
            }
            upsertVoiceToolCall(toolCallsById, {
                id,
                name: entry.function_name?.trim() || 'tool',
                status: 'running',
                arguments: entry.arguments ?? null,
                result: null,
                error: null,
                createdAt: entry.timestamp_iso ?? null,
            });
            continue;
        }

        if (entry.kind === 'tool_result') {
            const id = entry.tool_call_id?.trim();
            if (!id) {
                continue;
            }
            upsertVoiceToolCall(toolCallsById, {
                id,
                name: entry.function_name?.trim() || 'tool',
                status: 'success',
                arguments: entry.arguments ?? null,
                result: entry.result ?? null,
                error: null,
                createdAt: entry.timestamp_iso ?? null,
            });
            continue;
        }

        if (entry.kind === 'tool_cancel') {
            const id = entry.tool_call_id?.trim();
            if (!id) {
                continue;
            }
            upsertVoiceToolCall(toolCallsById, {
                id,
                name: entry.function_name?.trim() || 'tool',
                status: 'cancelled',
                arguments: null,
                result: null,
                error: null,
                createdAt: entry.timestamp_iso ?? null,
            });
        }
    }

    return [...toolCallsById.values()];
}

function extractCompletedVoiceAssistantPrompts(
    entries: Array<VoiceTimelineEntry>,
): Array<VoiceAssistantResponsePrompt> {
    const ttsRequestsByContextId = new Map<string, Array<string>>();
    const completedContextIds = new Set<string>();
    const prompts: Array<VoiceAssistantResponsePrompt> = [];

    for (const entry of entries) {
        const contextId = entry.context_id?.trim();
        const text = entry.text?.trim();
        if (entry.kind === 'tts_request' && contextId && text) {
            const requests = ttsRequestsByContextId.get(contextId) ?? [];
            requests.push(text);
            ttsRequestsByContextId.set(contextId, requests);
            continue;
        }

        if (
            entry.kind === 'tts_audio_completed' &&
            contextId &&
            !completedContextIds.has(contextId)
        ) {
            const assistantText = (ttsRequestsByContextId.get(contextId) ?? [])
                .join(' ')
                .trim();
            if (assistantText) {
                prompts.push({ text: assistantText });
                completedContextIds.add(contextId);
            }
            continue;
        }

        // Caregiver replies are paced from spoken audio completion. The voice
        // pipeline also emits later aggregate assistant transcript rows that
        // can phrase the same turn differently, so those rows are display-only.
    }

    return prompts;
}

function voiceTimelineFunctionCalls(value: unknown): Array<{
    readonly toolCallId: string;
    readonly functionName: string;
    readonly argumentsValue: unknown;
}> {
    if (!Array.isArray(value)) {
        return [];
    }

    return value.flatMap((item) => {
        if (!item || typeof item !== 'object') {
            return [];
        }

        const payload = item as {
            readonly tool_call_id?: unknown;
            readonly function_name?: unknown;
            readonly arguments?: unknown;
        };
        const toolCallId =
            typeof payload.tool_call_id === 'string'
                ? payload.tool_call_id.trim()
                : '';
        if (!toolCallId) {
            return [];
        }

        return [
            {
                toolCallId,
                functionName:
                    typeof payload.function_name === 'string' &&
                    payload.function_name.trim()
                        ? payload.function_name.trim()
                        : 'tool',
                argumentsValue: payload.arguments ?? null,
            },
        ];
    });
}

function upsertVoiceToolCall(
    toolCallsById: Map<string, ToolCallDisplayEntry>,
    toolCall: ToolCallDisplayEntry,
): void {
    const existing = toolCallsById.get(toolCall.id);
    if (!existing) {
        toolCallsById.set(toolCall.id, toolCall);
        return;
    }

    toolCallsById.set(toolCall.id, {
        ...existing,
        ...toolCall,
        arguments: toolCall.arguments ?? existing.arguments,
        result: toolCall.result ?? existing.result,
        createdAt: existing.createdAt ?? toolCall.createdAt,
    });
}

function finalizeTerminalVoiceToolCalls(
    toolCalls: Array<ToolCallDisplayEntry>,
    terminal: boolean,
): Array<ToolCallDisplayEntry> {
    if (!terminal) {
        return toolCalls;
    }

    return toolCalls.map((toolCall) => {
        if (
            toolCall.name !== 'end_call' ||
            (toolCall.status !== 'running' && toolCall.status !== 'queued')
        ) {
            return toolCall;
        }

        return {
            ...toolCall,
            status: 'success',
            result: toolCall.result ?? { ended: true },
        };
    });
}

function base64ToUint8Array(payload: string): Uint8Array {
    const binary = window.atob(payload);
    const bytes = new Uint8Array(binary.length);
    for (let index = 0; index < binary.length; index += 1) {
        bytes[index] = binary.charCodeAt(index);
    }
    return bytes;
}

function getVoiceTranscriptAudioPayloads(
    entry: VoiceTranscriptEntry,
): Array<string> {
    if (entry.audioPayloads.length > 0) {
        return [...entry.audioPayloads];
    }
    return entry.audioPayload ? [entry.audioPayload] : [];
}

function decodeMuLawPayloadToFloat32(payload: string): Float32Array {
    const bytes = base64ToUint8Array(payload);
    const samples = new Float32Array(bytes.length);
    for (let index = 0; index < bytes.length; index += 1) {
        samples[index] = decodeMuLawSample(bytes[index] ?? 0) / 32768;
    }
    return samples;
}

function silenceSamples(durationMs: number): Float32Array {
    return new Float32Array(Math.round((8000 * durationMs) / 1000));
}

function getMuLawPayloadDurationMs(payload: string): number {
    return (base64ToUint8Array(payload).length / 8000) * 1000;
}

function decodeMuLawSample(value: number): number {
    const muLawByte = ~value & 0xff;
    const sign = muLawByte & 0x80;
    const exponent = (muLawByte >> 4) & 0x07;
    const mantissa = muLawByte & 0x0f;
    let sample = ((mantissa << 3) + 0x84) << exponent;
    sample -= 0x84;
    return sign ? -sample : sample;
}

async function prepareBrowserAudioContext(
    audioContextRef: MutableRefObject<AudioContext | null>,
    options: { readonly resume?: boolean } = {},
): Promise<void> {
    const AudioContextConstructor = getBrowserAudioContextConstructor();
    if (!AudioContextConstructor) {
        return;
    }

    const audioContext =
        audioContextRef.current ?? new AudioContextConstructor();
    audioContextRef.current = audioContext;
    if ((options.resume ?? true) && audioContext.state === 'suspended') {
        await audioContext.resume();
    }
}

async function sleepMs(durationMs: number): Promise<void> {
    await new Promise<void>((resolve) => {
        window.setTimeout(resolve, durationMs);
    });
}

function clampNumber(value: number, min: number, max: number): number {
    return Math.max(min, Math.min(max, value));
}

function formatReplayClock(durationMs: number): string {
    const totalSeconds = Math.max(0, Math.floor(durationMs / 1000));
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

function isTerminalVoiceSimStatus(status: VoiceSimStatus): boolean {
    return status === 'ended' || status === 'error';
}

function hasActiveVoiceSimCall(
    calls: VoiceSimCallsByKey,
    excludedCallId: string | null,
): boolean {
    return Object.values(calls).some(
        (call) =>
            call.callId !== excludedCallId &&
            !isTerminalVoiceSimStatus(call.status),
    );
}

function isEndableVoiceSimStatus(status: VoiceSimStatus): boolean {
    return !isTerminalVoiceSimStatus(status) || status === 'error';
}

function voiceSimStatusLabel(status: VoiceSimStatus): string {
    switch (status) {
        case 'idle':
            return 'Idle';
        case 'connecting':
            return 'Connecting';
        case 'listening':
            return 'Listening';
        case 'phoebe-speaking':
            return 'Phoebe';
        case 'caregiver-thinking':
            return 'Thinking';
        case 'caregiver-speaking':
            return 'Caregiver';
        case 'ending':
            return 'Ending';
        case 'ended':
            return 'Ended';
        case 'error':
            return 'Error';
        default: {
            const exhaustive: never = status;
            return exhaustive;
        }
    }
}

function enumLabel(value: string): string {
    return value
        .split('_')
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ');
}

function formatDateTime(value: string): string {
    const date = new Date(normalizeTimestamp(value));
    if (Number.isNaN(date.getTime())) {
        return value;
    }
    return new Intl.DateTimeFormat(undefined, {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
    }).format(date);
}

function attachDecisionsToMessages(
    messages: Array<CaregiverSimMessageRead>,
    decisionLogs: Array<CaregiverSimDecisionLogRead>,
): {
    readonly decisionByMessageId: Map<string, CaregiverSimDecisionLogRead>;
    readonly orphanDecisions: Array<CaregiverSimDecisionLogRead>;
} {
    const decisionByMessageId = new Map<string, CaregiverSimDecisionLogRead>();
    const claimedMessageIds = new Set<string>();
    const orphanDecisions: Array<CaregiverSimDecisionLogRead> = [];

    // Pair oldest decisions first so duplicate-bodied messages stay aligned in
    // chronological order.
    const sortedDecisions = [...decisionLogs].sort(
        (a, b) =>
            new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
    );

    for (const decision of sortedDecisions) {
        const target = findTargetMessage(decision, messages, claimedMessageIds);
        if (target) {
            decisionByMessageId.set(target.id, decision);
            claimedMessageIds.add(target.id);
        } else {
            orphanDecisions.push(decision);
        }
    }

    return { decisionByMessageId, orphanDecisions };
}

function findTargetMessage(
    decision: CaregiverSimDecisionLogRead,
    messages: Array<CaregiverSimMessageRead>,
    claimedMessageIds: Set<string>,
): CaregiverSimMessageRead | undefined {
    if (decision.decision === 'ghost' && decision.inbound_body) {
        return messages.find(
            (message) =>
                message.role === 'inbound' &&
                message.body === decision.inbound_body &&
                !claimedMessageIds.has(message.id),
        );
    }
    if (decision.response_body) {
        return messages.find(
            (message) =>
                message.role === 'outbound' &&
                message.body === decision.response_body &&
                !claimedMessageIds.has(message.id),
        );
    }
    if (decision.decision === 'respond' && decision.inbound_body) {
        // Delayed respond row whose outbound has not been sent yet.
        return messages.find(
            (message) =>
                message.role === 'inbound' &&
                message.body === decision.inbound_body &&
                !claimedMessageIds.has(message.id),
        );
    }
    return undefined;
}

function getPendingRenderState(
    messages: Array<CaregiverSimMessageRead>,
    pendingSimulation: PendingSimulation,
): {
    readonly showPending: boolean;
    readonly showPhoebeMessage: boolean;
    readonly showTyping: boolean;
} {
    const pendingStartedAt = new Date(pendingSimulation.startedAt).getTime();
    const nearbyPendingWindowMs = 30_000;
    const hasStoredPhoebeMessage = messages.some(
        (message) =>
            message.role === 'inbound' &&
            message.body === pendingSimulation.body &&
            Math.abs(
                new Date(normalizeTimestamp(message.timestamp)).getTime() -
                    pendingStartedAt,
            ) <= nearbyPendingWindowMs,
    );
    const hasStoredCaregiverResponse = messages.some(
        (message) =>
            message.role === 'outbound' &&
            new Date(normalizeTimestamp(message.timestamp)).getTime() >=
                pendingStartedAt - nearbyPendingWindowMs,
    );
    const showPhoebeMessage = !hasStoredPhoebeMessage;
    const showTyping = !hasStoredCaregiverResponse;

    return {
        showPending: showPhoebeMessage || showTyping,
        showPhoebeMessage,
        showTyping,
    };
}

function getPendingScenarioTypingState(
    messages: Array<CaregiverSimMessageRead>,
    pendingScenarioRun: PendingScenarioRun,
): {
    readonly align: 'end' | 'start';
    readonly label: string;
} | null {
    const startedAt = new Date(pendingScenarioRun.startedAt).getTime();
    const newMessages = messages
        .filter((message) => {
            const timestamp = new Date(
                normalizeTimestamp(message.timestamp),
            ).getTime();
            return !Number.isNaN(timestamp) && timestamp >= startedAt - 1_000;
        })
        .sort(
            (a, b) =>
                new Date(normalizeTimestamp(a.timestamp)).getTime() -
                new Date(normalizeTimestamp(b.timestamp)).getTime(),
        );

    const latestMessage = newMessages[newMessages.length - 1];
    if (!latestMessage) {
        return {
            align: 'start',
            label: 'Phoebe',
        };
    }

    if (latestMessage.role === 'inbound') {
        return {
            align: 'end',
            label: pendingScenarioRun.personalityName,
        };
    }

    return {
        align: 'start',
        label: 'Phoebe',
    };
}

function ConversationGridSkeleton() {
    return (
        <div className="grid min-w-0 grid-cols-1 gap-3 md:grid-cols-2 2xl:grid-cols-3">
            {[0, 1, 2, 3, 4, 5].map((index) => (
                <Card key={index} className="min-w-0">
                    <CardHeader className="space-y-3 p-3">
                        <Skeleton className="h-4 w-36" />
                        <Skeleton className="h-3 w-28" />
                        <Skeleton className="h-3 w-44" />
                    </CardHeader>
                    <CardContent className="space-y-3 p-3 pt-0">
                        <PanelSkeleton
                            heightClassName={SIM_GRID_PANEL_SCROLL_HEIGHT_CLASS}
                        />
                        <div className="grid grid-cols-2 gap-2">
                            <Skeleton className="h-9 w-full" />
                            <Skeleton className="h-9 w-full" />
                        </div>
                    </CardContent>
                </Card>
            ))}
        </div>
    );
}

function PanelSkeleton({
    heightClassName = SIM_SCROLL_PANEL_HEIGHT_CLASS,
}: {
    readonly heightClassName?: string;
}) {
    return (
        <div
            className={cn(
                heightClassName,
                'border-border rounded-md border p-3',
            )}
        >
            <div className="space-y-3">
                <Skeleton className="h-16 w-3/4" />
                <Skeleton className="ml-auto h-16 w-2/3" />
                <Skeleton className="h-20 w-4/5" />
                <Skeleton className="ml-auto h-14 w-1/2" />
            </div>
        </div>
    );
}

function getCaregiverSimErrorMessage(error: unknown): string {
    const message =
        error instanceof Error && error.message.trim().length > 0
            ? error.message
            : 'Unable to load caregiver simulator.';

    if (message === 'Unable to reach caregiver simulator') {
        return (
            'Local caregiver simulator is unreachable. Inspect the caregiver-sim tmux pane, ' +
            'or start tools/caregiver_sim with pnpm dev.'
        );
    }
    if (message.includes('VOICE_SMOKE must be enabled')) {
        return (
            'Caregiver voice simulation requires voice smoke mode. Restart this worktree with ' +
            'scripts/worktree_dev.sh up --voice-smoke --no-attach.'
        );
    }
    if (message === 'Unable to reach voice service') {
        return (
            'Local voice service is unreachable. Restart this worktree with ' +
            'scripts/worktree_dev.sh up --voice-smoke --no-attach.'
        );
    }
    if (message === 'Failed to fetch') {
        return (
            'Unable to reach the local API or voice simulator. Confirm the worktree dev session is running ' +
            'and use scripts/worktree_dev.sh up --voice-smoke --no-attach for voice simulation.'
        );
    }

    return message;
}

function useRelativeNow(): number {
    const [now, setNow] = useState(() => Date.now());

    useEffect(() => {
        const interval = window.setInterval(() => {
            setNow(Date.now());
        }, 10_000);

        return () => window.clearInterval(interval);
    }, []);

    return now;
}

function formatTimestamp(
    value: string | null | undefined,
    now: number,
): string {
    if (!value) {
        return 'No activity';
    }

    const date = new Date(normalizeTimestamp(value));
    if (Number.isNaN(date.getTime())) {
        return value;
    }

    return formatDistanceStrict(date, now, { addSuffix: true });
}

function normalizeTimestamp(value: string): string {
    if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(value)) {
        return `${value.replace(' ', 'T')}Z`;
    }

    return value;
}
